/**
 * Hero effect execution for the Legendary Arena game engine.
 *
 * Executes a safe MVP subset of hero ability keywords when a hero card
 * is played. Only unconditional effects with valid magnitude are executed.
 * Conditional effects and unsupported keywords are safely skipped.
 *
 * Dispatch is data-driven (WP-251 / D-24022): each keyword maps to a handler
 * in HERO_EFFECT_HANDLERS (an ImplementationMap mirroring WP-009B) instead of a
 * switch. Handlers hold no state and live outside G.
 *
 * No boardgame.io imports. No registry imports. No .reduce().
 * Uses existing helpers only: moveCardFromZone, moveAllCards, shuffleDeck,
 * addResources, koCard.
 */
import { cardHasTeamWhenPlayed, cardCountsAsShieldHero } from './effectiveTeams.logic.js';
import { HERO_KEYWORDS } from '../rules/heroKeywords.js';
import { getHooksForCard, investigateCandidateMatches } from '../rules/heroAbility.types.js';
import { isHollowReason, DEFERRED_BY_DESIGN_MECHANICS } from '../diagnostics/hollowEffect.types.js';
import { recordHollowEffect } from '../diagnostics/hollowEffect.record.js';
import { recordEffectTrace } from '../diagnostics/effectTrace.record.js';
import { recordConditionalClause } from '../diagnostics/synergyCount.record.js';
import { heroClauseValue, heroClausePotentialFloor } from './heroClauseValue.derive.js';
import { evaluateAllConditions, findFailedCondition, describeFailedCondition, OWNED_CARD_KOED_CONDITION_TYPE, OWNED_HERO_KOED_CONDITION_TYPE, DECK_CARD_KOED_CONDITION_TYPE, } from './heroConditions.evaluate.js';
import { isWaitAndSeeCondition, recordDeferredConditionalGrant, resolveDeferredConditionalGrants, REPEATABLE_DEFEAT_CONDITION_TYPE, recordOwnedCardKoed, } from './deferredConditionalGrants.js';
import { shuffleDeck } from '../setup/shuffle.js';
import { moveCardFromZone, moveAllCards } from '../moves/zoneOps.js';
import { reshuffleDiscardIntoDeck } from '../moves/drawCards.logic.js';
import { addResources, enableRecruitSpendableAsAttack, enableDrawLock, enrollExcessiveViolenceCard, enablePlayBothSides, addRestrictedAttack, formatAttackTargets, addFightCostReduction, recordOutOfTimePlayed } from '../economy/economy.logic.js';
import { countDistinctVictoryPointValues } from '../economy/bloodFrenzy.logic.js';
// why: D-24741 — Pure Fury reads a target's current Attack from the single fight-cost authorities.
import { resolveFightCost, resolveMastermindFightCost } from '../economy/economy.resolve.js';
import { computeDayNight } from '../rules/dayNight.logic.js';
import { koCard } from '../board/ko.logic.js';
import { WOUND_EXT_ID, BYSTANDER_EXT_ID } from '../setup/pilesInit.js';
import { gainWoundWithOutcome } from '../board/wounds.logic.js';
import { seatsInTurnOrderFrom } from '../turn/seatOrder.logic.js';
import { resolveCountSource, explainCountSourceInputs } from './heroCountSource.resolve.js';
import { interpretHeroPrimitiveEffect } from './effectPrimitive.interpret.js';
import { offPlayCardStats, offPlayCardTraits, playedCardIdsStillInPlay } from './splitCard.logic.js';
import { cardHasClassWhenPlayed } from './sizeChanging.logic.js';
import { getEligibleVictoryVillains } from '../moves/resolveVictoryPileCardPick.js';
import { getEligibleKoDiscardCards } from '../moves/koDiscardChoice.resolve.js';
import { getEligibleZeroCostDiscardCards } from '../moves/resolveReturnZeroCostDiscard.js';
import { getEligibleDiscardToPlayCards } from '../moves/resolveDiscardToPlay.js';
import { buildDefeatWithBystanderTargets, dispatchDefeatWithBystanderTarget, isMastermindFreeDefeatTarget, } from '../moves/defeatChoice.resolve.js';
import { parkSeatChoice } from '../moves/seatChoice.resolve.js';
import { countRevealableDivingBlockCopies } from '../moves/divingBlock.logic.js';
// why: WP-828 / D-24761 — the grant handler only queues; the module references this one back only
// inside function bodies (a runtime-safe cycle).
import { queueOutOfTimeGrant } from '../moves/outOfTimeGrant.logic.js';
// why: WP-844 / D-24778 — a type-import-only module, so the executor stays off recruitSidekick.ts's
// pending-choice guard graph and no cycle is added.
import { gainSidekickToDiscard } from '../moves/gainSidekick.logic.js';
import { revealTopThreeForAssign, buildAllRevealThreeAssignDispositions, } from '../moves/revealThreeAssign.resolve.js';
import { buildHereHoldThisTargets, attachBystanderToCityVillain, captureBystanderToMastermind, buildHereHoldThisChoice, buildRandomActsWoundChoice, buildPassLeftChoice, } from '../moves/seatChoiceCards.js';
import { formatCardRef, heroClassDisplayName, resolveCardName, teamDisplayName } from '../log/logDisplay.js';
import { describeRevealPredicate, describeRevealActions, describeUnappliedRevealActions, formatRevealOutcomeLine, } from './revealLog.js';
import { formatCountScaledRate } from './countSourceLabel.js';
import { pushLog } from '../log/logPush.js';
import { parkCommuneCostChoice } from '../moves/communeSpiritWorld.logic.js';
// why: WP-855 / D-24791 — the deck-KO draw hold reads every parked choice queue. This file
// already reaches phaseCard.js through outOfTimeGrant.logic.js and revealOrWoundChoice.logic.js,
// so the direct import adds no new load-order edge.
import { hasAnyPendingChoice } from '../moves/phaseCard.js';
import { composeTransformNarrative, composeHeroRevealAttackNarrative, composeHeroRevealTopNarrative, composeNoOtherDeckNarrative, composeExcessiveViolenceFiredNarrative, composeWoundOutcomeClause, } from '../events/notableEvents.compose.js';
// why: WP-845 / D-24779 — the reveal-or-gain-a-Wound queue writer. The module imports
// phaseCard and seatChoice.resolve; every binding is read inside function bodies only, so the
// cycle is runtime-safe.
import { enqueueRevealOrWoundChoice, formatRevealOrWoundPlayerNames, } from '../moves/revealOrWoundChoice.logic.js';
// ---------------------------------------------------------------------------
// MVP keyword set
// ---------------------------------------------------------------------------
// why: WP-215 adds 'rescue' and 'reveal' to the executed set. WP-217 adds
// 'reveal-ko' and 'reveal-min'. WP-218 adds 'reveal-ko-or-draw' (D-21802).
// WP-219 adds 'reveal-cost-attack' (D-21901) and 'reveal-odd-draw' (D-21902).
// WP-220 adds 'reveal-attack-choose' (D-22003).
// WP-223 adds 'reveal-ko-attack' (D-22301).
// WP-247 adds 'attack-per-count' (D-24016) — count-scaled attack.
// WP-248 adds 'optional-ko-reward' (D-24019) — parks a "you may KO a card; if
// you do, <reward>" interactive choice.
// 'wound' and 'conditional' remain deferred — they require targeting UI or
// additional game systems not yet implemented.
//
// why (WP-251 / re-spec WP-253 D-24024): the registry-drift test splits into two
// concerns. HANDLED_KEYWORDS is the keywords with a HERO_EFFECT_HANDLERS entry — the
// single handler-completeness authority (the drift test asserts the handler keys
// deep-equal it bidirectionally). After the reveal collapse there are 8 handlers:
// the 7 legacy reveal-* keywords lost their dedicated handlers (folded into the one
// 'reveal' handler) but stay executable via revealRulesForLegacyKeyword translation.
export const HANDLED_KEYWORDS = new Set([
    'draw', 'attack', 'recruit', 'ko', 'rescue', 'reveal', 'attack-per-count', 'recruit-per-count', 'optional-ko-reward', 'optional-put-bottom-hq', 'put-any-number-bottom-hq', 'put-bottom-hq-icon-reward', 'victory-villain-attack', 'draw-or-empowered', 'count-scaled-choose', 'return-zero-cost-discard',
    // why: WP-714 / D-24537 — the count-scaled bystander-capture sibling of attack/recruit-per-count;
    // has a HERO_EFFECT_HANDLERS entry (heroEffectKidnapPerCount) that captures N = magnitude ×
    // ⌊count / perEach⌋ Bystanders to the first City villain (Mastermind fallback), so it belongs
    // here. Carries a magnitude → NOT in NO_MAGNITUDE_KEYWORDS.
    'kidnap-per-count',
    // why: D-24156 — the plain "gain a Wound" family; each has a HERO_EFFECT_HANDLERS entry (heroEffectGainWound), so it belongs in HANDLED_KEYWORDS (the bidirectional handler-completeness authority).
    'gain-wound-self', 'gain-wound-each',
    // why: D-24148 — mandatory immediate empty-discard-reward-or-shuffle (Jocasta's Reprocess / Electromagnetic Eyebeams); has a HERO_EFFECT_HANDLERS entry, so it belongs here.
    'shuffle-discard-empty-reward',
    // why: WP-382 / D-24183 — auto-resolving Wound-restricted KO-a-Wound-then-reward (Healing Factor family); has a HERO_EFFECT_HANDLERS entry (heroEffectKoWoundReward), so it belongs here.
    'ko-wound-reward',
    // why: WP-721 / D-24542 — the REWARDLESS sibling of ko-wound-reward ("You may KO a Wound from
    // your hand or discard pile." with no "if you do" clause); has a HERO_EFFECT_HANDLERS entry
    // (heroEffectKoWound) that KOs one Wound hand-first-else-discard with NO reward dispatch, so it
    // belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS (unlike ko-wound-reward).
    'ko-wound',
    // why: WP-383 / D-24184 — mandatory discard-to-play COST; has a HERO_EFFECT_HANDLERS entry (heroEffectDiscardToPlay) that parks the PendingDiscardToPlay, so it belongs here.
    'discard-to-play',
    // why: WP-486 / D-24291 — Silent Sniper's "Defeat a Villain or Mastermind that has a Bystander."; has a HERO_EFFECT_HANDLERS entry (heroEffectDefeatWithBystander) that defeats one eligible target via the shared fight-defeat path or parks a PendingDefeatChoice, so it belongs here.
    'defeat-with-bystander',
    // why: WP-535 / D-24345 — Rogue's Copy Powers "Play this card as a copy of another Hero you played this turn."; has a HERO_EFFECT_HANDLERS entry (heroEffectCopyPowers) that re-fires the chosen Hero's ability via the reentrant executeHeroEffects (or parks a PendingCopyPowersChoice when ≥2 qualify), so it belongs here.
    'copy-powers',
    // why: WP-580 / D-24389 — God of Thunder's "You can use Recruit as Attack this turn."; has a HERO_EFFECT_HANDLERS entry (heroEffectRecruitAsAttack) that sets the turn-scoped conversion flag, so it belongs here.
    'recruit-as-attack',
    // why: WP-731 / D-24552 — Venompool's Shenanigans "you can't draw any more cards this turn"; has a HERO_EFFECT_HANDLERS entry (heroEffectNoMoreDraws) that sets the turn-scoped drawsLocked flag, so it belongs here.
    'no-more-draws',
    // why: WP-592 / D-24401 — Rogue's Steal Abilities "Each player discards the top card of their deck. Play a copy of each of those cards."; has a HERO_EFFECT_HANDLERS entry (heroEffectStealAbilities) that runs the deterministic discard-then-copy phases, so it belongs here.
    'steal-abilities',
    // why: WP-564 / D-24373 — "Investigate for <criterion>" (static-criterion + draw); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectInvestigate) that looks at the top N, draws the
    // first matching card, and bottoms the rest, so it belongs here.
    'investigate',
    // why: WP-658 / D-24469 — "[keyword:Transform] this into <second-form>" (wwhk); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectTransform) that pulls the matching second-form out
    // of G.transformDeck into play, routes the base card back to the side deck, and applies the
    // second-form's printed attack/recruit, so it belongs here. The setup parser only emits a
    // transform effect for SUPPORTED_TRANSFORM_BASES cards; held-back cards never reach here.
    'transform',
    // why: WP-659 / D-24470 — "Each player may reveal another Hero → draw" (Psychic Link); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectRevealFromHand) that draws for each player holding a
    // criterion match, so it belongs here (the bidirectional handler-completeness authority).
    'reveal-from-hand',
    // why: WP-663 / D-24474 — Shadowed Thoughts' "play the top Villain-Deck card → +2 Attack";
    // has a HERO_EFFECT_HANDLERS entry (heroEffectOptionalPlayVillainTop) that parks the
    // pending choice, so it belongs here. (Carries magnitude 2 → NOT in NO_MAGNITUDE_KEYWORDS.)
    'optional-play-villain-top',
    // why: WP-667 / D-24480 — Radioactive Riot's "you may KO a card from your hand or discard
    // pile" (no reward); has a HERO_EFFECT_HANDLERS entry (heroEffectOptionalKoHandDiscard) that
    // parks a no-reward entry into the shared optional-ko-reward pending queue, so it belongs
    // here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'optional-ko-hand-discard',
    // why: WP-668 / D-24481 — Jade Giantess' reveal-Hero-Deck-per-2-Recruit → gain printed
    // attack; has a HERO_EFFECT_HANDLERS entry (heroEffectRevealHeroDeckAttack), so it belongs
    // here. Carries magnitude (the divisor) → NOT in NO_MAGNITUDE_KEYWORDS.
    'reveal-herodeck-attack',
    // why: WP-676 / D-24492 — the Smash keyword ("you may discard another card from your hand;
    // if you do, +N attack"); has a HERO_EFFECT_HANDLERS entry (heroEffectSmash) that parks a
    // PendingSmashDiscard, so it belongs here. Carries a magnitude (the +N Attack) → NOT in
    // NO_MAGNITUDE_KEYWORDS.
    'smash',
    // why: WP-678 / D-24494 (supersedes D-24060) — the two Undercover source-shape keywords.
    // Each has a HERO_EFFECT_HANDLERS entry that sends the sourced card to the Victory Pile
    // (worth 1 VP, tracked in G.playerZones[pid].undercover). 'undercover-hand-shield-hero' parks
    // a PendingUndercoverChoice when ≥2 hand Heroes qualify; 'undercover-officer-stack' is
    // deterministic. Both carry NO magnitude → also in NO_MAGNITUDE_KEYWORDS. (The BARE
    // 'undercover' token is NOT here — it is an honest hollow, no source zone → no handler.)
    'undercover-hand-shield-hero',
    'undercover-officer-stack',
    // why: WP-683 / D-24500 — Deadpool's Here, Hold This for a Second ("A Villain of your
    // choice captures a Bystander."); has a HERO_EFFECT_HANDLERS entry (heroEffectHereHoldThis)
    // that attaches via attachBystanderToVillain (Mastermind fallback at 0 Villains, empty-supply
    // no-op) or parks an active-scoped PendingSeatChoice, so it belongs here. Carries NO magnitude
    // → also in NO_MAGNITUDE_KEYWORDS.
    'here-hold-this',
    // why: WP-683 / D-24500 — Deadpool's Random Acts of Unkindness ("You may gain a Wound to your
    // hand. Then each player passes a card from their hand to the player on their left."); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectRandomActs) that parks the active-scoped wound choice
    // (which chains the simultaneous multi-seat pass-left), so it belongs here. Carries NO
    // magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'random-acts',
    // why: WP-681 / D-24498 — Deadpool's Do-Over ("discard the rest of your hand and draw
    // four cards", first-Hero-gated); has a HERO_EFFECT_HANDLERS entry (heroEffectDoOver)
    // that parks the accept/decline PendingDoOver. Carries NO magnitude → also in
    // NO_MAGNITUDE_KEYWORDS.
    'do-over',
    // why: WP-681 / D-24498 — Nick Fury's Battlefield Promotion ("KO a [team:shield] Hero
    // from hand/discard; if you do, gain a S.H.I.E.L.D. Officer to your hand"); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectOptionalKoShieldOfficer) that parks a
    // PendingOptionalKoReward with koTeamFilter 'shield' + rewardType 'gain-officer-hand'.
    // Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'optional-ko-shield-officer',
    // why: WP-682 / D-24499 — Nick Fury's Pure Fury ("Defeat any Villain or Mastermind
    // whose Attack is less than the number of S.H.I.E.L.D. Heroes in the KO pile"); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectPureFury) that reuses the defeat-with-bystander
    // shared free-defeat path (0/1/≥2 → no-op/auto/park PendingDefeatChoice choiceType
    // 'pure-fury'), so it belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'pure-fury',
    // why: WP-700 / D-24519 — "Draw N cards. Then put a card from your hand on top of your deck."
    // (Gambit's Stack the Deck + siblings); has a HERO_EFFECT_HANDLERS entry
    // (heroEffectPutHandOnDeckTop) that draws N then parks a mandatory PendingPutHandOnDeckTop, so
    // it belongs here. Carries a magnitude (the draw count) → NOT in NO_MAGNITUDE_KEYWORDS.
    'put-hand-on-deck-top',
    // why: WP-702 / D-24521 — "Reveal the top card of your deck. Discard it or put it back."
    // (Gambit's Hypnotic Charm entry 1 + standalone family); has a HERO_EFFECT_HANDLERS entry
    // (heroEffectRevealTopDispose) that snapshots the deck top and parks a PendingRevealTopDispose,
    // so it belongs here. Carries NO magnitude (per-card disposition) → also in NO_MAGNITUDE_KEYWORDS.
    'reveal-top-dispose',
    // why: WP-702 / D-24521 — "Do the same thing to each other player's deck." (Hypnotic Charm
    // entry 2, [hc:instinct]-gated); has a HERO_EFFECT_HANDLERS entry (heroEffectRevealTopDisposeOthers)
    // that snapshots each OTHER seat's deck top and parks one shared PendingRevealTopDispose, so it
    // belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'reveal-top-dispose-others',
    // why: D-24558 — co2e Hypnotic Charm's "[hc:covert]: You may KO the card you revealed from
    // your own deck."; has a HERO_EFFECT_HANDLERS entry (heroEffectRevealTopDisposeKo) that flags
    // the own revealed top of the pending reveal-top choice, so it belongs here. Carries NO
    // magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'reveal-top-dispose-ko',
    // why: WP-719 / D-24541 — Hawkeye's "Covering Fire" ([hc:tech]-gated "Choose one: each other
    // player draws a card or each other player discards a card"); has a HERO_EFFECT_HANDLERS entry
    // (heroEffectCoveringFire) that parks a PendingCoveringFireChoice for the active player, so it
    // belongs here. Carries NO magnitude (a choose-one branch, not a count) → also in
    // NO_MAGNITUDE_KEYWORDS.
    'covering-fire',
    // why: WP-795 / D-24664 — core Storm's "Spinning Cyclone" (move a Villain to a new City space,
    // rescue its Bystanders, swap if occupied); has a HERO_EFFECT_HANDLERS entry
    // (heroEffectSpinningCyclone) that parks a PendingMoveVillainChoice for the active player, so it
    // belongs here. Carries NO magnitude (a pick, not a count) → also in NO_MAGNITUDE_KEYWORDS.
    'spinning-cyclone',
    // why: WP-803 / D-24692 — Man / Woman Out of Time (rules v23 L1667-1694); has a
    // HERO_EFFECT_HANDLERS entry (heroEffectOutOfTime) that sets the just-played card aside into
    // G.outOfTimeCards, so it belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'out-of-time',
    // why: WP-735 / D-24555 — the Venomverse "Digest N / Indigestion" Victory-Pile-count branch;
    // has a HERO_EFFECT_HANDLERS entry (heroEffectDigestIndigestion) that branches on the Victory-Pile
    // count and dispatches the selected branch via the reentrant executeSingleEffect, so it belongs
    // here (the bidirectional handler-completeness authority). Carries NO top-level magnitude → also
    // in NO_MAGNITUDE_KEYWORDS.
    'digest-indigestion',
    // why: WP-736 / D-24556 — the Venomverse "Excessive Violence" fight-overspend keyword; has a
    // HERO_EFFECT_HANDLERS entry (heroEffectExcessiveViolence) that ENROLLS the played card into the
    // turn-scoped ledger (the inner effects fire later from the fight move's fireExcessiveViolencePlays),
    // so it belongs here (the bidirectional handler-completeness authority). Carries NO top-level
    // magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'excessive-violence',
    // why: WP-753 / D-24580 — "Reveal the top three cards of your deck. Draw one of them, discard
    // one, and KO one." (Crystal of Kadavus, Interplanetary Visitor); has a HERO_EFFECT_HANDLERS
    // entry (heroEffectRevealThreeAssign) that snapshots the top three and parks a
    // PendingRevealThreeAssign, so it belongs here. Carries NO magnitude → also in
    // NO_MAGNITUDE_KEYWORDS.
    'reveal-three-assign',
    // why: WP-753 / D-24580 — Crystal of Kadavus's "[team:venomverse][team:venomverse]: Do this
    // ability again."; has a HERO_EFFECT_HANDLERS entry (heroEffectRevealThreeAssignAgain) that
    // bumps the just-parked entry's repeat counter, so it belongs here. Carries NO magnitude → also
    // in NO_MAGNITUDE_KEYWORDS.
    'reveal-three-assign-again',
    // why: WP-754 / D-24581 — "You may discard a card. If you do, draw a card." (Hungry for
    // Action's Digest 3 branch + four standalone cards); has a HERO_EFFECT_HANDLERS entry
    // (heroEffectOptionalDiscardDraw) that parks a draw-reward entry on the Smash queue, so it
    // belongs here. Carries a magnitude (the draw count) → NOT in NO_MAGNITUDE_KEYWORDS.
    'optional-discard-draw',
    // why: WP-754 / D-24581 — "Reveal the top card of your deck. You may KO it." (Gruesome Feast /
    // Remove His Spine via Excessive Violence + Electroshock Therapy); has a HERO_EFFECT_HANDLERS
    // entry (heroEffectRevealTopMayKo) that parks a KO-or-keep entry on the reveal-top-dispose
    // queue, so it belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'reveal-top-may-ko',
    // why: WP-765 / D-24598 — hero Blood Frenzy (+N attack) and its recruit variant; each has a
    // HERO_EFFECT_HANDLERS entry (heroEffectBloodFrenzy / heroEffectBloodFrenzyRecruit) reading the
    // shared countDistinctVictoryPointValues, so they belong here. Carry NO magnitude → also in
    // NO_MAGNITUDE_KEYWORDS.
    'blood-frenzy',
    'blood-frenzy-recruit',
    // why: WP-765 / D-24598 — the fused Sunlight / Moonlight / "Instead, you get both" composite; has
    // a HERO_EFFECT_HANDLERS entry (heroEffectDayNightBoth) that selects and dispatches the branch(es)
    // via the reentrant executeSingleEffect, so it belongs here. Carries NO top-level magnitude →
    // also in NO_MAGNITUDE_KEYWORDS.
    'day-night-both',
    // why: WP-767 / D-24600 — Snarling Fangs' "you may KO one of your Heroes"; has a
    // HERO_EFFECT_HANDLERS entry (heroEffectOptionalKoYourHero) that parks a no-reward entry into
    // the shared optional-ko-reward queue, so it belongs here. Carries NO magnitude → also in
    // NO_MAGNITUDE_KEYWORDS.
    'optional-ko-your-hero',
    // why: WP-780 / D-24619 — cvwr Penumbra's "play both sides" of a later Divided Card this turn;
    // has a HERO_EFFECT_HANDLERS entry (heroEffectPlayBothSides) that sets the turn-scoped
    // isPlayBothSidesActive flag, so it belongs here. Carries NO magnitude → also in
    // NO_MAGNITUDE_KEYWORDS.
    'play-both-sides',
    // why: WP-794 / D-24663 — "Any Villain you fight on the <space> this turn gets -N" / "The
    // Mastermind gets -N this turn"; has a HERO_EFFECT_HANDLERS entry
    // (heroEffectFightCostReduction) that appends to the lazy fightCostReductions list, so it
    // belongs here. Carries a magnitude (the amount) → NOT in NO_MAGNITUDE_KEYWORDS.
    'fight-cost-reduction',
    // why: WP-826 / D-24759 — the Cap 75th optional-KO S.H.I.E.L.D. family. Each has a
    // HERO_EFFECT_HANDLERS entry that parks (or upgrades) entries on the shared optional-ko-reward
    // queue, so each belongs here. All three carry a magnitude → NOT in NO_MAGNITUDE_KEYWORDS.
    'ko-up-to-shield-hand-discard',
    'attack-per-ko-this-way',
    'optional-ko-shield-played-rescue',
    // why: WP-828 / D-24761 — Winter Soldier's 2>4 "A Hero in your hand gains Man Out of Time this
    // turn"; has a HERO_EFFECT_HANDLERS entry (heroEffectGrantOutOfTime) that queues one owed grant,
    // so it belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'grant-out-of-time',
    // why: WP-843 / D-24777 — co2e Crazed Rampage's "Each player reveals another [hc:strength] Hero
    // or gains a Wound"; has a HERO_EFFECT_HANDLERS entry (heroEffectRevealOrGainWoundEach), so it
    // belongs here. Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'reveal-or-gain-wound-each',
    // why: WP-844 / D-24778 — "Gain a [rule:Sidekick]" / "Gain three [rule:Sidekicks]"; has a
    // HERO_EFFECT_HANDLERS entry (heroEffectGainSidekick), so it belongs here. Carries a magnitude
    // (the Sidekicks gained) → NOT in NO_MAGNITUDE_KEYWORDS.
    'gain-sidekick',
    // why: WP-850 / D-24786 — Bishop's Firepower from the Future. 'discard-top-printed-attack' has a
    // HERO_EFFECT_HANDLERS entry (heroEffectDiscardTopPrintedAttack) and carries a magnitude (the cards
    // discarded) → NOT in NO_MAGNITUDE_KEYWORDS; 'ko-discarded-this-way' has one
    // (heroEffectKoDiscardedThisWay) and carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'discard-top-printed-attack',
    'ko-discarded-this-way',
    // why: WP-860 / D-24796 — Commune with the Spirit World; has a HERO_EFFECT_HANDLERS entry
    // (heroEffectDiscardArtifactOrThreeThenKo) that parks the cost seat choice, so it belongs here.
    // Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'discard-artifact-or-three-then-ko',
    // why: WP-857 / D-24793 — hero Feast; its HERO_EFFECT_HANDLERS entry is the shipped
    // heroEffectRevealTopMayKo (parks a KO-or-keep reveal of the deck top), so it belongs here.
    // Carries NO magnitude → also in NO_MAGNITUDE_KEYWORDS.
    'feast',
]);
// why: the 7 frozen legacy reveal keywords (REVEAL_KEYWORDS minus 'reveal') keep NO
// handler — they translate to a 'reveal' descriptor at parse time. They remain in
// the executable-coverage set so the drift test still recognizes them as reachable.
const FROZEN_REVEAL_TRANSLATED = [
    'reveal-ko', 'reveal-min', 'reveal-ko-or-draw', 'reveal-cost-attack', 'reveal-odd-draw', 'reveal-attack-choose', 'reveal-ko-attack',
];
// why: D-24049 — wall-crawl executes at RECRUIT time (the recruitHero deck-top
// placement), so it has NO HERO_EFFECT_HANDLERS entry and is NOT in HANDLED_KEYWORDS.
// It still joins MVP_KEYWORDS via this recruit-time category for two load-bearing
// reasons: (a) the hero mechanic ledger classifies an MVP_KEYWORDS member `executable`;
// (b) classifyHeroEffectReason returns `applied` for it, so the onRecruit hook that
// executeHeroEffects visits at PLAY time (it does not filter by timing) classifies
// not-hollow instead of firing a `no-handler` hollow — without this membership the
// now-recognized keyword would trade the old parse-unrecognized hollow for a fresh
// no-handler one (a regression). NOT added to HANDLED_KEYWORDS (that demands a handler
// and would break the HERO_EFFECT_HANDLERS-keys ↔ HANDLED_KEYWORDS bidirectional test).
export const RECRUIT_TIME_EXECUTED_KEYWORDS = ['wall-crawl'];
// why: D-24051 — dodge executes from the dodgeCard hand-discard-to-draw MOVE (the player
// discards a Dodge card from hand to draw a replacement), so — like wall-crawl — it has NO
// HERO_EFFECT_HANDLERS entry and is NOT in HANDLED_KEYWORDS. It still joins MVP_KEYWORDS via
// this hand-action category for two load-bearing reasons: (a) the hero mechanic ledger
// classifies an MVP_KEYWORDS member `executable`; (b) classifyHeroEffectReason returns
// `applied` for it, so the onPlay hook that executeHeroEffects visits at PLAY time (it does
// not filter by timing) classifies not-hollow instead of firing a `no-handler` hollow —
// without this membership the now-recognized keyword would trade the old parse-unrecognized
// hollow for a fresh no-handler one (a regression). Kept a SEPARATE set from
// RECRUIT_TIME_EXECUTED_KEYWORDS (duplicate-first per §16.1 — the two categories execute at
// different times and a premature merge would blur that); NOT added to HANDLED_KEYWORDS
// (that demands a handler and would break the HERO_EFFECT_HANDLERS-keys ↔ HANDLED_KEYWORDS
// bidirectional test).
export const HAND_ACTION_EXECUTED_KEYWORDS = [
    'dodge',
    // why: WP-783 / D-24629 — phasing is executed by the phaseCard hand move (swap a
    // Phasing card in hand with the top card of the deck), not on play. Same category and
    // same two reasons as dodge above: ledger-`executable`, and the play-time visit of its
    // onPlay hook classifies `applied` (no hollow) while the no-magnitude effect no-ops.
    'phasing',
];
// why: WP-678 / D-24494 (supersedes D-24060) — the WP-282 face-down-execution category is
// retired. Undercover no longer executes via a face-down store (that model was dead code —
// removed with the sendUndercover/playFromUndercover moves + the faceDownCards zone). Its two
// real, handler-bearing source-shape keywords (undercover-hand-shield-hero /
// undercover-officer-stack) are in HANDLED_KEYWORDS; the bare 'undercover' token is now an
// honest hollow (no source zone → no handler), so it is intentionally NOT in MVP_KEYWORDS.
// why: D-24801 (supersedes D-24074's class grant) — the two Size-Changing keywords are recruit discounts applied at recruit time (no onPlay handler); membership keeps the play-time hook visit not-hollow (the wall-crawl pattern)
// Size-Changing ("you can recruit this card for 2 less if you played any [Class] cards this
// turn", rules v23 p.33) and Microscopic Size-Changing (2 less per matching card played, one
// per icon, rules v23 p.30) have NO HERO_EFFECT_HANDLERS entry and are NOT in
// HANDLED_KEYWORDS — like wall-crawl / dodge / undercover. Their effect is realized by the
// recruit-cost reader (resolveRecruitCost in sizeChanging.logic.ts, read by recruitHero, the
// legal-move list and the HQ cost projection), not by an onPlay action.
// They still join MVP_KEYWORDS via this recruit-discount category for two load-bearing reasons:
// (a) the hero mechanic ledger classifies an MVP_KEYWORDS member `executable`; (b)
// classifyHeroEffectReason returns `applied` for it, so the onPlay hook that
// executeHeroEffects visits at PLAY time classifies not-hollow instead of firing a
// `no-handler` hollow — without this membership the now-recognized keyword would trade the
// old parse-unrecognized hollow for a fresh no-handler one (a regression). Kept a SEPARATE
// set from the recruit-/hand-/face-down-action categories (duplicate-first per §16.1 — they
// execute at different sites); NOT added to HANDLED_KEYWORDS (that demands a handler and
// would break the HERO_EFFECT_HANDLERS-keys ↔ HANDLED_KEYWORDS bidirectional test).
export const SIZE_CHANGING_RECRUIT_KEYWORDS = ['size-changing', 'microscopic-size-changing'];
// why: WP-498 / D-24301 — return-on-discard executes REACTIVELY at the discardFromHand
// chokepoint (checkReturnOnDiscard parks a pending choice when a card effect discards the
// marked card from hand), so — like wall-crawl / dodge / undercover / size-changing — it has
// NO HERO_EFFECT_HANDLERS entry and is NOT in HANDLED_KEYWORDS. It still joins MVP_KEYWORDS
// via this discard-time category for two load-bearing reasons: (a) the hero mechanic ledger
// classifies an MVP_KEYWORDS member `executable`; (b) classifyHeroEffectReason returns
// `applied` for it, so the onDiscard hook that executeHeroEffects visits at PLAY time (it does
// not filter by timing) classifies not-hollow instead of firing a `no-handler` hollow —
// without this membership the now-recognized keyword would trade the old parse-unrecognized
// hollow for a fresh no-handler one (a regression). Kept a SEPARATE set from the
// recruit-/hand-/face-down-/size-changing categories (duplicate-first per §16.1 — they execute
// at different sites); NOT added to HANDLED_KEYWORDS (that demands a handler and would break
// the HERO_EFFECT_HANDLERS-keys ↔ HANDLED_KEYWORDS bidirectional test).
// why: WP-705 / D-24526 — teleport-on-discard (Guerrilla Warfare) is a SECOND reactive
// discard-time keyword: it fires at the discardFromHand chokepoint (checkTeleportOnDiscard),
// never at play time. Enrolling it here → MVP_KEYWORDS keeps the play-time hook visit
// (executeHeroEffects does not filter by timing) classifying `applied` instead of a
// `no-handler` hollow, exactly like return-on-discard. NOT in HANDLED_KEYWORDS (no handler).
export const DISCARD_TIME_EXECUTED_KEYWORDS = [
    'return-on-discard',
    'teleport-on-discard',
];
// why: WP-682 / D-24499 — diving-block executes REACTIVELY at the gainWoundForPlayer
// chokepoint (checkDivingBlock parks a reveal/decline seat choice when a player holding
// Diving Block gains a Wound), so — exactly like return-on-discard at the discard
// chokepoint — it has NO HERO_EFFECT_HANDLERS entry and is NOT in HANDLED_KEYWORDS. It
// still joins MVP_KEYWORDS via this wound-time category for the two load-bearing reasons
// the sibling categories document: (a) the hero mechanic ledger classifies an MVP_KEYWORDS
// member `executable`; (b) classifyHeroEffectReason returns `applied` for it, so the
// onPlay hook that executeHeroEffects visits at PLAY time (it does not filter by timing)
// classifies not-hollow instead of firing a `no-handler` hollow. Kept a SEPARATE set from
// DISCARD_TIME_EXECUTED_KEYWORDS (duplicate-first per §16.1 — they execute at different
// chokepoints); NOT in HANDLED_KEYWORDS (no handler) and NOT in NO_MAGNITUDE_KEYWORDS (it
// never runs at play time, exactly like return-on-discard — the pre-gate drop is the
// correct no-op there).
// why: WP-827 / D-24760 — flying-shield-block executes at the same chokepoint
// (checkFlyingShieldBlock), so it joins this set for the same two reasons.
export const WOUND_TIME_EXECUTED_KEYWORDS = ['diving-block', 'flying-shield-block'];
// why: WP-858 / D-24794 — the Ritual Artifact keywords execute from the useRitualArtifact MOVE
// (moves/ritualArtifact.ts: the player discards a controlled or just-played Ritual Artifact for its
// reward once its condition held this turn), so — like phasing / dodge — they have NO
// HERO_EFFECT_HANDLERS entry and are NOT in HANDLED_KEYWORDS. They join MVP_KEYWORDS via this
// category for the same two load-bearing reasons: (a) the hero mechanic ledger classifies an
// MVP_KEYWORDS member `executable`; (b) classifyHeroEffectReason returns `applied` for the onPlay
// hook that executeHeroEffects visits at PLAY time, so playing the card grants nothing and records
// no hollow. Kept a SEPARATE set from HAND_ACTION_EXECUTED_KEYWORDS (duplicate-first per §16.1 —
// they execute from different moves on different zones).
export const RITUAL_ARTIFACT_EXECUTED_KEYWORDS = [
    'ritual-recruited-hero',
    'ritual-hero-classes',
];
// why (WP-251 / D-24024; D-24049; D-24051; D-24060): MVP_KEYWORDS = HANDLED_KEYWORDS ∪ the
// frozen-translated reveal keywords ∪ the recruit-time-executed keywords ∪ the
// hand-action-executed keywords ∪ the face-down-action-executed keywords — the set of
// keywords that execute (directly via a handler, via reveal translation, at recruit time via
// the recruitHero placement, via a hand-action move like dodgeCard, or via the face-down
// send/play moves). The executeSingleEffect pre-gate keys on it; the coverage drift test
// asserts every member is handled directly, reveal-translated, recruit-time-executed,
// hand-action-executed, OR face-down-executed. Do not duplicate this set elsewhere (the
// coverage probe's EXECUTED_KEYWORDS is a separate, informational copy).
export const MVP_KEYWORDS = new Set([
    ...HANDLED_KEYWORDS,
    ...FROZEN_REVEAL_TRANSLATED,
    ...RECRUIT_TIME_EXECUTED_KEYWORDS,
    ...HAND_ACTION_EXECUTED_KEYWORDS,
    ...SIZE_CHANGING_RECRUIT_KEYWORDS,
    ...DISCARD_TIME_EXECUTED_KEYWORDS,
    // why: WP-682 / D-24499 — the reactive wound-time keyword (diving-block); see the set above.
    ...WOUND_TIME_EXECUTED_KEYWORDS,
    // why: WP-858 / D-24794 — the move-executed Ritual Artifact keywords; see the set above.
    ...RITUAL_ARTIFACT_EXECUTED_KEYWORDS,
]);
// why: D-24019 — the reward of an optional-ko-reward effect is dispatched to an
// ALREADY-BUILT reward executor; only these four are seeded. Defensive guard at
// the park site: the parser already filters unseeded rewards, so an unseeded
// type here is a logged no-op that never reaches the pending queue. Mirrors the
// same constant in setup/heroAbility.setup.ts (two copies, per duplicate-first).
const OPTIONAL_KO_REWARD_SEEDED_REWARDS = new Set([
    'rescue',
    'draw',
    'attack',
    'recruit',
]);
// why: WP-382 / D-24183 — the ko-wound-reward reward is dispatched to an
// ALREADY-BUILT reward executor; only these three are seeded for the family's
// core vocabulary (draw / attack / recruit — the no-reward and Berserk members
// stay hollow, Honest-Partial). Mirrors the same constant in
// setup/heroAbility.setup.ts (two copies, per duplicate-first).
const KO_WOUND_REWARD_SEEDED_REWARDS = new Set([
    'draw',
    'attack',
    'recruit',
]);
// why: these keywords bypass the executeSingleEffect pre-check magnitude gate.
// 'rescue' defaults its magnitude to 1. 'reveal' is here because the collapsed
// reveal handler (D-24024) routes ALL 8 legacy reveal-* variants — including the
// no-magnitude ones (reveal-ko / reveal-odd-draw / reveal-cost-attack) and the
// M=0-valid ones (reveal / reveal-min) — so ALL reveal magnitude gating now lives
// in revealRulesForLegacyKeyword + the per-rule predicates, NEVER at this top-level
// gate. The 7 legacy reveal-* keywords no longer reach the pre-gate (they are
// translated to 'reveal' at parse time), so their former entries are dropped.
// (D-24024 / pre-flight PS-1)
const NO_MAGNITUDE_KEYWORDS = new Set([
    'rescue', 'reveal',
    // why: D-24156 — "gain a Wound" is exactly one Wound; the tokens carry no
    // magnitude segment, so the magnitude pre-gate must not drop them.
    'gain-wound-self', 'gain-wound-each',
    // why: WP-721 / D-24542 — ko-wound carries NO magnitude (it KOs exactly one Wound, no reward);
    // the Wound target is read from hand/discard at play time, so the magnitude pre-gate must not
    // drop it, or heroEffectKoWound never fires. (ko-wound-reward is NOT here — it carries the
    // reward magnitude.)
    'ko-wound',
    // why: victory-villain-attack parks a pending pick; the attack amount is read
    // from the chosen villain's fightCost at resolve time, not from a static magnitude
    'victory-villain-attack',
    // why: draw-or-empowered parks a pending choice carrying empoweredClass (not a magnitude);
    // the draw or the empowered grant is applied at resolve time, not at play time (D-24069)
    'draw-or-empowered',
    // why: WP-675 / D-24490 — count-scaled-choose parks a pending choice carrying two options
    // (each with its own per-unit magnitude), not a top-level magnitude; the chosen grant is
    // applied at resolve time, so the pre-gate must not drop it.
    'count-scaled-choose',
    // why: WP-486 / D-24291 — defeat-with-bystander carries no magnitude (it defeats one
    // eligible target); the target set is computed from G at play time, so the magnitude
    // pre-gate must not drop it.
    'defeat-with-bystander',
    // why: WP-535 / D-24345 — copy-powers carries no magnitude (it copies exactly one Hero);
    // the eligible-Hero set is computed from inPlay at play time, so the magnitude pre-gate
    // must not drop it.
    'copy-powers',
    // why: WP-580 / D-24389 — recruit-as-attack carries no magnitude (it sets a turn-scoped
    // conversion flag, grants no resource total); the magnitude pre-gate must not drop it,
    // or the handler never fires and the flag is never set (the live-verify defect).
    'recruit-as-attack',
    // why: WP-731 / D-24552 — no-more-draws carries no magnitude (it sets the turn-scoped
    // drawsLocked flag, moves no card); the magnitude pre-gate must not drop it, or the
    // handler never fires and the lock never arms.
    'no-more-draws',
    // why: WP-592 / D-24401 — steal-abilities carries no magnitude (it discards each deck top
    // and copies each discarded card); the target set is computed from G at play time, so the
    // magnitude pre-gate must not drop it, or the handler never fires (the silent-no-op defect).
    'steal-abilities',
    // why: WP-564 / D-24373 — investigate carries no magnitude (the look count and criterion
    // ride dedicated descriptor fields, not the magnitude); the magnitude pre-gate must not
    // drop it, or the handler never fires.
    'investigate',
    // why: WP-658 / D-24469 — transform carries no magnitude (the second-form target is read
    // from G.transformTargets, and the pulled instance's printed attack/recruit come from
    // G.cardStats); the magnitude pre-gate must not drop it, or the swap never fires.
    'transform',
    // why: WP-659 / D-24470 — reveal-from-hand carries no magnitude (each matching player draws
    // exactly one card; the set of drawing players is computed from each hand at play time), so
    // the magnitude pre-gate must not drop it, or the reveal-draw never fires.
    'reveal-from-hand',
    // why: WP-667 / D-24480 — optional-ko-hand-discard carries NO magnitude (no reward — it parks
    // an optional KO of one hand/discard card); the eligible set is computed from G at play time,
    // so the magnitude pre-gate must not drop it, or the KO choice never parks.
    'optional-ko-hand-discard',
    // why: WP-678 / D-24494 — the two Undercover source-shape keywords carry NO magnitude ("send
    // a Hero/Officer Undercover" — always one card); the eligible source is read from G at play
    // time, so the magnitude pre-gate must not drop them, or the send never fires / the pick never parks.
    'undercover-hand-shield-hero',
    'undercover-officer-stack',
    // why: WP-683 / D-24500 — here-hold-this carries NO magnitude (it attaches exactly one
    // Bystander to the chosen Villain); the eligible City set is computed from G at play time,
    // so the magnitude pre-gate must not drop it, or the capture / pick never fires.
    'here-hold-this',
    // why: WP-683 / D-24500 — random-acts carries NO magnitude (the optional Wound is one, the
    // pass is one card per seat); both steps are resolved via pending choices, so the magnitude
    // pre-gate must not drop it, or the wound choice never parks.
    'random-acts',
    // why: WP-681 / D-24498 — do-over carries NO magnitude (the draw amount is the fixed
    // printed 4, applied at resolve time, not a play-time grant); the magnitude pre-gate must
    // not drop it, or the accept/decline choice never parks.
    'do-over',
    // why: WP-681 / D-24498 — optional-ko-shield-officer carries NO magnitude (the reward is a
    // fixed single S.H.I.E.L.D. Officer to hand, dispatched at resolve time); the eligible KO set
    // is computed from G at play time, so the magnitude pre-gate must not drop it, or the KO
    // choice never parks.
    'optional-ko-shield-officer',
    // why: WP-682 / D-24499 — pure-fury carries NO magnitude (it defeats one eligible target
    // for free); the eligible target set + the S.H.I.E.L.D.-Hero KO count are computed from G
    // at play time, so the magnitude pre-gate must not drop it, or heroEffectPureFury never runs.
    'pure-fury',
    // why: WP-702 / D-24521 — the two reveal-top-dispose keywords carry NO magnitude: the outcome
    // is a per-card discard-or-keep disposition, not a count. The revealed set is snapshotted from
    // the deck top(s) at play time, so the magnitude pre-gate must not drop them, or the reveal
    // never parks its choice.
    'reveal-top-dispose',
    'reveal-top-dispose-others',
    // why: D-24558 — reveal-top-dispose-ko carries NO magnitude (it unlocks a disposition on an
    // already-parked reveal); the magnitude pre-gate must not drop it, or the KO never unlocks.
    'reveal-top-dispose-ko',
    // why: WP-719 / D-24541 — covering-fire carries NO magnitude: the outcome is a choose-one
    // branch (each other player draws OR discards), not a count. The per-seat effect is applied at
    // resolve time, so the magnitude pre-gate must not drop it, or heroEffectCoveringFire never
    // parks its choice.
    'covering-fire',
    // why: WP-795 / D-24664 — spinning-cyclone carries NO magnitude: the outcome is a pick (which
    // Villain, which space), resolved against the live City at resolve time, so the magnitude
    // pre-gate must not drop it, or heroEffectSpinningCyclone never parks its choice.
    'spinning-cyclone',
    // why: WP-803 / D-24692 — out-of-time carries NO magnitude (it moves the played card itself),
    // so the magnitude pre-gate must not drop it, or heroEffectOutOfTime never sets the card aside.
    'out-of-time',
    // why: WP-828 / D-24761 — grant-out-of-time carries NO magnitude (one grant per line), so the
    // magnitude pre-gate must not drop it, or heroEffectGrantOutOfTime never queues the grant.
    'grant-out-of-time',
    // why: WP-843 / D-24777 — reveal-or-gain-wound-each carries NO magnitude (each player who
    // cannot reveal gains exactly one Wound), so the magnitude pre-gate must not drop it, or
    // heroEffectRevealOrGainWoundEach never runs and no Wound lands.
    'reveal-or-gain-wound-each',
    // why: WP-850 / D-24786 — ko-discarded-this-way carries NO magnitude ("any number of those
    // cards" is read from the record the line before it wrote), so the magnitude pre-gate must not
    // drop it, or heroEffectKoDiscardedThisWay never parks the KO choice.
    'ko-discarded-this-way',
    // why: WP-860 / D-24796 — discard-artifact-or-three-then-ko carries NO magnitude (the cost and
    // the KO are chosen through seat choices), so the magnitude pre-gate must not drop it, or
    // heroEffectDiscardArtifactOrThreeThenKo never parks the Commune cost choice.
    'discard-artifact-or-three-then-ko',
    // why: WP-735 / D-24555 — digest-indigestion carries NO top-level magnitude (the branch
    // magnitudes ride the nested inline effects: +attack / +recruit / draw:N / rescue:N). The
    // magnitude pre-gate must not drop it, or heroEffectDigestIndigestion never fires and the
    // branch never runs.
    'digest-indigestion',
    // why: WP-736 / D-24556 — excessive-violence carries NO top-level magnitude (the wrapper only
    // enrolls the card; the inner draw:N / rescue:N / optional-ko / +recruit effects ride their own
    // markers inside excessiveViolenceEffects). The magnitude pre-gate must not drop it, or
    // heroEffectExcessiveViolence never fires and the card is never enrolled.
    'excessive-violence',
    // why: WP-753 / D-24580 — both reveal-three-assign keywords carry NO magnitude: the outcome is
    // a per-card draw / discard / KO assignment (and a repeat counter), not a count. The revealed
    // set is snapshotted from the deck top at play time, so the magnitude pre-gate must not drop
    // them, or the reveal never parks its choice and the repeat never arms.
    'reveal-three-assign',
    'reveal-three-assign-again',
    // why: WP-754 / D-24581 — reveal-top-may-ko carries NO magnitude: the outcome is a single
    // KO-or-keep disposition of one revealed card, not a count. The deck top is snapshotted at play
    // (or Excessive Violence fire) time, so the magnitude pre-gate must not drop it, or the reveal
    // never parks its choice. (optional-discard-draw is NOT here — it carries the draw count.)
    'reveal-top-may-ko',
    // why: WP-857 / D-24793 — feast shares reveal-top-may-ko's handler and likewise carries NO
    // magnitude (one KO-or-keep disposition of the deck top). The magnitude pre-gate must not drop
    // it, or the handler is registered but never reached and the Feast never parks its prompt.
    'feast',
    // why: WP-765 / D-24598 — blood-frenzy / blood-frenzy-recruit carry NO magnitude (the grant is
    // the distinct-VP count, read at resolve time) and day-night-both carries NO top-level magnitude
    // (the branch magnitudes ride the nested sunlightEffects / moonlightEffects). The magnitude
    // pre-gate must not drop them, or the handlers never run.
    'blood-frenzy',
    'blood-frenzy-recruit',
    'day-night-both',
    // why: WP-767 / D-24600 — optional-ko-your-hero carries NO magnitude (it offers exactly one
    // optional KO, no reward); the eligible Heroes are read from hand + play at park time, so the
    // magnitude pre-gate must not drop it, or the per-defeat choice never parks.
    'optional-ko-your-hero',
    // why: WP-780 / D-24619 — play-both-sides carries NO magnitude (it sets the turn-scoped
    // isPlayBothSidesActive flag, moves no card); the magnitude pre-gate must not drop it, or the
    // handler never fires, the flag never arms, and the choose-a-side picker still appears.
    'play-both-sides',
]);
// ---------------------------------------------------------------------------
// Magnitude validation
// ---------------------------------------------------------------------------
/**
 * Returns true if magnitude is a finite integer >= 0.
 *
 * @param magnitude - The magnitude value from a HeroEffectDescriptor.
 * @returns Whether the magnitude is valid for execution.
 */
function isValidMagnitude(magnitude) {
    if (magnitude === undefined) {
        return false;
    }
    if (!Number.isFinite(magnitude)) {
        return false;
    }
    if (magnitude < 0) {
        return false;
    }
    if (!Number.isInteger(magnitude)) {
        return false;
    }
    return true;
}
/**
 * Returns whether executeSingleEffect's magnitude pre-gate drops this effect before
 * its handler runs.
 *
 * why: D-24649 — the one copy of the pre-gate rule. executeSingleEffect enforces it;
 * classifyHeroEffectReason (hollow detection) and hookHasDispatchableEffect (the
 * Synergy Rate) read it, so the three cannot drift apart. `ko` and every
 * NO_MAGNITUDE_KEYWORDS member bypass the gate; any other keyword needs a valid
 * magnitude.
 *
 * @param effect - The declared hero effect descriptor.
 * @returns Whether the pre-gate drops the effect for a missing or invalid magnitude.
 */
function failsMagnitudePreGate(effect) {
    if (effect.type === 'ko' || NO_MAGNITUDE_KEYWORDS.has(effect.type)) {
        return false;
    }
    return !isValidMagnitude(effect.magnitude);
}
// ---------------------------------------------------------------------------
// Draw helper (extracted from drawCards move logic)
// ---------------------------------------------------------------------------
/**
 * Draws cards from a player's deck into their hand.
 *
 * Replicates the draw algorithm from drawCards (coreMoves.impl.ts:52-76)
 * without the move validation and stage gating — those are the move's
 * responsibility, already handled by playCard before this function runs.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - Active player whose zones to modify.
 * @param count - Number of cards to draw.
 * @param shuffleContext - ShuffleProvider for deterministic reshuffle.
 * @returns How many cards were actually drawn (WP-417 — fewer than `count` when
 *   the deck and discard pile both ran dry; the caller logs the realized amount).
 */
function drawFromPlayerDeck(G, playerID, count, shuffleContext) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return 0;
    }
    let drawnCount = 0;
    for (let cardsDrawn = 0; cardsDrawn < count; cardsDrawn++) {
        // If deck is empty, attempt reshuffle from discard
        if (playerZones.deck.length === 0) {
            if (playerZones.discard.length === 0) {
                // No cards available anywhere — stop drawing
                return drawnCount;
            }
            // why: Reshuffling discard into deck is the standard Legendary rule
            // when the draw pile is exhausted. Uses ShuffleProvider for
            // deterministic shuffling — same pattern as drawCards move.
            const reshuffled = moveAllCards(playerZones.discard, []);
            playerZones.discard = reshuffled.from;
            playerZones.deck = shuffleDeck(reshuffled.to, shuffleContext);
        }
        const topCard = playerZones.deck[0];
        if (!topCard) {
            return drawnCount;
        }
        const result = moveCardFromZone(playerZones.deck, playerZones.hand, topCard);
        playerZones.deck = result.from;
        playerZones.hand = result.to;
        drawnCount++;
    }
    return drawnCount;
}
// ---------------------------------------------------------------------------
// executeHeroEffects — main entry point
// ---------------------------------------------------------------------------
/**
 * Applies one hook's effects and records its traces + hollow classification.
 *
 * why: WP-568 — extracted from executeHeroEffects' loop body so the DEFERRED
 * re-check fires a hook through exactly the same path the immediate play does. A
 * second dispatch copy would be free to drift, which is the class of defect the
 * hero-effect arc has already paid for. Callers must have confirmed the hook's
 * conditions pass; this function does not re-gate.
 *
 * @param G - The game state, mutated in place.
 * @param ctx - Move context (carries `random` for draws/reveals).
 * @param playerID - The acting player.
 * @param cardId - The played card that owns the hook.
 * @param hook - The hook whose effects to apply.
 * @param turn - Turn number stamped into each emitted EffectTrace.
 * @returns How many effects reached a handler.
 */
function runHookEffects(G, ctx, playerID, cardId, hook, turn) {
    let firedEffectCount = 0;
    if (hook.effects !== undefined) {
        for (const effect of hook.effects) {
            const fired = executeSingleEffect(G, ctx, playerID, cardId, effect);
            if (fired) {
                firedEffectCount++;
            }
            // why: WP-706 / D-24528 — for a count-scaled effect, re-resolve the realized
            // computation from the SETTLED G at trace-build time (the void executor does not
            // surface count/perEach/magnitude/grant). Deterministic: the grant added to
            // turnEconomy without moving any inPlay card in this same loop iteration, so the
            // re-read equals the granted count. undefined for a non-count-scaled effect, so the
            // pure assembler omits the `resolution` key.
            const resolution = buildCountScaledResolution(G, playerID, cardId, effect);
            // why: WP-488 / D-24294 — emit a trace per legacy hero effect dispatch from THIS
            // caller loop (it carries turn + hook.timing + cardId + the descriptor + the
            // dispatch boolean together). `fired` maps to `fired`/`no-handler`. Inert +
            // hash-excluded.
            recordEffectTrace(G, buildHeroLegacyEffectTrace(cardId, hook.timing, effect, fired, turn, resolution));
        }
    }
    // why: D-24031 / RISK-2 — primitiveEffects run AFTER the legacy `effects` loop, in
    // array order, inside the same conditions-passed gate. The legacy-then-primitive
    // order is locked for determinism: a line carrying both a legacy effect (e.g. an
    // [icon:recruit]) and the Berserk composition applies them in a fixed order. Each
    // top-level node gets its own fresh, never-persisted execution context.
    if (hook.primitiveEffects !== undefined) {
        for (const primitiveEffect of hook.primitiveEffects) {
            // why: WP-317 — pass the hook's source card so a composable gain-resource grant
            // (Empowered / Berserk) logs which card granted, mirroring the card-named
            // `did not activate` line above.
            const fired = interpretHeroPrimitiveEffect(G, ctx, playerID, primitiveEffect, cardId);
            if (fired) {
                firedEffectCount++;
            }
            // why: WP-488 / D-24294 — emit a trace per primitive-composition hero effect
            // dispatch (hero-primitive), so composed hero effects are not untraced. `fired`
            // maps to `fired`/`no-handler` by interpretHeroPrimitiveEffect's boolean.
            recordEffectTrace(G, buildHeroPrimitiveEffectTrace(cardId, hook.timing, primitiveEffect, fired, turn));
        }
    }
    // why: WP-257 / D-24033 — AFTER the hook ran, classify whether the whole hook was
    // hollow (per-hook rule, NOT state-diff): the detector asks "did any declared
    // mechanic on this line reach an executable handler?" — never whether G changed.
    // recordHollowEffect fires only when NO declared effect was reachable AND ≥1 was a
    // hollow reason (mixed-hook lines with ≥1 reachable effect never flag).
    detectHollowHeroHook(G, ctx, cardId, hook);
    return firedEffectCount;
}
/**
 * Executes hero ability effects for a played card.
 *
 * Called from playCard after the card is placed in inPlay and base stats
 * are applied. Iterates hooks in registration order, effects in descriptor
 * array order. Hooks with conditions are evaluated via evaluateAllConditions
 * (WP-023) — effects execute only when ALL conditions pass. Unsupported
 * keywords and invalid magnitudes are skipped.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - boardgame.io context passed as unknown to avoid importing
 *   boardgame.io. Narrowed to ShuffleProvider at the draw call site.
 * @param playerID - Active player ID (plain string, no framework import).
 * @param cardId - The CardExtId of the hero card that was just played.
 * @returns The count of hero effects that fired for this play (WP-409 / D-24221) —
 *   observability only, stashed on G.lastPlayEffectsFired by applyCardPlay.
 */
export function executeHeroEffects(G, ctx, playerID, cardId) {
    // why: guard against G states that predate WP-021 (e.g., older test
    // mocks that don't include heroAbilityHooks). No hooks means no effects.
    if (!G.heroAbilityHooks || G.heroAbilityHooks.length === 0) {
        return 0;
    }
    const hooks = getHooksForCard(G.heroAbilityHooks, cardId);
    // why: WP-409 / D-24221 — tally the hero effects that FIRED for this play (each
    // executeSingleEffect / interpretHeroPrimitiveEffect that reached a handler).
    // Condition-gated hooks `continue` below and contribute nothing, so a
    // synergy-unlocked hook raises the count — the honest "how much did this play do"
    // signal for the future combo cue. Explicit counter, not .reduce() (effect
    // application). Returned to applyCardPlay; NOT read by any rule.
    let firedEffectCount = 0;
    // why: WP-488 / D-24294 — the turn stamped on every EffectTrace this play emits, read
    // once from the FnContext wrapper (readTurnNumber reads the nested bgio ctx.turn). Reused
    // for both hero sub-paths below rather than re-read per effect.
    const turn = readTurnNumber(ctx);
    for (const hook of hooks) {
        // why: WP-708 / D-24531 — a hook is a countable "conditional clause" for the
        // per-match Synergy Rate iff it carries >=1 condition AND an executable effect
        // body. An unconditional hook has no synergy decision; a hollow (unimplemented)
        // body is our backlog, never the player's miss — both are excluded from the tally.
        // why: WP-765 / D-24598 — day/night conditions are excluded from the clause count: Sunlight /
        // Moonlight is board state, not a synergy the player built, and counting it would log a
        // guaranteed "miss" for one line of every two-line day/night card on every play.
        // why: D-24649 — the body must DISPATCH at play, not merely be reachable. A hook whose
        // only effects are magnitude-less icons ("use this bonus [icon:attack] against the
        // Mastermind") or a keyword with no play-time handler (Goblin Glider's gated Dodge)
        // does nothing when its condition holds, so counting it would log a fake "assembled".
        const isCountableConditionalClause = countSynergyConditions(hook) > 0 && hookHasDispatchableEffect(hook);
        // why: WP-853 / D-24789 — an owned-KO trigger ("Whenever a card you own is KO'd this
        // turn") starts when the card is played, so it ALWAYS waits once its other conditions
        // hold — even if the owned-KO count is already above 0 from an earlier KO in this
        // same move. Without this, that count would pass the gate now, fire once, record
        // nothing and miss every later KO this turn. Only the owned-KO type: the defeat family
        // keeps today's path (its flag is never set at play, and Snarling Fangs under Sunlight
        // must keep its "did not activate" line, D-24598).
        // why: WP-856 / D-24792 — the Hero-KO trigger ("The first time that one of your Heroes
        // is KO'd this turn") starts at play the same way, so it takes this branch too (RS-1:
        // a Hero KO earlier in the same move does not fire it at play).
        // why: WP-855 / D-24791 — the deck-KO trigger ("Whenever a card is KO'd from your deck
        // this turn") is the other per-KO trigger, so it always waits at play too: a deck KO
        // earlier in the same move must not fire it now and leave later KOs uncredited.
        const ownedKoCondition = findConditionOfType(hook, OWNED_CARD_KOED_CONDITION_TYPE) ??
            findConditionOfType(hook, OWNED_HERO_KOED_CONDITION_TYPE) ??
            findConditionOfType(hook, DECK_CARD_KOED_CONDITION_TYPE);
        if (ownedKoCondition !== undefined &&
            evaluateAllConditions(G, playerID, conditionsWithout(hook, ownedKoCondition), cardId)) {
            recordDeferredConditionalGrant(G, playerID, cardId, G.heroAbilityHooks.indexOf(hook));
            const ownedKoReason = describeFailedCondition(G, playerID, ownedKoCondition);
            const ownedKoHollowNote = hookHasHollowBody(hook) ? ' Its effect is not supported yet.' : '';
            const ownedKoWaitingOutcome = ownedKoHollowNote !== ''
                ? ownedKoHollowNote
                : ' It will apply if you reach it this turn.';
            pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} ability is waiting — ${ownedKoReason}.${ownedKoWaitingOutcome}`, 'neutral', cardId);
            // why: same tally posture as the wait-and-see branch below — a waiting trigger is
            // neither assembled nor a miss (WP-708).
            continue;
        }
        // why: cardId is threaded through to condition evaluation so heroClassMatch
        // and requiresTeam can exclude the triggering card from their inPlay scan
        // (self-exclusion rule — a card's own class/team does not satisfy its own
        // superpower).
        if (!evaluateAllConditions(G, playerID, hook.conditions, cardId)) {
            // why: WP-257 — a hook whose conditions failed reached a real (condition)
            // handler and intentionally did not execute — a `condition-failed` reachable
            // outcome, NOT hollow. No hollow-detection record is emitted (the WP-257
            // detector channel is untouched by this branch).
            // why: WP-295 / D-24082 — but DO surface a human-readable log line so a
            // class/team-synergy gate that suppressed the ability is observable in the
            // game log (G.messages -> UIState.log) instead of a silent skip — the
            // exact "the effect did nothing" confusion from the live diagnostic.
            // why: WP-434 — a class/synergy-gated ability that did not fire is `blocked`
            // (the effect was suppressed and nothing happened) per the LOG_OUTCOMES taxonomy.
            // why: WP-566 / D-24375 — name the condition that ACTUALLY failed. The
            // previous single string claimed "Hero class or team synergy" for every
            // failure; it was right for 2 of 4 constructed types and wrong for both
            // numeric-threshold ones, so it fired 8 times on Surge of Power (a recruit
            // gate with no class or team component) in one observed match while being
            // simultaneously correct for three class-gated cards.
            // why: findFailedCondition is a SIBLING of evaluateAllConditions and walks
            // the same short-circuit order, so the reported condition is the one that
            // stopped the ability. The guard covers the unreachable case where the
            // conditions pass on the second walk; the generic clause keeps the line
            // present, because D-24082 requires a gated ability to stay observable.
            const failedCondition = findFailedCondition(G, playerID, hook.conditions, cardId);
            const reason = failedCondition === undefined
                ? 'a play condition was not met'
                : describeFailedCondition(G, playerID, failedCondition);
            // why: WP-568 / D-24377 — a NUMERIC-THRESHOLD gate is a whole-turn window,
            // not a snapshot. Record it and say so; the per-move re-check fires it if the
            // threshold is reached later this turn. This is a THIRD log state and must not
            // reuse the "did not activate" wording above: not-yet-met is not the same as
            // failed, and a player who reads "did not activate" stops trying.
            // why: `neutral`, not `blocked`. LOG_OUTCOMES has no waiting member and adding
            // one is a canonical-array change out of scope here; `blocked` means the effect
            // was suppressed and nothing happened, which is wrong for an ability that may
            // still fire this turn.
            // why: D-24623 — a gate over a hollow body (the gate parsed, the effect did not) must
            // not promise an effect. The line still names the failed condition; the tail says the
            // effect is unsupported instead of implying it would fire.
            const hollowBodyNote = hookHasHollowBody(hook) ? ' Its effect is not supported yet.' : '';
            if (failedCondition !== undefined && isWaitAndSeeCondition(failedCondition)) {
                recordDeferredConditionalGrant(G, playerID, cardId, G.heroAbilityHooks.indexOf(hook));
                const waitingOutcome = hollowBodyNote !== '' ? hollowBodyNote : ' It will apply if you reach it this turn.';
                pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} ability is waiting — ${reason}.${waitingOutcome}`, 'neutral', cardId);
                // why: WP-708 — the wait-and-see branch is NOT counted toward the Synergy
                // Rate: a numeric-threshold gate that may still be reached later this turn is
                // neither assembled nor a miss. Snapshot-gate synergy (hc / team / keyword /
                // playedThisTurn) is measured; deferred threshold synergy is a Phase-2 concern.
                continue;
            }
            // why: WP-708 / D-24531 — a hard-blocked conditional clause (the condition was
            // genuinely unmet) counts as PLAYED but not assembled.
            // why: WP-709 / D-24532 — its clause value still accrues to potentialValue (the
            // value it COULD have offered had the condition held). heroClauseValue reads the
            // count source from settled zones, which are independent of the failed boolean
            // condition, so the ceiling is well-defined on this whiff branch too.
            // why: WP-712 / D-24535 — the potential-floor is a FLOOR, not a replacement. When
            // the gate class equals the count source the count is 0 and heroClauseValue is 0,
            // so the floor lifts a count-0 whiff to the per-each magnitude; when the gate class
            // differs from the count source the count can be > 0 and heroClauseValue already
            // holds the true current-board value, which Math.max preserves (never lowered).
            if (isCountableConditionalClause) {
                recordConditionalClause(G, playerID, {
                    assembled: false,
                    clauseValue: Math.max(heroClauseValue(G, playerID, cardId, hook), heroClausePotentialFloor(hook)),
                });
            }
            // why: WP-702 live-verify follow-up — a card with MORE THAN ONE ability hook (e.g. Gambit's
            // Hypnotic Charm: an unconditional reveal-top clause PLUS an `[hc:instinct]`-gated each-other
            // clause) can have one clause fire while another is gated. The bare "ability did not activate"
            // then reads as if the WHOLE card fizzled — the exact confusion Jeff flagged from the live log,
            // where the own-deck reveal clearly resolved yet this line sat between its two entries. Name it
            // as ONE of the card's abilities when the card carries multiple hooks; keep the singular wording
            // for a single-ability card (where the whole card's ability really was the gated one).
            // why: WP-716 / D-24539 — a count-scaled line whose class/team gate IS its count source
            // ("[hc:ranged]: +1 for each other [hc:ranged] Hero") did not fail: it scaled by 0. "Did not
            // activate" read as broken in live play (Absorb Energies played first), so say +0 and name
            // what to play first. Scoped to that family: a flat-gated line, a gate that differs from the
            // count source, or a count that would grant more than 0 keeps the "did not activate" wording.
            // Text only — outcome stays `blocked` and the line still carries the card, so the Play
            // Diagnostics conditionNotMet read (WP-438) is unchanged.
            const zeroScaledClause = hollowBodyNote === ''
                ? describeZeroScaledCountClause(G, playerID, cardId, hook, failedCondition)
                : undefined;
            if (zeroScaledClause !== undefined) {
                const zeroScaledVerb = hooks.length > 1 ? 'scaled one of its abilities to' : 'scaled to';
                pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} ${zeroScaledVerb} ${zeroScaledClause}.`, 'blocked', cardId);
                continue;
            }
            const abilityPhrase = hooks.length > 1 ? 'did not activate one of its abilities' : 'ability did not activate';
            pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} ${abilityPhrase} — ${reason}.${hollowBodyNote}`, 'blocked', cardId);
            continue;
        }
        // why: WP-708 / D-24531 — the conditions passed, so a countable conditional clause
        // was ASSEMBLED (the player met the synergy condition). Recorded before
        // runHookEffects: "assembled" is the player's decision (the condition was met),
        // independent of whether the downstream handler then no-ops.
        // why: WP-709 / D-24532 — an assembled clause realizes its full value; clauseValue
        // accrues to BOTH realizedValue and potentialValue. Computed before runHookEffects
        // (like the Phase-1 count) — the grant only mutates turnEconomy, so this count-source
        // read equals the value the grant produces (== the WP-706 computedValue for a single
        // count-scaled effect: the AC-3 cross-check).
        if (isCountableConditionalClause) {
            recordConditionalClause(G, playerID, {
                assembled: true,
                clauseValue: heroClauseValue(G, playerID, cardId, hook),
            });
        }
        else if ((hook.conditions?.length ?? 0) === 0 && hookHasCountScaledValueEffect(hook) && hookHasDispatchableEffect(hook)) {
            // why: WP-712 / D-24535 — a PURE count-scaled clause (no boolean gate, e.g. Avengers
            // Assemble / Perfect Teamwork "for each color of Hero you have") carries synergy VALUE
            // but has no assembly decision. Record its realized value (= potential, since there is
            // no gate to miss) so Realized Value % reflects the marquee count-scaled synergies —
            // with countsTowardRate false, so Synergy Rate (played/assembled, WP-708) is unchanged.
            // Mutually exclusive with isCountableConditionalClause (conditions.length === 0 here).
            recordConditionalClause(G, playerID, {
                assembled: true,
                clauseValue: heroClauseValue(G, playerID, cardId, hook),
                countsTowardRate: false,
            });
        }
        // why: effects is optional on HeroAbilityHook. A hook may carry legacy `effects`,
        // composition `primitiveEffects`, or both — run whichever are present. (The former
        // early-`continue` on absent `effects` is gone because it would skip a Berserk hook,
        // which carries only primitiveEffects.)
        // why: WP-568 — same dispatch path the deferred re-check uses (runHookEffects).
        firedEffectCount += runHookEffects(G, ctx, playerID, cardId, hook, turn);
    }
    return firedEffectCount;
}
// ---------------------------------------------------------------------------
// Hollow-effect detection (WP-257 / D-24033 + D-24034)
// ---------------------------------------------------------------------------
/**
 * Reads the boardgame.io turn number for a HollowEffectRecord, defaulting to 0
 * when unavailable.
 *
 * executeHeroEffects is called with the move's FnContext WRAPPER — playCard's
 * `...context` rest (`{ ctx, random, events, ... }` minus G/playerID), NOT the
 * bare boardgame.io Ctx. The turn lives on the NESTED bgio ctx
 * (`moveContext.ctx.turn`); a former top-level `.turn` read was always undefined,
 * so every hero hollow record stamped turn 0 regardless of the real turn. (The
 * draw handler reads the wrapper's top-level `.random`, which is why the wrapper —
 * not the bare ctx — is what gets passed; the sibling villain executor receives
 * the bare Ctx and reads `ctx.turn` directly.) Test wrappers (makeMockCtx) and any
 * non-numeric value fall back to 0 — never a throw.
 *
 * @param moveContext - The boardgame.io FnContext wrapper, typed unknown to avoid
 *   a framework import.
 * @returns The turn number, or 0 when unavailable.
 */
function readTurnNumber(moveContext) {
    if (moveContext !== null && typeof moveContext === 'object') {
        const innerCtx = moveContext.ctx;
        if (innerCtx !== null && typeof innerCtx === 'object') {
            const turn = innerCtx.turn;
            if (typeof turn === 'number' && Number.isFinite(turn)) {
                return turn;
            }
        }
    }
    return 0;
}
/**
 * Classifies one declared hero effect by handler REACHABILITY (never by diffing
 * G). Returns the EffectExecutionReason the per-hook rule aggregates.
 *
 * Mirrors executeSingleEffect's gating, but answers "is a handler reachable for
 * this mechanic?" rather than mutating: a deferred-by-design mechanic is
 * `deferred`; any MVP keyword (a direct handler OR a reveal translation exists)
 * is reachable (`applied`); a recognized HeroKeyword with no handler is
 * `no-handler`; a token that is not even a recognized keyword is
 * `unsupported-keyword`. A play-time keyword whose descriptor fails the
 * magnitude pre-gate never reaches its handler, so it is `parse-unrecognized`
 * (D-24649).
 *
 * @param effect - The declared hero effect descriptor.
 * @returns The reachability classification reason.
 */
function classifyHeroEffectReason(effect) {
    const keyword = effect.type;
    // why: D-24033 — the explicit deferred allowlist is consulted BEFORE the
    // MVP/handler check. `wound`/`conditional` have no handler today (absent from
    // MVP_KEYWORDS), so without this they would classify `no-handler` → hollow even
    // though they are implemented-as-deferred by design.
    if (DEFERRED_BY_DESIGN_MECHANICS.has(keyword)) {
        return 'deferred';
    }
    // why: any MVP keyword has a reachable handler — either a direct
    // HERO_EFFECT_HANDLERS entry or a reveal translation (revealRulesForLegacyKeyword).
    // Reaching a handler is the not-hollow condition.
    if (MVP_KEYWORDS.has(keyword)) {
        // why: D-24649 (amends D-24033's "magnitude is a within-handler concern") — the
        // magnitude pre-gate runs BEFORE the handler, so a descriptor it drops never
        // reaches one. These are icons the parser read with no number: "use this bonus
        // [icon:attack] against the Mastermind", "gets no [icon:attack] from Shards".
        // The line's real mechanic is unparsed, so it is `parse-unrecognized`. Only a
        // keyword with a play-time handler can be dropped this way: the keywords that
        // run at recruit / discard / wound time / from the hand have no handler entry,
        // never pass through this gate, and stay `applied`.
        if (HERO_EFFECT_HANDLERS[effect.type] !== undefined && failsMagnitudePreGate(effect)) {
            return 'parse-unrecognized';
        }
        return 'applied';
    }
    // why: a recognized HeroKeyword with neither a handler nor a deferred entry is
    // `no-handler` (recognized-but-unimplemented). A token that is not even a valid
    // HeroKeyword (only reachable via a malformed hook / test cast) is
    // `unsupported-keyword` — dispatch cannot execute it.
    if (isValidHeroKeyword(keyword)) {
        return 'no-handler';
    }
    return 'unsupported-keyword';
}
/**
 * Returns whether a hero hook carries a count-scaled attack/recruit value effect
 * (`attack-per-count` / `recruit-per-count`) — WP-712 / D-24535.
 *
 * why: gates the no-condition value-recording branch to the pure count-scaled synergy
 * clauses (Avengers Assemble / Perfect Teamwork). Mirrors the exact type check
 * `buildCountScaledResolution` uses so the two cannot drift.
 *
 * @param hook - The hero ability hook.
 * @returns Whether the hook has a count-scaled attack/recruit effect.
 */
function hookHasCountScaledValueEffect(hook) {
    for (const effect of hook.effects ?? []) {
        if (effect.type === 'attack-per-count' || effect.type === 'recruit-per-count') {
            return true;
        }
    }
    return false;
}
/**
 * Returns the played-this-turn count source that counts exactly the cards a class or
 * team gate checks for, or undefined when no count source does (WP-716 / D-24539).
 *
 * why: the zero-scale log line may say "+0 — no other Ranged Hero played yet" only when
 * the gate and the count are the same thing. A gate on a different axis (Instinct gate,
 * extra-draws count) genuinely blocked the line, so it keeps "did not activate".
 *
 * @param condition - The class or team gate that failed.
 * @returns The matching count source, or undefined.
 */
function countSourceMatchingGate(condition) {
    if (condition.type === 'heroClassMatch') {
        switch (condition.value) {
            case 'strength':
                return 'strength-heroes-played-this-turn';
            case 'ranged':
                return 'ranged-heroes-played-this-turn';
            case 'tech':
                return 'tech-heroes-played-this-turn';
            case 'covert':
                return 'covert-heroes-played-this-turn';
            default:
                return undefined;
        }
    }
    if (condition.type === 'requiresTeam') {
        switch (condition.value) {
            case 'avengers':
                return 'avengers-played-this-turn';
            case 'shield':
                return 'shield-heroes-played-this-turn';
            case 'x-men':
                return 'x-men-played-this-turn';
            default:
                return undefined;
        }
    }
    return undefined;
}
/**
 * Returns the "+0" resource phrase for a count-scaled effect type, or undefined for any
 * other effect type (WP-716 / D-24539).
 *
 * @param effectType - The effect descriptor's type.
 * @returns e.g. `"+0 Attack"`, or undefined when the effect is not count-scaled.
 */
function zeroScaledResourcePhrase(effectType) {
    if (effectType === 'attack-per-count') {
        return '+0 Attack';
    }
    if (effectType === 'recruit-per-count') {
        return '+0 Recruit';
    }
    if (effectType === 'kidnap-per-count') {
        return '0 Bystander captures';
    }
    return undefined;
}
/**
 * Describes a gated count-scaled line that scaled to 0, or returns undefined when the
 * failed gate is not that case (WP-716 / D-24539).
 *
 * The line qualifies only when the hook's sole effect is count-scaled (attack / recruit /
 * kidnap-per-count), the failed gate is the class or team its count source counts, and
 * the count would grant 0 now. Read-only: re-resolves the count from G, never mutates.
 *
 * @param G - Game state (read only).
 * @param playerID - The acting player.
 * @param cardId - The played card (self-excluded from the count).
 * @param hook - The gated hook.
 * @param failedCondition - The condition that stopped the hook, if known.
 * @returns e.g. `"+0 Recruit — no other Ranged Hero played yet this turn"`, or undefined.
 */
function describeZeroScaledCountClause(G, playerID, cardId, hook, failedCondition) {
    if (failedCondition === undefined) {
        return undefined;
    }
    if ((hook.primitiveEffects?.length ?? 0) > 0) {
        return undefined;
    }
    const effects = hook.effects ?? [];
    if (effects.length !== 1) {
        return undefined;
    }
    const effect = effects[0];
    const resourcePhrase = zeroScaledResourcePhrase(effect.type);
    if (resourcePhrase === undefined || effect.countSource === undefined) {
        return undefined;
    }
    if (effect.countSource !== countSourceMatchingGate(failedCondition)) {
        return undefined;
    }
    // why: same grant arithmetic as the per-count handlers; "+0" is said only when it is true.
    const count = resolveCountSource(G, playerID, effect.countSource, cardId);
    const perEach = effect.perEach && effect.perEach > 0 ? effect.perEach : 1;
    const magnitude = typeof effect.magnitude === 'number' ? effect.magnitude : 0;
    if (magnitude * Math.floor(count / perEach) !== 0) {
        return undefined;
    }
    const gateName = failedCondition.type === 'requiresTeam'
        ? teamDisplayName(failedCondition.value)
        : heroClassDisplayName(failedCondition.value);
    return `${resourcePhrase} — no other ${gateName} Hero played yet this turn`;
}
/**
 * Returns whether a hero hook carries at least one executable (non-hollow) effect
 * body (WP-708 / D-24531).
 *
 * Mirrors `detectHollowHeroHook`'s reachability rule (D-24033): a composition
 * primitive always reaches the interpreter (reachable), and a legacy effect is
 * reachable when `classifyHeroEffectReason` is not a hollow reason. The synergy
 * counter uses this to exclude a conditional hook whose effect body is
 * unimplemented — an unbuilt payoff is our backlog, never the player's synergy
 * miss, so it must not enter the per-match Synergy Rate. Since D-24649 a
 * magnitude-dropped effect is hollow, so a hook whose only effects are
 * magnitude-less icons is not executable either.
 *
 * @param hook - The hero ability hook.
 * @returns Whether the hook has a reachable effect body.
 */
// why: D-24649 — exported (engine-internal, not re-exported from index.ts) so the
// parity tests and WP-776's hand-readiness helper reuse it instead of copying it.
export function hookHasExecutableEffect(hook) {
    if ((hook.primitiveEffects?.length ?? 0) > 0) {
        return true;
    }
    for (const effect of hook.effects ?? []) {
        if (!isHollowReason(classifyHeroEffectReason(effect))) {
            return true;
        }
    }
    return false;
}
/**
 * Returns whether playing a hero hook dispatches at least one effect to a handler
 * right now (D-24649; the predicate WP-776 locks).
 *
 * Mirrors all three steps of executeSingleEffect's gate: the keyword is in
 * MVP_KEYWORDS, the magnitude passes the pre-gate, and a play-time handler exists.
 * A composition primitive always reaches the interpreter. Stricter than
 * hookHasExecutableEffect: a keyword that runs at another time (wall-crawl at
 * recruit, dodge from the hand, return-on-discard at discard) is executable but
 * dispatches nothing at play.
 *
 * @param hook - The hero ability hook.
 * @returns Whether playing the hook reaches at least one handler.
 */
// why: exported (engine-internal, not re-exported from index.ts) for the Synergy Rate
// gate below, the executeSingleEffect parity test, and WP-776.
export function hookHasDispatchableEffect(hook) {
    if ((hook.primitiveEffects?.length ?? 0) > 0) {
        return true;
    }
    for (const effect of hook.effects ?? []) {
        if (!MVP_KEYWORDS.has(effect.type)) {
            continue;
        }
        if (failsMagnitudePreGate(effect)) {
            continue;
        }
        if (HERO_EFFECT_HANDLERS[effect.type] !== undefined) {
            return true;
        }
    }
    return false;
}
/**
 * Reports whether a hook declares an effect body that cannot execute: at least one legacy
 * effect or unresolved marker, and no executable effect (D-24623). A gated line whose body
 * parsed to nothing records an unresolved marker, so it counts; an empty hook does not.
 *
 * @param hook - The hero ability hook.
 * @returns True when the hook's declared body is entirely hollow.
 */
function hookHasHollowBody(hook) {
    const declaredCount = (hook.effects?.length ?? 0) + (hook.unresolvedMarkers?.length ?? 0);
    // why: D-24649 — this stays on hookHasExecutableEffect, not hookHasDispatchableEffect.
    // "Its effect is not supported yet" must agree with what detectHollowHeroHook records when
    // the same gate passes, and both read classifyHeroEffectReason. Since D-24649 a
    // magnitude-less-only body is hollow there too, so Storm Tidal Wave's failed gate now says
    // "not supported yet". The dispatchable predicate would also flag a gated keyword that runs
    // at another time (Goblin Glider's Dodge grant), which D-24033 classifies `applied`, not
    // hollow, so the failed-gate line would call unsupported a body the hollow record never does.
    return declaredCount > 0 && !hookHasExecutableEffect(hook);
}
/**
 * Counts a hook's conditions that are player-built synergy gates — every condition except the
 * Sunlight / Moonlight board-state gates (WP-765 / D-24598).
 *
 * // why: day/night is decided by the HQ, not by what the player assembled, so a
 * day/night-only hook is not a Synergy Rate clause (the Synergy exclusion in D-24598).
 *
 * @param hook - The hero ability hook.
 * @returns How many of its conditions count toward the Synergy Rate.
 */
function countSynergyConditions(hook) {
    let count = 0;
    for (const condition of hook.conditions ?? []) {
        if (condition.type !== 'sunlightInEffect' && condition.type !== 'moonlightInEffect') {
            count += 1;
        }
    }
    return count;
}
/**
 * Returns whether a string is a valid HeroKeyword.
 *
 * Local copy of the setup-parser guard (duplicate-first per §16.1; a third
 * appearance would justify extracting it). Used to split `no-handler`
 * (recognized keyword) from `unsupported-keyword` (unrecognized token).
 *
 * @param value - The candidate keyword string.
 * @returns Whether the value is a member of HERO_KEYWORDS.
 */
function isValidHeroKeyword(value) {
    for (const keyword of HERO_KEYWORDS) {
        if (keyword === value) {
            return true;
        }
    }
    return false;
}
/**
 * Records a HollowEffectRecord for a hero hook iff the whole hook is hollow.
 *
 * Per-hook rule (D-24033): a hook flags hollow when (1) it declared ≥1 effect
 * (a legacy effect, a composition primitive, or an unresolved marker), AND
 * (2) NO declared effect reached a handler (no `applied`/`handler-noop`/
 * `condition-failed`/`deferred` outcome), AND (3) ≥1 declared effect resolved to
 * a hollow reason. A mixed hook with even one reachable effect never flags.
 *
 * Conditions are evaluated by the caller; this runs only for a conditions-passed
 * hook, so a failed-condition hook is already excluded (it `continue`d, a
 * `condition-failed` reachable outcome). primitiveEffects always reach the
 * interpreter (a recognized composition), so they count as reachable.
 *
 * @param G - Game state (mutated under Immer draft only via recordHollowEffect).
 * @param ctx - The boardgame.io context (read for the turn number only).
 * @param cardId - The played hero card's CardExtId.
 * @param hook - The hero ability hook that just ran.
 */
function detectHollowHeroHook(G, ctx, cardId, hook) {
    const effects = hook.effects ?? [];
    const primitiveEffects = hook.primitiveEffects ?? [];
    const unresolvedMarkers = hook.unresolvedMarkers ?? [];
    // why: "declared ≥1 effect" — a hook with no legacy effect, no composition, and
    // no unresolved marker declares nothing executable (e.g. a keyword-only or
    // empty hook), so it can never be hollow.
    if (effects.length === 0 && primitiveEffects.length === 0 && unresolvedMarkers.length === 0) {
        return;
    }
    // why: a composition primitive always reaches the interpreter (a recognized
    // open-mechanic handler), so its presence makes the hook reachable — it is
    // never hollow. Short-circuit before classifying the legacy effects.
    if (primitiveEffects.length > 0) {
        return;
    }
    let hasReachable = false;
    let firstHollow = null;
    for (const effect of effects) {
        const reason = classifyHeroEffectReason(effect);
        if (!isHollowReason(reason)) {
            hasReachable = true;
        }
        else if (firstHollow === null) {
            // why: D-24649 — a hollow MVP keyword can only be a magnitude-dropped icon (see
            // classifyHeroEffectReason). Label it `attack-no-magnitude`, not `attack`, so the
            // log line and the /coverage in-play list do not read as the implemented grant.
            const mechanic = MVP_KEYWORDS.has(effect.type) ? `${effect.type}-no-magnitude` : effect.type;
            firstHollow = { reason, mechanic };
        }
    }
    // why: each unresolved marker is a `parse-unrecognized` hollow reason (the
    // parser saw a marker token and resolved it to nothing). Flavor text leaves
    // unresolvedMarkers empty, so it never reaches here.
    for (const marker of unresolvedMarkers) {
        if (firstHollow === null) {
            firstHollow = { reason: 'parse-unrecognized', mechanic: marker };
        }
    }
    // why: per-hook rule — flag ONLY when no declared effect was reachable AND ≥1
    // was hollow. A mixed hook (≥1 reachable) is not hollow even if another effect
    // is unhandled.
    if (hasReachable || firstHollow === null) {
        return;
    }
    // why: the reason field is one of the three hollow reasons (isHollowReason
    // gated firstHollow). The cast narrows EffectExecutionReason to the
    // HollowEffectRecord.reason subset for the record contract.
    recordHollowEffect(G, {
        cardId,
        cardType: 'hero',
        timing: hook.timing,
        mechanic: firstHollow.mechanic,
        reason: firstHollow.reason,
        turn: readTurnNumber(ctx),
    });
}
// ---------------------------------------------------------------------------
// Effect-trace emission (WP-488 / D-24294)
// ---------------------------------------------------------------------------
/**
 * Builds the per-dispatch `EffectTrace` for one legacy hero effect
 * (`hook.effects` → `executeSingleEffect`, WP-488 / D-24294).
 *
 * `fired` (executeSingleEffect's boolean) maps to `fired`; a false return (unsupported
 * keyword, failed magnitude pre-gate, or an undefined handler) maps to `no-handler`.
 * The `handler` label is the keyword token (the HERO_EFFECT_HANDLERS map key) when a
 * handler ran, `""` when none — a STRING label, never the function. `effect` is the
 * keyword token verbatim.
 *
 * @param cardId - The played hero card's CardExtId.
 * @param timing - The hook timing label.
 * @param effect - The dispatched hero effect descriptor.
 * @param fired - Whether executeSingleEffect reached a handler and ran.
 * @param turn - The turn number for the trace.
 * @param resolution - The count-scaled resolution record (WP-706 / D-24528), or
 *   undefined for a non-count-scaled effect. Computed by the caller (which holds
 *   `G`/`playerID`) and passed in so this stays a PURE record assembler — no resolve /
 *   explain fold. Assigned conditionally: undefined omits the key entirely.
 * @returns The effect trace to record.
 */
function buildHeroLegacyEffectTrace(cardId, timing, effect, fired, turn, resolution) {
    // why: effect.type is typed HeroKeyword but read defensively so a malformed hook /
    // test cast cannot throw before the guarded writer runs.
    const keywordValue = effect.type;
    const effectToken = typeof keywordValue === 'string' ? keywordValue : '';
    const status = heroLegacyTraceStatus(effect, fired);
    const trace = {
        cardId,
        scope: 'hero',
        timing,
        effect: effectToken,
        // why: label the mechanic for a reachable dispatch (fired OR reachable no-op);
        // only a genuine no-handler leaves the label empty.
        handler: status === 'no-handler' ? '' : effectToken,
        status,
        fireSite: 'hero-executor',
        params: buildHeroEffectTraceParams(effect),
        turn,
    };
    // why: WP-706 / D-24528 — conditional assignment so a non-count-scaled effect
    // (resolution === undefined) OMITS the key under exactOptionalPropertyTypes, matching
    // the omit-when-absent posture of the rest of the trace channel.
    if (resolution !== undefined) {
        trace.resolution = resolution;
    }
    return trace;
}
/**
 * Builds the count-scaled `resolution` sub-record for a legacy hero trace, or
 * undefined when the effect is not count-scaled (WP-706 / D-24528).
 *
 * why: the `attack-per-count` / `recruit-per-count` executors are `void` and never
 * surface the computed count/perEach/magnitude/grant, so the resolution is RE-RESOLVED
 * here from the settled `G` at trace-build time. Deterministic: the grant added to
 * `turnEconomy` without moving any `inPlay` card in the same caller-loop iteration, so
 * `resolveCountSource` re-reads the identical count the grant used, and `computedValue`
 * (magnitude × floor(count / perEach), same absent/≤0 → 1 perEach normalization the
 * executor applies) equals the granted value. `explainCountSourceInputs` is the separate
 * read-only counted-inputs pass; `countedInputs` is omitted when it returns `[]` (the
 * victory-pile sources). Guarded — diagnostics is best-effort, so any unexpected shape
 * returns undefined rather than throwing and never breaks the play path.
 *
 * @param G - Game state (read-only).
 * @param playerID - The acting player.
 * @param cardId - The played card that owns the effect (the trigger, for self-exclusion).
 * @param effect - The dispatched hero effect descriptor.
 * @returns The resolution record, or undefined for a non-count-scaled effect.
 */
function buildCountScaledResolution(G, playerID, cardId, effect) {
    // why: only the two count-scaled keywords carry a resolution; every other effect omits it.
    let resource;
    if (effect.type === 'attack-per-count') {
        resource = 'attack';
    }
    else if (effect.type === 'recruit-per-count') {
        resource = 'recruit';
    }
    else {
        resource = undefined;
    }
    if (resource === undefined) {
        return undefined;
    }
    // why: mirrors the executor's own no-source skip — nothing to scale by, no resolution.
    if (effect.countSource === undefined) {
        return undefined;
    }
    try {
        const countSource = effect.countSource;
        const magnitude = typeof effect.magnitude === 'number' ? effect.magnitude : 0;
        // why: same absent/≤0 → 1 normalization the executor applies, so computedValue equals
        // the granted value and never divides by 0/undefined.
        const perEach = effect.perEach && effect.perEach > 0 ? effect.perEach : 1;
        // why: re-resolve from the settled G — byte-identical to the integer the grant used.
        const count = resolveCountSource(G, playerID, countSource, cardId);
        const computedValue = magnitude * Math.floor(count / perEach);
        const countedInputs = explainCountSourceInputs(G, playerID, countSource, cardId);
        const resolution = {
            countSource,
            resource,
            magnitude,
            count,
            perEach,
            computedValue,
        };
        // why: omit countedInputs when the explain returned [] (the victory-pile sources), so
        // the key is absent rather than an empty array (the countedInputs invariant).
        if (countedInputs.length > 0) {
            resolution.countedInputs = countedInputs;
        }
        return resolution;
    }
    catch {
        // why: diagnostics is best-effort (mirrors the guarded trace push) — a malformed hook
        // or missing zone must never throw out of the play path; drop the resolution instead.
        return undefined;
    }
}
/**
 * Decides the trace status for one legacy hero dispatch (WP-488 / D-24294).
 *
 * A `false` return from `executeSingleEffect` is NOT necessarily a missing handler:
 * a keyword whose handler is reachable — a direct `HERO_EFFECT_HANDLERS` entry, a
 * reveal translation, or an execute-at-another-site MVP keyword (`return-on-discard`
 * at the discard chokepoint, `wall-crawl` at recruit, `dodge`/`undercover` at their
 * moves, `size-changing` at class-read) — that simply did not mutate at THIS dispatch
 * (a magnitude pre-gate, or the real work fires at its own site) is a reachable
 * **no-op**, not a hollow. Only a recognized-but-unimplemented or an unsupported
 * keyword is a genuine `no-handler`.
 *
 * Reuses `classifyHeroEffectReason` — the SAME classifier the hollow detector uses —
 * so the trace status can no longer disagree with the hollow record (the disagreement
 * that made a real match's Unending Energy / Surge of Power read as phantom hollows).
 * Mirrors the villain executor's `DELIBERATE_NO_OP_VILLAIN_PRIMITIVES` treatment,
 * which likewise reads `no-op`, never `no-handler`.
 *
 * @param effect - The dispatched hero effect descriptor.
 * @param fired - Whether executeSingleEffect reached a handler and ran.
 * @returns The trace status.
 */
function heroLegacyTraceStatus(effect, fired) {
    if (fired) {
        return 'fired';
    }
    const reason = classifyHeroEffectReason(effect);
    if (reason === 'applied' || reason === 'deferred') {
        return 'no-op';
    }
    return 'no-handler';
}
/**
 * Builds the per-dispatch `EffectTrace` for one primitive-composition hero effect
 * (`hook.primitiveEffects` → `interpretHeroPrimitiveEffect`, WP-488 / D-24294).
 *
 * `fired` (the interpreter's boolean — false only for an unknown top-level node) maps
 * to `fired`/`no-handler`. The `handler` label and `effect` token are the composition's
 * top-level node `type` (the EFFECT_NODE_HANDLERS map key, e.g. `sequence`), read
 * defensively. `params` stays `{}` — a composition is a nested AST with no flat scalar
 * descriptor fields to copy, and spreading the tree would leak non-scalar nodes into G.
 *
 * @param cardId - The played hero card's CardExtId.
 * @param timing - The hook timing label.
 * @param node - The top-level composition effect node.
 * @param fired - Whether the interpreter dispatched to a real handler.
 * @param turn - The turn number for the trace.
 * @returns The effect trace to record.
 */
function buildHeroPrimitiveEffectTrace(cardId, timing, node, fired, turn) {
    const nodeType = node.type;
    const effectToken = typeof nodeType === 'string' ? nodeType : '';
    return {
        cardId,
        scope: 'hero',
        timing,
        effect: effectToken,
        handler: fired ? effectToken : '',
        status: fired ? 'fired' : 'no-handler',
        fireSite: 'hero-primitive',
        params: {},
        turn,
    };
}
/**
 * Copies a hero descriptor's own SCALAR parameter fields into a trace `params`
 * snapshot, omitting `undefined` keys (WP-488 / D-24294).
 *
 * Explicit field-by-field copy — NEVER a spread-and-cast (which would leak the
 * `type` keyword and the non-scalar `empoweredClasses` / `revealRules` arrays and
 * break `exactOptionalPropertyTypes`). Every copied value is `string | number |
 * boolean`; the `type` token is carried as the trace's `effect`, not here.
 *
 * @param effect - The dispatched hero effect descriptor.
 * @returns A shallow scalar snapshot of the descriptor's parameter fields.
 */
function buildHeroEffectTraceParams(effect) {
    const params = {};
    if (effect.magnitude !== undefined) {
        params.magnitude = effect.magnitude;
    }
    if (effect.countSource !== undefined) {
        params.countSource = effect.countSource;
    }
    if (effect.rewardType !== undefined) {
        params.rewardType = effect.rewardType;
    }
    if (effect.empoweredClass !== undefined) {
        params.empoweredClass = effect.empoweredClass;
    }
    if (effect.revealCount !== undefined) {
        params.revealCount = effect.revealCount;
    }
    if (effect.reorderRemainder !== undefined) {
        params.reorderRemainder = effect.reorderRemainder;
    }
    // why: empoweredClasses (string[]) and revealRules (RevealRule[]) are non-scalar —
    // deliberately omitted; params carries only string | number | boolean per D-24294.
    return params;
}
function heroEffectDraw(G, ctx, playerID, cardId, effect) {
    // why: WP-731 / D-24552 — Venompool's Shenanigans locks further draws for the
    // rest of this turn. When the flag is set, a hero `draw`-keyword effect draws
    // NOTHING and logs `blocked`. This guards ONLY the hero-effect draw path; the
    // end-of-turn hand refill / setup deal / other-seat draws use
    // `drawCardsIntoHand` directly, so the lock lifts at turn end as printed.
    const requestedCount = effect.magnitude;
    if (G.turnEconomy.drawsLocked === true) {
        pushLog(G, `Player ${playerID} can't draw ${requestedCount} card(s) from ${formatCardRef(G.cardDisplayData, cardId)} — no more draws this turn.`, 'blocked', cardId);
        return;
    }
    // why: ctx is narrowed to ShuffleProvider here because deck reshuffle
    // needs ctx.random.Shuffle. boardgame.io ctx satisfies ShuffleProvider
    // structurally — this is the established pattern from WP-005B/008B.
    const drawnCount = drawFromPlayerDeck(G, playerID, requestedCount, ctx);
    // why: WP-665 / D-24476 — count the REALIZED effect-draw toward the current
    // player's per-turn draw total, which the `cardsDrawnThisTurnAtLeast` gate reads
    // ("if you drew two cards this turn", Gamma-Draining Nanites). heroEffectDraw is
    // the `draw:N` keyword path and always runs for the current player. D-24732 adds the
    // other effect-draw sites (see TurnEconomy.cardsDrawn); the end-of-turn hand fill
    // (drawCardsIntoHand) is deliberately NOT counted (else the gate is trivially met).
    G.turnEconomy.cardsDrawn += drawnCount;
    // why: WP-417 / D-24237 — the draw handler was silent, so "Draw a card." on the
    // play line was the ONLY evidence the ability existed and a short draw (deck and
    // discard both empty) was indistinguishable from a full one. Name the realized
    // amount, and say so explicitly when it fell short of the printed amount.
    if (drawnCount < requestedCount) {
        // why: WP-434 — a short draw (fewer than requested; deck + discard empty) is
        // `partial` — the ability fired but the source ran dry mid-way.
        pushLog(G, `Player ${playerID} drew ${drawnCount} of ${requestedCount} card(s) from ${formatCardRef(G.cardDisplayData, cardId)} — their deck and discard pile were empty.`, 'partial', cardId);
        return;
    }
    // why: WP-434 — a full realized draw is `applied` (green).
    pushLog(G, `Player ${playerID} drew ${drawnCount} card(s) from ${formatCardRef(G.cardDisplayData, cardId)}.`, 'applied', cardId);
}
function heroEffectAttack(G, _ctx, playerID, cardId, effect) {
    const attackGrant = effect.magnitude;
    if (effect.attackRestriction !== undefined) {
        heroEffectRestrictedAttack(G, playerID, cardId, attackGrant, effect.attackRestriction);
        return;
    }
    G.turnEconomy = addResources(G.turnEconomy, attackGrant, 0);
    // why: WP-417 / D-24237 — an ability-granted attack was silent; only the
    // count-scaled variant (attack-per-count) logged. Both now report the grant.
    // why: WP-434 — an ability-granted attack economy is `applied` (green).
    pushLog(G, `Player ${playerID} gained +${attackGrant} attack from ${formatCardRef(G.cardDisplayData, cardId)}.`, 'applied', cardId);
}
/**
 * Grants "usable only against …" attack (WP-790 / D-24652).
 *
 * The targets are the parsed restriction's targets, plus `'mastermind'` when a
 * fused Ranged widen (`widenToMastermindWhen`) holds at play. The grant goes
 * through `addRestrictedAttack`, so it still counts toward the turn's attack.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - The player who played the card.
 * @param cardId - The played hero card.
 * @param attackGrant - The printed attack magnitude.
 * @param attackRestriction - The parsed restriction.
 */
function heroEffectRestrictedAttack(G, playerID, cardId, attackGrant, attackRestriction) {
    const targets = [...attackRestriction.targets];
    // why: WP-790 / D-24652 — Storm's / Electro's Ranged "… against the Mastermind instead"
    // line was fused onto this grant at setup; its conditions are evaluated HERE, at grant time,
    // so the bucket's eligibility is fixed when the card is played (as the printed text reads).
    if (attackRestriction.widenToMastermindWhen !== undefined &&
        !targets.includes('mastermind') &&
        evaluateAllConditions(G, playerID, attackRestriction.widenToMastermindWhen, cardId)) {
        targets.push('mastermind');
    }
    G.turnEconomy = addRestrictedAttack(G.turnEconomy, attackGrant, targets, cardId);
    pushLog(G, `Player ${playerID} gained +${attackGrant} attack (only against: ${formatAttackTargets(targets)}) from ${formatCardRef(G.cardDisplayData, cardId)}.`, 'applied', cardId);
}
function heroEffectRecruit(G, _ctx, playerID, cardId, effect) {
    const recruitGrant = effect.magnitude;
    G.turnEconomy = addResources(G.turnEconomy, 0, recruitGrant);
    // why: WP-417 / D-24237 — mirrors the attack grant above; an ability-granted
    // recruit was previously invisible in the game log.
    // why: WP-434 — an ability-granted recruit economy is `applied` (green).
    pushLog(G, `Player ${playerID} gained +${recruitGrant} recruit from ${formatCardRef(G.cardDisplayData, cardId)}.`, 'applied', cardId);
}
function heroEffectKo(G, _ctx, playerID, cardId, _effect) {
    // why: MVP KO targets the played card itself. This models "KO this
    // card" text found on some heroes. No player choice — target selection
    // is deferred to future WPs. The card must be removed from inPlay
    // before being added to the KO pile.
    const playerZones = G.playerZones[playerID];
    if (playerZones) {
        const moveResult = moveCardFromZone(playerZones.inPlay, [], cardId);
        if (moveResult.found) {
            playerZones.inPlay = moveResult.from;
            G.ko = koCard(G.ko, cardId);
            recordOwnedCardKoed(G, playerID, cardId, 'inPlay');
            // why: WP-417 / D-24237 — a self-KO removes the card the player just played
            // from the board; a silent removal reads as the card vanishing. Name it.
            // why: WP-434 — a self-KO ability that removed the played card is `applied`.
            pushLog(G, `Player ${playerID} KO'd ${formatCardRef(G.cardDisplayData, cardId)} via its own ability.`, 'applied', cardId);
        }
    }
}
function heroEffectRescue(G, _ctx, playerID, _cardId, effect) {
    const rescueMagnitude = effect.magnitude ?? 1;
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    if (G.piles.bystanders.length === 0) {
        // why: D-24017 — an empty Bystander supply is a legitimate no-op, but a
        // silent skip reads as "the hero card did nothing" (player confusion,
        // per the live diagnostic). Log it so the reason is observable in the
        // game log (UIState.log), mirroring how fight rescues are logged.
        pushLog(G, `Player ${playerID} could not rescue a Bystander via a hero ability — the Bystander supply is empty.`);
        return;
    }
    const rescueCount = Math.min(rescueMagnitude, G.piles.bystanders.length);
    let rescuedCount = 0;
    for (let rescued = 0; rescued < rescueCount; rescued++) {
        // why: top-of-pile convention — pile[0] is the first available bystander (D-21501)
        const topBystander = G.piles.bystanders[0];
        if (!topBystander) {
            break;
        }
        const moveResult = moveCardFromZone(G.piles.bystanders, playerZones.victory, topBystander);
        G.piles.bystanders = moveResult.from;
        playerZones.victory = moveResult.to;
        rescuedCount++;
    }
    // why: D-24017 — surface the hero-ability rescue in the game log the same
    // way fight rescues are (fightVillain/fightMastermind), so a successful
    // rescue is observable to the player rather than a silent zone move.
    pushLog(G, `Player ${playerID} rescued ${rescuedCount} bystander(s) via a hero ability.`);
}
/**
 * Hero handler for the `gain-wound-self` / `gain-wound-each` keywords (D-24156).
 *
 * The active player (`gain-wound-self`) or every player (`gain-wound-each`)
 * gains one Wound from the shared supply into their discard pile. Reuses the
 * WP-017 `gainWound` helper and mirrors the WP-316 villain per-target loop
 * (`villainEffectGainWound`): a missing zone or empty supply is a legitimate
 * no-op per target, and the active player's `woundsDrawn` UI economy bumps when
 * they gain. No targeting, no magnitude, no randomness — the Wound is drawn
 * top-of-pile deterministically.
 */
function heroEffectGainWound(G, _ctx, playerID, cardId, effect) {
    // why: rules v23 L2247 — `each` starts with the current player, then clockwise
    // (D-24780, amends D-24156's sorted order), so who misses the last Wound follows the
    // printed rule; a pure function of the seat ids, so it replays identically. `self`
    // targets the active player only.
    const targetPlayerIds = effect.type === 'gain-wound-each'
        ? seatsInTurnOrderFrom(Object.keys(G.playerZones), playerID)
        : [playerID];
    let anyWoundGained = false;
    const gainedPlayerIds = [];
    const divingBlockPendingPlayerIds = [];
    for (const targetPlayerId of targetPlayerIds) {
        const targetZones = G.playerZones[targetPlayerId];
        if (!targetZones) {
            continue;
        }
        if (G.piles.wounds.length === 0) {
            // why: D-24017 — an empty Wound supply is a legitimate no-op, but a silent
            // skip reads as "the card did nothing"; log it so the reason is observable
            // (mirrors the heroEffectRescue empty-supply logging).
            pushLog(G, `Player ${targetPlayerId} could not gain a Wound — the Wound supply is empty.`);
            continue;
        }
        // why: WP-682 / D-24499 — gainWoundForPlayer chokepoint so a hero self-wound
        // (gain-wound-self / gain-wound-each) is also seen by Diving Block; for
        // gain-wound-each a NON-active recipient reaches the reveal via the WP-684 wave.
        if (gainWoundWithOutcome(G, targetPlayerId) === 'diving-block-pending') {
            divingBlockPendingPlayerIds.push(targetPlayerId);
        }
        else {
            gainedPlayerIds.push(targetPlayerId);
        }
        anyWoundGained = true;
        if (targetPlayerId === playerID) {
            // why: woundsDrawn projects the active player's wounds only (UI economy),
            // matching the villain gain-wound path (villainEffectGainWound).
            G.turnEconomy.woundsDrawn += 1;
        }
    }
    // why: D-24017 — surface the wound gain in the game log so a printed penalty
    // that actually landed is observable, not a silent zone move.
    // why: D-24673 — while a Diving Block reveal is pending the Wound is not gained yet;
    // the reveal / decline line that follows gives the outcome.
    if (divingBlockPendingPlayerIds.length > 0) {
        pushLog(G, effect.type === 'gain-wound-each'
            ? `Each player gains a Wound (${formatCardRef(G.cardDisplayData, cardId)}): ${composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds)}.`
            : `Player ${playerID} would gain a Wound — Diving Block can prevent it (${formatCardRef(G.cardDisplayData, cardId)}).`);
    }
    else if (anyWoundGained) {
        pushLog(G, effect.type === 'gain-wound-each'
            ? `Each player gained a Wound (${formatCardRef(G.cardDisplayData, cardId)}).`
            : `Player ${playerID} gained a Wound (${formatCardRef(G.cardDisplayData, cardId)}).`);
    }
}
/**
 * Hero handler for the `discard-artifact-or-three-then-ko` keyword (WP-860 / D-24796).
 *
 * Commune with the Spirit World: "You may discard an [keyword:Artifact] you control or three cards
 * from your hand. If you do, KO a card from your hand or discard pile." Parks the 'commune-cost'
 * seat choice for the playing player (or logs why nothing opens); the rest chains from
 * resolveSeatChoice. Never throws.
 *
 * @param G - The game state, mutated in place.
 * @param _ctx - Unused move context.
 * @param playerID - The player who played the card.
 * @param cardId - The Commune card played.
 * @param _effect - The `{ type: 'discard-artifact-or-three-then-ko' }` descriptor (no magnitude).
 */
function heroEffectDiscardArtifactOrThreeThenKo(G, _ctx, playerID, cardId, _effect) {
    // why: WP-860 / D-24796 — a hero handler parks a seat choice at play (the Here, Hold This / Random
    // Acts precedent); the cost / discard / KO logic lives in moves/communeSpiritWorld.logic.ts.
    parkCommuneCostChoice(G, playerID, cardId);
}
/**
 * Hero handler for the `gain-sidekick` keyword (WP-844 / D-24778).
 *
 * "Gain a [rule:Sidekick]" / "Gain three [rule:Sidekicks]": moves up to `magnitude`
 * Sidekicks, top of `G.piles.sidekicks` first, into the playing player's discard pile
 * through `gainSidekickToDiscard`, stopping at an empty Stack. Writes one log line —
 * `applied` (all gained), `partial` (some) or `blocked` (none). No randomness, no
 * targeting, never throws.
 */
function heroEffectGainSidekick(G, _ctx, playerID, cardId, effect) {
    // why: D-24778 — the magnitude pre-gate admits 0 (isValidMagnitude), but the marker
    // pattern never emits one; a request below 1 is a defensive silent no-op.
    const requestedCount = effect.magnitude ?? 0;
    if (requestedCount < 1) {
        return;
    }
    // why: D-24778 — rules v23 L1170: "Gain" means "put it into your discard pile", and the
    // top of the Stack (pile[0]) goes first, so the gain is deterministic. Rules v23
    // L3216-3218: a card-effect gain does not count against the once-per-turn Sidekick
    // recruit, so this never reads or writes hasRecruitedSidekickThisTurn (D-24776) and
    // spends no Recruit. It also never sets hasActedThisTurn: that flag means "recruited or
    // fought this turn" and bars Healing (D-24179 / D-24180), and a gain is neither.
    let gainedCount = 0;
    for (let i = 0; i < requestedCount; i++) {
        if (!gainSidekickToDiscard(G, playerID)) {
            break;
        }
        gainedCount += 1;
    }
    // why: D-24017 — an empty or short Stack is a legitimate no-op, but a silent skip reads
    // as "the card did nothing"; the partial / blocked lines make the shortfall observable.
    const card = formatCardRef(G.cardDisplayData, cardId);
    if (gainedCount === requestedCount) {
        pushLog(G, `Player ${playerID} gained ${String(gainedCount)} Sidekick(s) from the Sidekick Stack (${card}).`, 'applied', cardId);
    }
    else if (gainedCount > 0) {
        pushLog(G, `Player ${playerID} gained ${String(gainedCount)} of ${String(requestedCount)} Sidekick(s) — the Sidekick Stack ran out (${card}).`, 'partial', cardId);
    }
    else {
        pushLog(G, `Player ${playerID} could not gain a Sidekick — the Sidekick Stack is empty (${card}).`, 'blocked', cardId);
    }
}
/**
 * Hero handler for the `discard-top-printed-attack` keyword (WP-850 / D-24786).
 *
 * Bishop's Firepower from the Future: "Discard the top four cards of your deck. You get
 * +[icon:attack] equal to those cards' printed [icon:attack]." Discards `magnitude` cards one
 * at a time from the playing player's deck top to their discard pile, sums each card's
 * printed Attack (`offPlayCardStats`: the trailing `+` dropped, Divided halves added — a Wound
 * or Bystander adds 0), grants the total, and writes `G.lastDeckTopDiscard` so the same
 * card's `ko-discarded-this-way` line knows which cards "those cards" are.
 *
 * // why (ctx): executeHeroEffects passes the move-context wrapper, so the empty-deck
 * reshuffle reads ctx.random through it exactly as heroEffectStealAbilities does.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context (threaded into the reshuffle).
 * @param playerID - The player who played the card.
 * @param cardId - The played card instance.
 * @param effect - The `{ type: 'discard-top-printed-attack', magnitude: N }` descriptor.
 */
function heroEffectDiscardTopPrintedAttack(G, ctx, playerID, cardId, effect) {
    // why: D-24786 — the marker pattern never emits a count below 1; a request below 1 is a
    // defensive silent no-op.
    const requestedCount = effect.magnitude ?? 0;
    if (requestedCount < 1) {
        return;
    }
    const zones = G.playerZones[playerID];
    if (!zones) {
        return;
    }
    const discardedCardIds = [];
    let totalAttack = 0;
    for (let i = 0; i < requestedCount; i++) {
        if (zones.deck.length === 0) {
            // why: D-24285 — rules v23: when a card effect needs the top of an empty deck, shuffle
            // the discard pile into a new deck first, mid-effect. That pile includes cards this play
            // already discarded, so one of them can come back and be discarded (and counted) again.
            reshuffleDiscardIntoDeck(zones, ctx);
        }
        const topCardId = zones.deck[0];
        if (topCardId === undefined) {
            // why: D-24786 — deck and discard are both empty. `break`, never `return`: the grant,
            // the log line and the record below must run on every path, so line 2 never reads a
            // record an earlier play of this same card instance left behind.
            break;
        }
        const moveResult = moveCardFromZone(zones.deck, zones.discard, topCardId);
        zones.deck = moveResult.from;
        zones.discard = moveResult.to;
        discardedCardIds.push(topCardId);
        totalAttack += offPlayCardStats(G, topCardId)?.attack ?? 0;
    }
    if (G.turnEconomy && totalAttack > 0) {
        G.turnEconomy = addResources(G.turnEconomy, totalAttack, 0);
    }
    // why: D-24017 — a short or empty deck is a legitimate partial / no-op, but a silent
    // shortfall reads as "the card did nothing"; each outcome gets its own line.
    const card = formatCardRef(G.cardDisplayData, cardId);
    const discardedRefs = [];
    for (const discardedCardId of discardedCardIds) {
        discardedRefs.push(formatCardRef(G.cardDisplayData, discardedCardId));
    }
    if (discardedCardIds.length === requestedCount) {
        pushLog(G, `Player ${playerID} discarded ${discardedRefs.join(', ')} from the top of their deck and gained +${String(totalAttack)} attack (${card}).`, 'applied', cardId);
    }
    else if (discardedCardIds.length > 0) {
        pushLog(G, `Player ${playerID} discarded only ${String(discardedCardIds.length)} of ${String(requestedCount)} cards (${discardedRefs.join(', ')}) — their deck and discard pile ran out — and gained +${String(totalAttack)} attack (${card}).`, 'partial', cardId);
    }
    else {
        pushLog(G, `Player ${playerID} had no cards in their deck or discard pile to discard (${card}).`, 'blocked', cardId);
    }
    // why: D-24786 — the second printed line is a separate hook with no return channel from this
    // one, so this record hands it "those cards". Written on every path (an empty list when
    // nothing was discarded) so it always describes THIS play.
    G.lastDeckTopDiscard = { playerID, sourceCardId: cardId, cardIds: discardedCardIds };
}
/**
 * Hero handler for the `ko-discarded-this-way` keyword (WP-850 / D-24786).
 *
 * Firepower from the Future's "[team:x-men]: KO any number of those cards." The card's first
 * line has already discarded "those cards" and recorded them in `G.lastDeckTopDiscard`
 * (executeHeroEffects runs a card's abilities in order, D-24521 §6). This handler parks the
 * WP-693 KO-from-discard choice limited by `eligibleCardIds` to the recorded cards still in
 * the discard pile; the player picks any number of them, zero included. The X-Men gate rides
 * this line's own `requiresTeam` condition, so an unmet gate never reaches here.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Move context (unused; nothing is shuffled here).
 * @param playerID - The player who played the card.
 * @param cardId - The played card instance.
 * @param _effect - The `{ type: 'ko-discarded-this-way' }` descriptor (no magnitude).
 */
function heroEffectKoDiscardedThisWay(G, _ctx, playerID, cardId, _effect) {
    // why: D-24786 — the record is consumed here on every branch, so it never outlives the play
    // that wrote it once this line has run. `delete`, not `= undefined`: the engine compiles with
    // exactOptionalPropertyTypes.
    const record = G.lastDeckTopDiscard;
    delete G.lastDeckTopDiscard;
    const card = formatCardRef(G.cardDisplayData, cardId);
    if (record === undefined || record.playerID !== playerID || record.sourceCardId !== cardId) {
        pushLog(G, `Player ${playerID} had no discarded cards to KO (${card}).`, 'blocked', cardId);
        return;
    }
    // why: D-24786 — line 1 could discard nothing (deck and discard both empty) and has already
    // logged that; saying none of "the discarded cards" are left would imply some were discarded.
    if (record.cardIds.length === 0) {
        pushLog(G, `Player ${playerID} discarded no cards, so there is nothing to KO (${card}).`, 'neutral', cardId);
        return;
    }
    // why: D-24786 — "those cards" are recomputed against the live discard: a mid-effect
    // reshuffle may have put some of them back into the deck, and only cards still in the
    // discard pile can be KO'd from it.
    const eligibleCardIds = getEligibleKoDiscardCards(G, playerID, undefined, record.cardIds);
    if (eligibleCardIds.length === 0) {
        pushLog(G, `Player ${playerID} has none of the discarded cards left in their discard pile to KO (${card}).`, 'neutral', cardId);
        return;
    }
    // why: the queue is created lazily at the park site, so a match that never parks one leaves
    // the field undefined and the hash oracles stay byte-stable.
    if (!G.pendingKoDiscardChoices) {
        G.pendingKoDiscardChoices = [];
    }
    const entry = {
        choiceType: 'ko-from-discard',
        playerID,
        maxCount: eligibleCardIds.length,
        sourceCardId: cardId,
        eligibleCardIds,
    };
    G.pendingKoDiscardChoices.push(entry);
    pushLog(G, `Player ${playerID} may KO any number of the ${String(eligibleCardIds.length)} discarded cards (${card}).`, 'neutral', cardId);
}
// ---------------------------------------------------------------------------
// Parameterized reveal handler + per-action helpers (WP-253 / D-24024)
//
// The 8 legacy reveal-* handlers collapsed into ONE 'reveal' handler that peeks
// the deck top (× revealCount, =1 today) and evaluates an ordered RevealRule
// branch-list. The per-action helpers hold the verbatim zone-mutation bodies the
// legacy handlers used; revealRulesForLegacyKeyword (rules/revealRule.ts) maps the
// 8 card markers onto these rules, so behavior is byte-identical.
// ---------------------------------------------------------------------------
function heroEffectReveal(G, ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const rules = effect.revealRules ?? [];
    // why: reveal-attack-choose's reject-second guard (D-22001) aborts the WHOLE
    // effect — no peek, no attack, no second park — when a choice is already pending.
    // Only the choose action parks a choice, so the guard is hoisted above the peek
    // loop; other reveal rules never read pendingHeroChoice. (D-24024)
    if (revealRulesContainAnyAction(rules, ['choose-discard-or-return']) && G.pendingHeroChoice !== undefined) {
        return;
    }
    // why: reveal-cost-attack / reveal-attack-choose / reveal-ko-attack all guarded
    // G.turnEconomy BEFORE any mutation (including reveal-ko-attack's KO). Reproduce
    // that: when the rules grant attack and turnEconomy is undefined the whole effect
    // no-ops, never partially KOing first. (D-24024 / D-22003 AC-9 / D-22301)
    if (revealRulesContainAnyAction(rules, ['attack-by-cost', 'attack-fixed']) && !G.turnEconomy) {
        return;
    }
    const revealCount = effect.revealCount ?? 1;
    // why: WP-789 / D-24637 §3a — a reorder reveal buffers EVERY overlay event (hit or miss)
    // in reveal order, because a parked reorder prompt already shows the remainder (the
    // D-24547 double-surface rule): the flush after the loop drops the misses only when the
    // prompt parked. Buffering all events (not just misses) keeps reveal order on the flush.
    // A reveal without `reorderRemainder` passes no buffer and emits inline, as before.
    let deferredEvents;
    if (effect.reorderRemainder === true) {
        deferredEvents = [];
    }
    let didParkReorder = false;
    // why (D-24024 → D-24027): this is the multi-peek WP-253 deferred. peekOffset indexes
    // the live deck; peekIndex counts iterations. DUAL BOUND — iterate at most revealCount
    // times (peekIndex) AND stop at the deck end (peekOffset >= deck.length); an offset-only
    // loop would reveal the WHOLE deck. count=1 is BYTE-IDENTICAL to the WP-253 deck[0] peek:
    // a single iteration runs at offset 0, and the skip-and-advance + the deck-end stop both
    // reduce to the WP-253 no-op `return`.
    let peekOffset = 0;
    for (let peekIndex = 0; peekIndex < revealCount; peekIndex++) {
        // why: re-read the live deck each iteration (do NOT snapshot) — a prior draw/ko shifts
        // the deck. When the offset overruns the deck end, a reveal still owes a card, so
        // reshuffle the discard into the deck and retry — the standard Legendary rule that a
        // reveal (like a draw) reshuffles an exhausted deck mid-effect (D-24285, superseding
        // D-21502's no-op FOR THIS HANDLER; the D-24200 reveal-eight strike keeps its
        // deliberate no-top-up). ctx narrows to ShuffleProvider exactly as the draw handler
        // does — reshuffle needs ctx.random.Shuffle.
        if (peekOffset >= playerZones.deck.length) {
            reshuffleDiscardIntoDeck(playerZones, ctx);
            // why: still short after the reshuffle means the discard was empty too — nothing
            // remains anywhere, so stop the loop. `break` (not `return`) so the post-loop
            // reorder park (WP-479 / D-24286) still runs on this deck-exhausted exit — a
            // fully-expensive ≥2 remainder with an empty discard must still offer the reorder.
            if (peekOffset >= playerZones.deck.length) {
                break;
            }
        }
        const topCardId = playerZones.deck[peekOffset];
        // why: a peek with no card id OR no resolvable cost SKIPS-AND-ADVANCES — leave the card on
        // the deck and peek the next — it MUST NOT `return`/abort the rest of the reveal (copilot
        // #22). At count=1 this is observably the same no-op as the WP-253 `return`; at count>1 it
        // stops one uncosted card in the top N from silently killing the reveal of the cards beneath
        // it (the exact "the card did nothing" failure D-24017 exists to stamp out). The S.H.I.E.L.D.
        // starters carry synthesized cost-0 stats, and a Wound resolves to cost 0 (D-24583), so the
        // skip now applies only to a card the engine has no cost for at all.
        if (!topCardId) {
            peekOffset++;
            continue;
        }
        const revealedCost = resolveRevealedCardCost(G, topCardId);
        if (revealedCost === undefined) {
            peekOffset++;
            continue;
        }
        const deckLengthBeforeRules = playerZones.deck.length;
        applyRevealRules(G, playerID, playerZones, cardId, topCardId, revealedCost, rules, deferredEvents);
        // why: advance the offset ONLY when the deck length is unchanged (the card stayed on the
        // deck). A draw/ko shrank the deck and slid the next card into the same index, so the
        // offset must NOT advance — this is what keeps the WP-253 count=2 test (each iteration
        // re-reads deck[0] after a draw) byte-identical.
        if (playerZones.deck.length === deckLengthBeforeRules) {
            peekOffset++;
        }
    }
    // why: WP-479 / D-24286 — "Put the rest back in any order". The revealed-but-not-drawn
    // cards are the top `peekOffset` of the deck (drawn/KO'd cards were removed; non-drawn
    // cards, incl. skipped no-stats starters, stayed on top in order and each bumped
    // peekOffset). When the reveal is marked `reorderRemainder` and ≥2 remained, park an
    // interactive reorder choice over them — the current player picks their top-of-deck
    // order via resolveReorderChoice; the block-all guard freezes the deck top until then.
    // A remainder of 0 or 1 has no order to choose, so it auto-skips (no park). peekOffset
    // is clamped to deck.length defensively (it cannot structurally exceed it — the loop
    // guard stops at the deck end).
    if (effect.reorderRemainder === true) {
        const remainderCount = Math.min(peekOffset, playerZones.deck.length);
        if (remainderCount >= 2) {
            const cardIds = playerZones.deck.slice(0, remainderCount);
            if (!G.pendingReorderChoices) {
                G.pendingReorderChoices = [];
            }
            G.pendingReorderChoices.push({
                choiceType: 'reorder-deck-top',
                playerID,
                cardIds,
            });
            didParkReorder = true;
        }
    }
    // why: WP-789 / D-24637 §3a — flush the buffered reveal events in reveal order: all of
    // them when no reorder prompt parked, only the hits when one did (the prompt already
    // shows the left-on-top cards). Runs after the deck-exhausted `break` exit too.
    if (deferredEvents !== undefined && Array.isArray(G.notableEvents)) {
        for (const deferredEntry of deferredEvents) {
            if (didParkReorder && deferredEntry.isMiss) {
                continue;
            }
            G.notableEvents.push(deferredEntry.event);
        }
    }
}
/**
 * Evaluates a reveal branch-list against one peeked card's cost. Applies the first
 * matching rule's actions and stops, unless the matched rule sets `continue: true`,
 * in which case it keeps evaluating later rules.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - Active player ID.
 * @param playerZones - The active player's zones (resolved once by the handler).
 * @param sourceCardId - The played hero card whose ability triggered the reveal
 *   (WP-726 — names the source in the heroEffectResolved overlay narrative).
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @param cost - The peeked card's cost (the predicate input).
 * @param rules - The ordered RevealRule branch-list.
 * @param deferredEvents - Optional buffer (WP-789 / D-24637 §3a). When provided, the
 *   overlay event is pushed here (with whether it was a miss) instead of G.notableEvents;
 *   the reorder-reveal caller flushes it after its loop.
 */
function applyRevealRules(G, playerID, playerZones, sourceCardId, topCardId, cost, rules, deferredEvents) {
    // why: WP-325 — accumulate the reveal outcome so ONE line summarizes the peeked
    // card (a `continue: true` chain matches more than one rule). The first matched
    // predicate + every matched rule's actions compose the line after the loop.
    let matchedPredicateText;
    const matchedActionPhrases = [];
    // why: WP-417 / D-24237 — WP-B.2's realized-result item. `describeRevealActions`
    // states what the branch CLAIMED; these are the action kinds whose helper guard
    // fired and mutated nothing, so the line can say what did not actually happen.
    const unappliedActionKinds = [];
    for (const rule of rules) {
        if (!revealPredicateMatches(G, rule.predicate, cost, topCardId)) {
            continue;
        }
        if (matchedPredicateText === undefined) {
            matchedPredicateText = describeRevealPredicate(rule.predicate);
        }
        matchedActionPhrases.push(describeRevealActions(rule.actions, cost));
        for (const unappliedKind of applyRevealRuleActions(G, playerID, playerZones, topCardId, cost, rule.actions)) {
            unappliedActionKinds.push(unappliedKind);
        }
        // why: first-match-wins unless the rule opts into `continue` — the
        // reveal-attack-choose attack rule sets continue so the always→choose rule still
        // parks the choice after the attack. (D-24024) `break` (not `return`) so the
        // reveal-outcome line below still emits; the reveal behavior is unchanged — the
        // same rules are applied in the same order with the same stop condition.
        if (rule.continue !== true) {
            break;
        }
    }
    // why: WP-325 — one reveal-outcome line per peeked card, naming the revealed card +
    // cost + predicate result + action(s), so a conditional "What If…?" effect is no
    // longer silent (the last silent effect path). G.messages is excluded from
    // finalStateHash (D-24081), so this is replay-safe; the Array.isArray guard tolerates
    // a narrow reveal-test fixture G that omits the messages array.
    // why: WP-434 — project the reveal result onto a LOG_OUTCOMES colour: no branch
    // matched → `blocked` (the What-If test failed); matched but some action was
    // guard-blocked → `partial`; matched and fully applied → `applied`. Hoisted above
    // the G.messages block (WP-726) because the heroEffectResolved emit below reads it
    // and must NOT be transitively gated on G.messages being present.
    let revealLogOutcome;
    if (matchedPredicateText === undefined) {
        revealLogOutcome = 'blocked';
    }
    else if (unappliedActionKinds.length > 0) {
        revealLogOutcome = 'partial';
    }
    else {
        revealLogOutcome = 'applied';
    }
    // why: WP-325 — one reveal-outcome line per peeked card. G.messages is excluded from
    // finalStateHash (D-24081), so this is replay-safe; the Array.isArray guard tolerates
    // a narrow reveal-test fixture G that omits the messages array.
    if (Array.isArray(G.messages)) {
        const outcome = matchedPredicateText === undefined
            ? { matched: false }
            : {
                matched: true,
                predicateText: matchedPredicateText,
                actionsText: matchedActionPhrases.join(', '),
                unappliedActionsText: describeUnappliedRevealActions(unappliedActionKinds),
            };
        pushLog(G, formatRevealOutcomeLine(G.cardDisplayData, playerID, topCardId, cost, outcome), revealLogOutcome, topCardId);
    }
    // why: WP-726 / D-24547 — surface the auto-resolving deck-top reveal on the WP-697
    // heroEffectResolved "Hero Ability" overlay (the grant otherwise reaches only
    // G.messages, which is not projected to clients). WP-789 / D-24637 revises D-24547 §2:
    // emit on EVERY reveal, hit or miss — a miss is exactly when the player needs to see
    // the card, because it stays on top as their next draw. A reveal that parks a choice
    // (`choose-discard-or-return` — reveal-attack-choose) still emits nothing: its
    // pending-choice UI already shows the card, so an overlay would double-surface.
    // Guarded on G.notableEvents (the minimal test builder omits it; a real match seeds
    // []); the source + revealed names resolve HERE (raw-ext_id fallback) keeping the
    // composer pure. G.notableEvents IS hashed, but the core-only sentinel plays no
    // deck-top reveal hero, so this is byte-inert there (WP-697 outcome; verified).
    if (!revealRulesContainAnyAction(rules, ['choose-discard-or-return']) &&
        Array.isArray(G.notableEvents)) {
        const sourceCardName = resolveTransformCardName(G, sourceCardId);
        const revealedCardName = resolveTransformCardName(G, topCardId);
        const isMiss = matchedPredicateText === undefined;
        let outcomeText;
        if (isMiss) {
            outcomeText = 'left on top';
        }
        else {
            outcomeText = matchedActionPhrases.join(', ');
        }
        // why: WP-789 / D-24637 — revealedCardId lets the overlay resolve and show the
        // revealed card exactly like `ambushResolved` / `bystanderRevealed`; a revealed
        // card is public at the table (the log line above already names it).
        const revealEvent = {
            type: 'heroEffectResolved',
            playerId: playerID,
            narrative: composeHeroRevealTopNarrative(sourceCardName, revealedCardName, cost, outcomeText),
            revealedCardId: topCardId,
        };
        if (deferredEvents !== undefined) {
            deferredEvents.push({ event: revealEvent, isMiss });
        }
        else {
            G.notableEvents.push(revealEvent);
        }
    }
}
/**
 * Returns whether the peeked card's cost satisfies a reveal predicate.
 *
 * @param G - Game state (for logging an unknown/malformed predicate; never throws).
 * @param predicate - The RevealPredicate to test.
 * @param cost - The peeked card's cost.
 * @returns Whether the predicate matches.
 */
function revealPredicateMatches(G, predicate, cost, topCardId) {
    if (predicate.kind === 'always') {
        return true;
    }
    if (predicate.kind === 'cost-zero') {
        return cost === 0;
    }
    if (predicate.kind === 'cost-odd') {
        return cost % 2 !== 0;
    }
    // why: WP-729 / D-24550 — trait predicates read the peeked card's projected traits
    // from G.cardTraits (never mutated), mirroring investigateCardMatchesCriteria's
    // projection: `team` matches the single printed team; `hero-class` matches either
    // printed class (heroClass / heroClass2, the WP-703 dual-class rule). traitValue is
    // the normalizeTraitSlug slug from the marker — never a hardcoded literal.
    if (predicate.kind === 'team') {
        if (predicate.traitValue === undefined) {
            pushLog(G, 'A reveal rule used a team predicate with no team value and was skipped. Check the reveal rule markup.');
            return false;
        }
        const traits = G.cardTraits ? G.cardTraits[topCardId] : undefined;
        return traits?.team !== undefined && traits.team !== null && traits.team === predicate.traitValue;
    }
    if (predicate.kind === 'hero-class') {
        if (predicate.traitValue === undefined) {
            pushLog(G, 'A reveal rule used a hero-class predicate with no class value and was skipped. Check the reveal rule markup.');
            return false;
        }
        // why: WP-772 / D-24604 — the peeked deck-top card is off play, so a split card matches on
        // either half's class (rules v23 p.49), resolved through the split-face map. (The team
        // branch above needs no change: team is hero-level, equal on both halves.)
        const traits = offPlayCardTraits(G, topCardId);
        return traits?.heroClass === predicate.traitValue || traits?.heroClass2 === predicate.traitValue;
    }
    if (predicate.kind === 'cost-lte') {
        // why: a threshold of 0 is legitimate (reveal M=0 → cost-lte 0), so test for
        // undefined explicitly rather than a falsy `?? default`.
        if (predicate.threshold === undefined) {
            pushLog(G, 'A reveal rule used a cost-lte predicate with no threshold and was skipped. Check the reveal rule markup.');
            return false;
        }
        return cost <= predicate.threshold;
    }
    if (predicate.kind === 'cost-gte') {
        if (predicate.threshold === undefined) {
            pushLog(G, 'A reveal rule used a cost-gte predicate with no threshold and was skipped. Check the reveal rule markup.');
            return false;
        }
        return cost >= predicate.threshold;
    }
    // why: unknown predicate kind → warn to G.messages and do not match, never throw
    // (the rule-execution-pipeline unknown-effect posture). (D-24024)
    pushLog(G, `A reveal rule used an unknown predicate kind "${String(predicate.kind)}" and was skipped. Check the reveal rule markup.`);
    return false;
}
/**
 * Resolves the cost a reveal predicate reads for a revealed deck-top card: its
 * `G.cardStats` cost, or 0 for a Wound, which has no stats entry. Returns undefined
 * for any other card without stats (the reveal skips it).
 *
 * @param G - Game state.
 * @param cardId - The revealed deck-top card's CardExtId.
 * @returns The card's cost, or undefined when it cannot be resolved.
 */
function resolveRevealedCardCost(G, cardId) {
    const cardStats = G.cardStats[cardId];
    if (cardStats !== undefined) {
        return cardStats.cost;
    }
    // why: D-24583 — a Wound has no printed cost and no G.cardStats entry, so every "Reveal
    // the top card … If it costs 0, KO it" skipped a Wound on top, the effect's main tabletop
    // use. An uncosted card costs 0 (rules v23 L2861-2862: Enraging Wounds "still count as
    // Wounds … They cost 0"). Resolved here rather than by adding a Wound entry to
    // G.cardStats, which would change every match's hashed initial state.
    if (cardId === WOUND_EXT_ID) {
        return 0;
    }
    return undefined;
}
/**
 * Applies a matched rule's actions in order. Stops the rule early when a
 * deck-mutating action (draw / ko) reports it did not apply, so a follow-on action
 * (reveal-ko-attack's fixed attack) fires only after the KO succeeded.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - Active player ID.
 * @param playerZones - The active player's zones.
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @param cost - The peeked card's cost (for attack-by-cost).
 * @param actions - The matched rule's actions, applied in order.
 * @returns The action kinds that did NOT realize their mutation (WP-417 /
 *   D-24237) — read only to compose the reveal-outcome log line. Behavior
 *   (which actions run, and the deck-mutating abort) is unchanged.
 */
function applyRevealRuleActions(G, playerID, playerZones, topCardId, cost, actions) {
    const unappliedActionKinds = [];
    for (const action of actions) {
        const applied = applyRevealAction(G, playerID, playerZones, topCardId, cost, action);
        if (!applied) {
            unappliedActionKinds.push(action.kind);
        }
        // why: only a deck-mutating action (ko / draw) gates the rest of the rule —
        // reveal-ko-attack's [ko, attack-fixed] grants the fixed attack ONLY after the
        // KO move returned found (no partial mutation). A non-mutating action that
        // no-ops (e.g. attack with no turnEconomy) does NOT abort the rule. (D-24024 / D-22301)
        if (!applied && isDeckMutatingRevealAction(action.kind)) {
            return unappliedActionKinds;
        }
    }
    return unappliedActionKinds;
}
/**
 * Dispatches one reveal action to its helper. Returns whether the action applied
 * its intended mutation (true for non-deck actions that succeeded; false when a
 * helper's guard fired). Unknown action kinds warn and return true (not a
 * deck-mutation failure, so they do not break the rule).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - Active player ID.
 * @param playerZones - The active player's zones.
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @param cost - The peeked card's cost (for attack-by-cost).
 * @param action - The RevealAction to apply.
 * @returns Whether the action applied.
 */
function applyRevealAction(G, playerID, playerZones, topCardId, cost, action) {
    if (action.kind === 'draw') {
        const isDrawn = applyRevealDraw(playerZones, topCardId);
        // why: D-24732 — a reveal rule's "draw it" (Spider-Man's cost-2-or-less reveals, Card
        // Shark) is a real draw by the active player, so it counts toward cardsDrawn.
        // The turnEconomy guard matches this pipeline's legacy tolerance of an absent economy.
        if (isDrawn && G.turnEconomy) {
            G.turnEconomy.cardsDrawn += 1;
        }
        return isDrawn;
    }
    if (action.kind === 'ko') {
        return applyRevealKo(G, playerZones, topCardId, playerID);
    }
    if (action.kind === 'discard') {
        return applyRevealDiscard(playerZones, topCardId);
    }
    if (action.kind === 'attack-by-cost') {
        return applyRevealAttackByCost(G, cost);
    }
    if (action.kind === 'attack-fixed') {
        return applyRevealAttackFixed(G, action.amount);
    }
    if (action.kind === 'choose-discard-or-return') {
        return applyRevealChoose(G, playerID, topCardId);
    }
    // why: unknown action kind → warn to G.messages and skip, never throw. Treated as
    // a no-op that is NOT a deck-mutation failure, so it does not break the rule. (D-24024)
    pushLog(G, `A reveal rule used an unknown action kind "${String(action.kind)}" and was skipped. Check the reveal rule markup.`);
    return true;
}
/**
 * Returns whether a reveal action mutates the deck (draw / ko). Only these gate the
 * rest of a rule's action list when they fail to apply.
 *
 * @param kind - The reveal action kind.
 * @returns Whether the action is deck-mutating.
 */
function isDeckMutatingRevealAction(kind) {
    // why: D-24582 — 'discard' also moves the deck-top card, so a failed discard aborts the
    // rule's follow-on attack-fixed grant exactly like a failed KO (See Future Timelines).
    return kind === 'draw' || kind === 'ko' || kind === 'discard';
}
/**
 * Returns whether any rule's action list contains one of the given action kinds.
 * Used to hoist the reject-second (choose) and turnEconomy (attack) guards above
 * the peek loop, reproducing the legacy handlers' whole-effect guard ordering.
 *
 * @param rules - The reveal branch-list.
 * @param kinds - The action kinds to look for.
 * @returns Whether any action in any rule matches one of the kinds.
 */
function revealRulesContainAnyAction(rules, kinds) {
    for (const rule of rules) {
        for (const action of rule.actions) {
            for (const kind of kinds) {
                if (action.kind === kind) {
                    return true;
                }
            }
        }
    }
    return false;
}
/**
 * Draw action — moves the peeked deck-top card into the player's hand. Verbatim
 * from the legacy reveal / reveal-min / reveal-odd-draw draw bodies.
 *
 * @param playerZones - The active player's zones.
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @returns Whether the card was found and moved (false leaves zones unchanged).
 */
function applyRevealDraw(playerZones, topCardId) {
    const moveResult = moveCardFromZone(playerZones.deck, playerZones.hand, topCardId);
    if (!moveResult.found) {
        return false;
    }
    playerZones.deck = moveResult.from;
    playerZones.hand = moveResult.to;
    return true;
}
/**
 * KO action — removes the peeked deck-top card from the deck and adds it to the KO
 * pile. Verbatim from the legacy reveal-ko / reveal-ko-or-draw / reveal-ko-attack
 * KO bodies (card removed from deck before being added to KO — D-21801 zone integrity).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerZones - The active player's zones.
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @param ownerPlayerID - The player whose deck it is.
 * @returns Whether the card was found and KO'd (false leaves zones unchanged).
 */
function applyRevealKo(G, playerZones, topCardId, 
// why: WP-853 / D-24789 — PlayerZones carries no owner id, so the caller threads it
// in for the owned-KO count (Absorb Energies).
ownerPlayerID) {
    const moveResult = moveCardFromZone(playerZones.deck, [], topCardId);
    if (!moveResult.found) {
        return false;
    }
    playerZones.deck = moveResult.from;
    G.ko = koCard(G.ko, topCardId);
    recordOwnedCardKoed(G, ownerPlayerID, topCardId, 'deck');
    return true;
}
/**
 * Discard action — moves the peeked deck-top card to the player's discard pile
 * (D-24582; See Future Timelines "If it costs 0, discard it and you get +2 attack").
 * Mirrors applyRevealDraw with the discard pile as the destination.
 *
 * @param playerZones - The active player's zones.
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @returns Whether the card was found and discarded (false leaves zones unchanged).
 */
function applyRevealDiscard(playerZones, topCardId) {
    const moveResult = moveCardFromZone(playerZones.deck, playerZones.discard, topCardId);
    if (!moveResult.found) {
        return false;
    }
    playerZones.deck = moveResult.from;
    playerZones.discard = moveResult.to;
    return true;
}
/**
 * Attack-by-cost action — grants attack equal to the peeked card's cost; no zone
 * mutation (the card stays on the deck). Verbatim from reveal-cost-attack /
 * reveal-attack-choose; keeps the turnEconomy guard (D-21901 / D-22003).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param cost - The peeked card's cost.
 * @returns Whether the grant applied (false when turnEconomy is undefined).
 */
function applyRevealAttackByCost(G, cost) {
    if (!G.turnEconomy) {
        return false;
    }
    G.turnEconomy.attack += cost;
    return true;
}
/**
 * Attack-fixed action — grants a fixed attack amount (the reveal-ko-attack
 * magnitude). Verbatim from reveal-ko-attack's `G.turnEconomy.attack += magnitude`.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param amount - The fixed attack grant.
 * @returns Whether the grant applied (false when turnEconomy is undefined or amount
 *   is missing).
 */
function applyRevealAttackFixed(G, amount) {
    if (!G.turnEconomy) {
        return false;
    }
    if (amount === undefined) {
        return false;
    }
    G.turnEconomy.attack += amount;
    return true;
}
/**
 * Choose-discard-or-return action — parks the existing PendingHeroChoice the player
 * resolves via resolveHeroChoice. Verbatim from reveal-attack-choose: the
 * turnEconomy guard fires BEFORE the park (an undefined turnEconomy means NO park);
 * the reject-second (a choice already pending) is hoisted to the handler top, so a
 * pending choice aborts the whole effect. (D-22001 / D-22003)
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - Active player ID.
 * @param topCardId - The peeked deck-top card's CardExtId.
 * @returns Whether the choice was parked (false when turnEconomy is undefined).
 */
function applyRevealChoose(G, playerID, topCardId) {
    if (!G.turnEconomy) {
        return false;
    }
    const pendingChoice = {
        choiceType: 'discard-or-return',
        cardId: topCardId,
        playerID,
    };
    G.pendingHeroChoice = pendingChoice;
    return true;
}
function heroEffectAttackPerCount(G, _ctx, playerID, cardId, effect) {
    // why: D-24016 — magnitude is the per-unit rate; resolveCountSource resolves
    // the count it scales by, so the grant is magnitude × count. The resolver is
    // pure/total (unknown source → 0), so the grant is deterministic at play time.
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    if (!G.turnEconomy) {
        return;
    }
    // why: a count-scaled attack effect with no count source is a skipped no-op
    // (mirrors the magnitude gate) — there is nothing to scale by.
    if (effect.countSource === undefined) {
        return;
    }
    // why: WP-673 / D-24488 — pass the triggering card so an "each OTHER card"
    // source (worthy-cards-played-this-turn) can exclude this card from its own
    // count. Sources reading a zone this card is never in (victory-bystanders)
    // ignore it.
    const count = resolveCountSource(G, playerID, effect.countSource, cardId);
    // why: WP-677 / D-24493 — perEach is the "for each N" divisor (absent ≡ 1). The grant
    // is magnitude × floor(count / perEach), so "+1 attack for each 2 S.H.I.E.L.D. Levels"
    // grants floor(level / 2). A perEach of 0/undefined defaults to 1 (no divisor).
    const perEach = effect.perEach && effect.perEach > 0 ? effect.perEach : 1;
    const grant = effect.magnitude * Math.floor(count / perEach);
    G.turnEconomy = addResources(G.turnEconomy, grant, 0);
    // why: record the source, count, and grant so the count-scaled attack is
    // observable in replay inspection (no implicit side effects).
    pushLog(G, `Count-scaled attack: +${grant} (${formatCountScaledRate(effect.magnitude, perEach, effect.countSource, count)}).`);
}
/**
 * Count-scaled recruit (WP-674 / D-24489). The recruit sibling of
 * heroEffectAttackPerCount: "+N recruit for each other card you played this turn
 * that costs 4 or more" (noir's Follow Big Leads). The grant is
 * `magnitude × resolveCountSource(...)`, added to G.turnEconomy.recruit instead
 * of attack. Same pure/total contract as the attack variant.
 *
 * @param G - Game state (mutated: recruit added to the turn economy).
 * @param _ctx - Unused (framework parity).
 * @param playerID - The active player.
 * @param cardId - The triggering card, passed to resolveCountSource so an "each
 *   OTHER card" source can exclude this card from its own count.
 * @param effect - The recruit-per-count effect descriptor (magnitude + countSource).
 */
function heroEffectRecruitPerCount(G, _ctx, playerID, cardId, effect) {
    // why: WP-674 / D-24489 — magnitude is the per-unit rate; resolveCountSource
    // resolves the count it scales by, so the grant is magnitude × count. Mirrors
    // heroEffectAttackPerCount exactly, granting recruit rather than attack.
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    if (!G.turnEconomy) {
        return;
    }
    // why: a count-scaled recruit effect with no count source is a skipped no-op
    // (mirrors the magnitude gate) — there is nothing to scale by.
    if (effect.countSource === undefined) {
        return;
    }
    // why: WP-674 / D-24489 — pass the triggering card so an "each OTHER card"
    // source (cost-four-plus-played-this-turn) can exclude this card from its own count.
    const count = resolveCountSource(G, playerID, effect.countSource, cardId);
    // why: WP-677 / D-24493 — perEach is the "for each N" divisor (absent ≡ 1); grant is
    // magnitude × floor(count / perEach). See heroEffectAttackPerCount.
    const perEach = effect.perEach && effect.perEach > 0 ? effect.perEach : 1;
    const grant = effect.magnitude * Math.floor(count / perEach);
    G.turnEconomy = addResources(G.turnEconomy, 0, grant);
    // why: record the source, count, and grant so the count-scaled recruit is
    // observable in replay inspection (no implicit side effects).
    pushLog(G, `Count-scaled recruit: +${grant} (${formatCountScaledRate(effect.magnitude, perEach, effect.countSource, count)}).`);
}
/**
 * Count-scaled bystander capture (WP-714 / D-24537). The capture sibling of
 * heroEffectAttackPerCount / heroEffectRecruitPerCount: it scales the same way
 * (N = magnitude × floor(count / perEach)), but the grant is N Bystander captures
 * instead of a resource add. Drives Ultron's Genetic Experimentation ("[hc:tech]:
 * Kidnap a Bystander for each other [hc:tech] Ally you played this turn").
 *
 * Each capture attaches one Bystander to the FIRST City villain by ascending city
 * index via the shipped attachBystanderToCityVillain; when the City holds no villain
 * the universal-rules fallback captureBystanderToMastermind captures instead. The
 * loop is NON-interactive (no PendingSeatChoice) and supply-bounded — it breaks the
 * moment the Bystander supply empties. No RNG, no .reduce(), count 0 → no-op.
 *
 * @param G - Game state (mutated: piles.bystanders + the capture zones).
 * @param _ctx - Unused (the capture is deterministic; no stage ride, no randomness).
 * @param playerID - The active player (owner of the played card).
 * @param cardId - The triggering card, passed to resolveCountSource so the
 *   tech-heroes-played-this-turn source EXCLUDES this card from its own count.
 * @param effect - The kidnap-per-count effect descriptor (magnitude + countSource).
 */
function heroEffectKidnapPerCount(G, _ctx, playerID, cardId, effect) {
    // why: WP-714 / D-24537 — magnitude is the per-unit rate; resolveCountSource
    // resolves the count it scales by, so N = magnitude × floor(count / perEach).
    // The count-scaling half is copied from heroEffectRecruitPerCount; the grant is
    // N Bystander captures instead of a resource add.
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: a count-scaled capture with no count source is a skipped no-op (mirrors the
    // magnitude gate) — there is nothing to scale by.
    if (effect.countSource === undefined) {
        return;
    }
    // why: WP-714 / D-24537 — thread the triggering card so tech-heroes-played-this-turn
    // counts each OTHER Tech card played this turn (self-EXCLUSIVE, honouring hc2 +
    // Copy Powers copied classes via the shipped resolver — no fresh class loop).
    const count = resolveCountSource(G, playerID, effect.countSource, cardId);
    // why: perEach is the "for each N" divisor (absent ≡ 1); see heroEffectAttackPerCount.
    const perEach = effect.perEach && effect.perEach > 0 ? effect.perEach : 1;
    const captures = effect.magnitude * Math.floor(count / perEach);
    // why: WP-714 / D-24537 — capture one Bystander per iteration to the FIRST City
    // villain by ascending city index (D-24537 auto-target; captureBystanderToMastermind
    // fallback when the City holds none), reusing the here-hold-this machinery WITHOUT
    // its interactive PendingSeatChoice. Supply-bounded: break the moment the Bystander
    // supply empties (the villainEffectCaptureBystander counted-variant precedent). A
    // for loop (never .reduce()) per the zone/effect-application rule; count 0 → no-op.
    for (let capturedSoFar = 0; capturedSoFar < captures; capturedSoFar += 1) {
        if (G.piles.bystanders.length === 0) {
            break;
        }
        const targets = buildHereHoldThisTargets(G);
        if (targets.length === 0) {
            captureBystanderToMastermind(G);
        }
        else {
            attachBystanderToCityVillain(G, targets[0].cityIndex);
        }
    }
    // why: record the count and capture total so the count-scaled kidnap is observable
    // in replay inspection (no implicit side effects).
    pushLog(G, `Count-scaled kidnap: ${captures} Bystander capture(s) (${formatCountScaledRate(effect.magnitude, perEach, effect.countSource, count)}).`);
}
/**
 * Sends one card Undercover (WP-678 / D-24494, supersedes D-24060).
 *
 * Moves `cardId` from `fromZone` into the acting player's Victory Pile and records
 * it in the per-player `undercover` tracker (worth 1 VP each; universal-rules-v23
 * §Undercover). Undercover'd cards live in `victory` (so S.H.I.E.L.D. Level and
 * presence count them) AND in `undercover` (so scoring awards 1 VP per entry without
 * inferring from card type). Returns the new `fromZone` array (the caller assigns it);
 * a no-op (card not found) returns `fromZone` unchanged.
 *
 * @param G - Game state (mutates the player's `victory` + `undercover`).
 * @param playerID - The acting player.
 * @param cardId - The card to send Undercover.
 * @param fromZone - The source array the card is removed from (hand, or G.piles.officers).
 * @returns The new source array with `cardId` removed.
 */
export function sendCardUndercover(G, playerID, cardId, fromZone) {
    const zones = G.playerZones[playerID];
    if (!zones) {
        return fromZone;
    }
    const moveResult = moveCardFromZone(fromZone, zones.victory, cardId);
    if (!moveResult.found) {
        return fromZone;
    }
    zones.victory = moveResult.to;
    // why: `?? []` guards a reconstructed/legacy state that predates the tracker (see the
    // scoring guard); a live match always has it (playerInit).
    zones.undercover = [...(zones.undercover ?? []), cardId];
    pushLog(G, `Player ${playerID} sent ${cardId} Undercover (into the Victory Pile, +1 VP).`);
    return moveResult.from;
}
/**
 * Undercover from the hand: "send a [team:shield] Hero from your hand Undercover"
 * (WP-678 / D-24494). Eligible targets are hand cards counting as team `shield`.
 * 0 eligible → legal no-op; 1 → auto-send; ≥2 → park a PendingUndercoverChoice
 * resolved by resolveUndercoverChoice. Carries no magnitude.
 *
 * @param G - Game state (mutated).
 * @param _ctx - Unused (framework parity).
 * @param playerID - The acting player.
 * @param cardId - The triggering (played) card, recorded on the pending entry.
 * @param _effect - Unused (the source shape is fixed by the keyword).
 */
function heroEffectUndercoverHandShieldHero(G, _ctx, playerID, cardId, _effect) {
    const zones = G.playerZones[playerID];
    if (!zones) {
        return;
    }
    // why: eligible = hand cards counting as team `shield` (the printed team; copied teams
    // are an in-play concept and never apply to a hand card). The triggering card is in
    // inPlay when its onPlay fires, so it is not in hand — no self-exclusion needed.
    const eligible = zones.hand.filter((handCardId) => cardHasTeamWhenPlayed(G, handCardId, 'shield'));
    if (eligible.length === 0) {
        // why: a legal no-op — the option was taken but no [team:shield] Hero is in hand.
        return;
    }
    if (eligible.length === 1) {
        // why: exactly one eligible target — auto-send (no prompt), mirroring the shipped
        // single-candidate auto-resolve pattern (optional-ko-reward / copy-powers).
        zones.hand = sendCardUndercover(G, playerID, eligible[0], zones.hand);
        return;
    }
    // why: ≥2 eligible — park an interactive pick resolved by resolveUndercoverChoice
    // (D-24494). Lazy-init at the park site (never in Game.setup); absent = no pending choice.
    if (!G.pendingUndercoverChoice) {
        G.pendingUndercoverChoice = [];
    }
    const pending = {
        playerID,
        cardId,
        source: 'hand-shield-hero',
        eligibleTargets: [...eligible],
    };
    G.pendingUndercoverChoice.push(pending);
}
/**
 * Undercover from the S.H.I.E.L.D. Officer Stack: "send a card from the S.H.I.E.L.D.
 * Officer Stack Undercover" (WP-678 / D-24494). Deterministic — the Officer Stack is
 * homogeneous, so this sends the top Officer (no player choice). Empty stack → no-op.
 * Carries no magnitude.
 *
 * @param G - Game state (mutated).
 * @param _ctx - Unused (framework parity).
 * @param playerID - The acting player.
 * @param _cardId - Unused (the sent card comes from the Officer Stack, not the played card).
 * @param _effect - Unused (the source shape is fixed by the keyword).
 */
function heroEffectUndercoverOfficerStack(G, _ctx, playerID, _cardId, _effect) {
    const zones = G.playerZones[playerID];
    if (!zones) {
        return;
    }
    const officers = G.piles?.officers;
    if (!officers || officers.length === 0) {
        // why: a legal no-op — the Officer Stack is empty.
        return;
    }
    // why: the Officer Stack is homogeneous (identical S.H.I.E.L.D. Officers), so "a card"
    // is deterministically the top one — no interactive choice.
    G.piles.officers = sendCardUndercover(G, playerID, officers[0], officers);
}
/**
 * Reveal-Hero-Deck-attack (WP-668 / D-24481). Jade Giantess's "For every 2
 * Recruit you made this turn, Reveal the top card of the Hero Deck, put it on
 * the bottom of that deck, and you get that card's printed Attack."
 *
 * A deterministic SYNCHRONOUS onPlay effect — the reveals are forced and pure
 * upside (no player decision), so it resolves in place rather than through the
 * pending-choice machinery (the investigate / reveal-from-hand pattern). The
 * only G mutations are the attack grant and the G.heroDeck reorder; no new zone,
 * no new G field.
 *
 * Timing: iterations are a SNAPSHOT of Recruit-made-so-far at play time —
 * `Math.floor(G.turnEconomy.recruit / divisor)`, read once here. This is the
 * faithful tabletop resolution (an ability counts Recruit generated by cards
 * resolved before it this turn), deliberately NOT the WP-568 recruit-threshold
 * wait-and-see: a scaling factor cannot re-fire cleanly (re-running the loop each
 * move would multi-count the reveals).
 *
 * Bottoming keeps the shared Hero Deck non-depleting, so N reveals proceed as
 * long as the deck is non-empty (a deck shorter than N simply re-reveals cycled
 * cards — faithful to the printed instruction repeated N times). An empty Hero
 * Deck reveals nothing.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (no RNG — a top-of-deck read is deterministic).
 * @param playerID - The acting player.
 * @param cardId - The played Jade Giantess card (for the log reference).
 * @param effect - The effect descriptor; `magnitude` is the "for every N Recruit" divisor.
 */
function heroEffectRevealHeroDeckAttack(G, _ctx, playerID, cardId, effect) {
    if (!G.turnEconomy) {
        return;
    }
    // why: magnitude carries the "for every N Recruit" divisor (2 for Jade Giantess).
    // A non-positive divisor is a defensive skip — the parser only emits [1-9]\d*,
    // so this is unreachable in practice, but guards against a division by zero.
    const divisor = effect.magnitude;
    if (divisor <= 0) {
        return;
    }
    // why: SNAPSHOT of Recruit made so far this turn — the faithful tabletop count
    // (G.turnEconomy.recruit is the gross recruit-made accumulator the
    // recruitMadeThisTurnAtLeast gate reads). Read once; not re-checked later.
    const iterations = Math.floor(G.turnEconomy.recruit / divisor);
    if (iterations === 0) {
        // why: below the per-N-Recruit threshold nothing is revealed — a `neutral`
        // line so the player sees WHY the ability produced no Attack (the "did
        // nothing" confusion the hero-effect arc keeps paying for), without claiming
        // the ability failed (it resolved correctly at 0 scale).
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} revealed no Hero-Deck cards — they made fewer than ${divisor} Recruit this turn.`, 'neutral', cardId);
        return;
    }
    if (!G.heroDeck || G.heroDeck.length === 0) {
        // why: WP-434 — an empty Hero Deck (rare, near game end) is `blocked` — the
        // ability would fire but there is nothing to reveal. The `!G.heroDeck` guard
        // tolerates older/stripped mocks that predate the zone (mirrors the !G.turnEconomy
        // guard above); the real G always carries heroDeck.
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} could not reveal a Hero-Deck card — the Hero Deck is empty.`, 'blocked', cardId);
        return;
    }
    // why: reveal the top card, add its PRINTED attack (G.cardStats[id].attack —
    // the setup-injected stat, the same value investigate reads), then rotate it to
    // the BOTTOM of G.heroDeck by rebinding the array (front = top; NEVER .shift()).
    let totalAttack = 0;
    let revealedCount = 0;
    for (let iteration = 0; iteration < iterations; iteration++) {
        const topCardId = G.heroDeck[0];
        if (topCardId === undefined) {
            break;
        }
        // why: WP-772 / D-24604 — a Hero Deck card is off play, so a split card's printed Attack is
        // the total of both halves (rules v23 p.49), resolved through the split-face map.
        const cardStat = offPlayCardStats(G, topCardId);
        // why: a card with no stat entry contributes 0 — never a fabricated value.
        const printedAttack = cardStat !== undefined ? cardStat.attack : 0;
        totalAttack += printedAttack;
        revealedCount++;
        // why: rotate top → bottom by rebind (front-slice + append), so the shared
        // Hero Deck keeps its length and a deck shorter than N re-reveals cycled cards.
        G.heroDeck = [...G.heroDeck.slice(1), topCardId];
    }
    G.turnEconomy = addResources(G.turnEconomy, totalAttack, 0);
    // why: WP-434 — a realized count-scaled reveal is `applied` (green); name the
    // reveal count and the total Attack so the scaled grant is observable.
    pushLog(G, `Player ${playerID} revealed ${revealedCount} card(s) from the Hero Deck via ${formatCardRef(G.cardDisplayData, cardId)} and gained +${totalAttack} attack.`, 'applied', cardId);
    // why: WP-697 / D-24516 — emit the heroEffectResolved notable event LAST, after
    // the attack grant and the `applied` log push, observing a realized reveal
    // (revealedCount > 0; realized means cards were revealed, NOT that attack > 0 —
    // a 0-printed-attack reveal still did the invisible work). Placement after every
    // early return (the missing-turnEconomy / divisor-≤-0 guard, the below-threshold
    // `neutral` return, the empty-deck `blocked` return) means those non-realized
    // paths emit nothing. Guarded because the minimal heroEffects test builder omits
    // G.notableEvents (a real match seeds [] at setup); handlers never throw, so an
    // absent array is a silent skip — the transformResolved emission precedent.
    // G.messages is not projected to clients, so this event is what drives the
    // arena-client "Hero Ability" overlay. The name resolves HERE via the shared
    // resolveTransformCardName display-name resolver (raw-ext_id fallback), keeping
    // composeHeroRevealAttackNarrative pure.
    if (Array.isArray(G.notableEvents)) {
        const cardName = resolveTransformCardName(G, cardId);
        G.notableEvents.push({
            type: 'heroEffectResolved',
            playerId: playerID,
            narrative: composeHeroRevealAttackNarrative(cardName, revealedCount, totalAttack),
        });
    }
}
function heroEffectOptionalKoReward(G, _ctx, playerID, cardId, effect) {
    // why: D-24019 — parks an interactive choice (mirrors WP-242); the reward
    // is granted on resolve (resolveOptionalKoReward), not at play time. The
    // player either declines (no KO, no reward) or KOs exactly one card from
    // their hand or discard pile, in which case the reward fires.
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: eligible = discard ∪ hand, ANY card INCLUDING wounds (the printed
    // text is "a card", not "a Hero") — no type/cost/keyword filtering. 0
    // eligible (both zones empty) → skipped no-op + a G.messages line (mirrors
    // the D-24017 empty-supply rescue logging), so the player can see why the
    // ability did nothing.
    // why: D-24442 — the KO source is hand ∪ discard ∪ inPlay (cards played this
    // turn), so the ability parks whenever ANY of the three has a card. Counting
    // inPlay is what lets a player who has emptied hand+discard (e.g. after a
    // within-turn reshuffle) still KO a card they played this turn. NOTE: at this
    // onPlay dispatch the triggering card itself is already in inPlay, so
    // eligibleCount is ≥ 1 in normal play — the zero-branch below is now a
    // defensive no-op (reachable only via a direct/unit dispatch with an empty
    // inPlay), kept so a genuinely empty state parks nothing rather than dangling.
    const eligibleCount = playerZones.discard.length + playerZones.hand.length + playerZones.inPlay.length;
    if (eligibleCount === 0) {
        pushLog(G, `Player ${playerID} could not KO a card for a hero ability — hand, discard pile, and in-play cards are all empty, so no reward was granted.`);
        return;
    }
    // why: defensive — the parser only emits a seeded rewardType, but an
    // unseeded reward here is a logged no-op that never reaches the queue
    // (no reward executor exists for it).
    const rewardType = effect.rewardType;
    if (rewardType === undefined || !OPTIONAL_KO_REWARD_SEEDED_REWARDS.has(rewardType)) {
        pushLog(G, `Player ${playerID} played a hero ability whose optional-KO reward is not yet supported, so the choice was skipped.`);
        return;
    }
    // why: lazy-init at the park site (mirrors villainEffects.execute.ts:190
    // pendingKoHeroChoices) — NEVER in Game.setup; the optional field tolerates
    // older snapshots. The park itself is SILENT (no G.messages line), mirroring
    // the WP-242 park; the reward grant is logged by the dispatched executor.
    if (!G.pendingOptionalKoRewards) {
        G.pendingOptionalKoRewards = [];
    }
    G.pendingOptionalKoRewards.push({
        playerID,
        rewardType,
        rewardMagnitude: effect.magnitude ?? 1,
        sourceCardId: cardId,
    });
}
/**
 * Park handler for the `optional-ko-hand-discard` hero keyword (WP-667 / D-24480).
 *
 * Radioactive Riot's "you may KO a card from your hand or discard pile" — the
 * NO-REWARD, hand+discard-only variant of optional-ko-reward. It parks a no-reward
 * entry (`rewardType: 'none'`, `koZones: ['hand','discard']`) into the SAME
 * `G.pendingOptionalKoRewards` queue, so the block-all guard, getLegalMoves
 * short-circuit, resolve move, projection, and client prompt are all reused. The
 * recruit-threshold:6 gate is evaluated on the hook BEFORE this handler runs (the
 * wait-and-see window), so this runs only when the player has made ≥6 Recruit.
 *
 * KO source = hand ∪ discard ONLY (the printed text; NOT inPlay — unlike the
 * D-24442 wide set the rewarded variant uses). 0 eligible (both empty) → a logged
 * no-op that parks nothing (never a throw), so the player can see why nothing
 * happened.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the KO happens at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the resolve-move log).
 * @param _effect - The `{ type: 'optional-ko-hand-discard' }` descriptor (no magnitude).
 */
function heroEffectOptionalKoHandDiscard(G, _ctx, playerID, cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: WP-667 — eligible = hand ∪ discard ONLY (Radioactive Riot excludes in-play
    // cards, unlike the D-24442 wide set). 0 eligible → skipped no-op + a log line so the
    // player sees why the ability did nothing (mirrors the optional-ko-reward empty branch).
    const eligibleCount = playerZones.hand.length + playerZones.discard.length;
    if (eligibleCount === 0) {
        pushLog(G, `Player ${playerID} could not KO a card for ${formatCardRef(G.cardDisplayData, cardId)}'s ability — their hand and discard pile are both empty.`);
        return;
    }
    // why: WP-667 / D-24480 — park a NO-REWARD entry into the shared optional-ko-reward
    // queue; rewardType 'none' makes the resolve skip the reward dispatch, koZones
    // ['hand','discard'] makes the resolve reject an in-play KO + the projection list an
    // empty inPlay set. Lazy-init the queue (mirrors the reward park); the park is SILENT.
    if (!G.pendingOptionalKoRewards) {
        G.pendingOptionalKoRewards = [];
    }
    G.pendingOptionalKoRewards.push({
        playerID,
        rewardType: 'none',
        rewardMagnitude: 0,
        sourceCardId: cardId,
        koZones: ['hand', 'discard'],
    });
}
/**
 * Park handler for the `optional-ko-your-hero` hero keyword (WP-767 / D-24600).
 *
 * Snarling Fangs' Moonlight "Whenever you defeat a Villain or Mastermind this turn, you
 * may KO one of your Heroes." The per-defeat timing and the Moonlight gate ride the hook's
 * conditions (the D-24467 wait-and-see deferral re-evaluates both at each defeat), so this
 * handler runs once per qualifying defeat. It parks a NO-REWARD entry into the shared
 * `G.pendingOptionalKoRewards` queue, reusing the block-all guard, the bot short-circuit,
 * the resolve move, the projection and the client prompt.
 *
 * KO source = hand ∪ played this turn (rules v23 §3439 "your Heroes"), Heroes only. 0
 * eligible (hand + play hold only Wounds, or nothing) → a logged no-op that parks nothing.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the KO happens at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the resolve-move log).
 * @param _effect - The `{ type: 'optional-ko-your-hero' }` descriptor (no magnitude).
 */
function heroEffectOptionalKoYourHero(G, _ctx, playerID, cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    let eligibleCount = 0;
    for (const handCardId of playerZones.hand) {
        if (handCardId !== WOUND_EXT_ID) {
            eligibleCount += 1;
        }
    }
    for (const inPlayCardId of playerZones.inPlay) {
        if (inPlayCardId !== WOUND_EXT_ID) {
            eligibleCount += 1;
        }
    }
    if (eligibleCount === 0) {
        pushLog(G, `Player ${playerID} could not KO a Hero for ${formatCardRef(G.cardDisplayData, cardId)}'s ability — they have no Heroes in hand or played this turn.`);
        return;
    }
    // why: WP-767 / D-24600 — koZones ['hand','inPlay'] excludes the discard pile ("your
    // Heroes" = hand + played this turn), and koHeroesOnly because a Wound is not a Hero. The
    // resolve, projection and bot all honour both. Lazy-init the queue; the park is SILENT.
    if (!G.pendingOptionalKoRewards) {
        G.pendingOptionalKoRewards = [];
    }
    G.pendingOptionalKoRewards.push({
        playerID,
        rewardType: 'none',
        rewardMagnitude: 0,
        sourceCardId: cardId,
        koZones: ['hand', 'inPlay'],
        koHeroesOnly: true,
    });
}
/**
 * Park handler for the `smash` hero keyword (WP-676 / D-24492).
 *
 * Per universal-rules-v23 §Smash, "Smash N" = "You may discard another card from
 * your hand. If you do, you get +N attack." — an interactive OPTIONAL per-instance
 * choice. This parks ONE `PendingSmashDiscard { playerID, magnitude }` per Smash
 * hook onto the FIFO `G.pendingSmashDiscards` queue; the +N Attack is granted at
 * resolve time (resolveSmashDiscard), never at play time. She-Hulk's Hurl Trucks
 * prints two "Smash 2" as two separate abilities[] entries → two hooks → two parks,
 * resolved independently (+0 / +2 / +4).
 *
 * // why: D-24492 — `smash` is NOT in NO_MAGNITUDE_KEYWORDS, so executeSingleEffect's
 * magnitude pre-gate has already confirmed a valid +N magnitude before this runs
 * (the +N Attack rides the effect magnitude). A bare magnitude-less `[keyword:Smash]`
 * verb token (the co-printed conditional-KO clauses on Korg / Namora) never reaches
 * here — it fails that pre-gate and safe-skips (no park, no freeze).
 *
 * // why: an empty hand parks NOTHING (nothing to discard → the "you may discard
 * another card" has no legal target), logged as a neutral no-op so the player can
 * see why the ability granted no Attack. The played Smash card is already in inPlay,
 * so the hand holds only "another card"; an empty hand means no eligible discard.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the discard + Attack grant happen at resolve time).
 * @param playerID - The player who played the Smash card.
 * @param cardId - The played card (recorded for the no-op log line).
 * @param effect - The effect descriptor carrying the Attack magnitude.
 */
function heroEffectSmash(G, _ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: D-24492 — "discard ANOTHER card from your hand": the eligible set is the
    // whole current hand (the played Smash card is already in inPlay). An empty hand
    // means nothing to discard, so park nothing and log the no-op (mirrors the
    // optional-ko-reward empty branch), so the player sees why the ability did nothing.
    if (playerZones.hand.length === 0) {
        pushLog(G, `Player ${playerID} could not Smash for ${formatCardRef(G.cardDisplayData, cardId)} — their hand had no other card to discard, so no Attack was granted.`);
        return;
    }
    // why: D-24492 — lazy-init at the park site (mirrors the optional-ko-reward park) —
    // NEVER in Game.setup, so a game that never plays a Smash card carries no new field and
    // both hash oracles stay byte-unchanged. The park is SILENT (no G.messages line); the
    // resolve move logs the discard/decline outcome. The magnitude is the +N Attack granted
    // iff the player discards; effect.magnitude is pre-gate-validated (smash NOT in
    // NO_MAGNITUDE_KEYWORDS), the ?? 1 is a defensive default for a direct unit dispatch.
    if (!G.pendingSmashDiscards) {
        G.pendingSmashDiscards = [];
    }
    G.pendingSmashDiscards.push({
        playerID,
        magnitude: effect.magnitude ?? 1,
    });
}
/**
 * Park handler for the `optional-discard-draw` hero keyword (WP-754 / D-24581).
 *
 * "You may discard a card. If you do, draw a card." — vnom Hungry for Action (its Digest 3
 * branch), gotg Gritty Scavenger, asrd Bio-Engineered Cyborg, shld GW Bridge and antm Risky
 * Science ([hc:tech]-gated). Parks one draw-reward entry on the existing Smash queue;
 * resolveSmashDiscard discards the chosen hand card and then draws `magnitude` cards, or the
 * player declines and nothing happens.
 *
 * // why: D-24581 — reuse the Smash queue rather than a sibling pending type. Smash already
 * models "optionally discard one hand card, then a reward" with the same eligible set (the whole
 * hand — the played card is already in play), decline path, stale check and prompt; only the
 * reward differs, carried by the omit-when-absent `reward: 'draw'` discriminator. No new move,
 * queue, block-all guard or prompt.
 *
 * // why: `optional-discard-draw` is NOT in NO_MAGNITUDE_KEYWORDS — the magnitude is the number
 * of cards drawn, already validated by the executeSingleEffect pre-gate; the ?? 1 is a defensive
 * default for a direct unit dispatch. An empty hand parks nothing (no card to discard) and logs
 * the no-op, mirroring heroEffectSmash.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the discard + draw happen at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the no-op log line).
 * @param effect - The effect descriptor carrying the draw count.
 */
function heroEffectOptionalDiscardDraw(G, _ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    if (playerZones.hand.length === 0) {
        pushLog(G, `Player ${playerID} could not discard a card to draw for ${formatCardRef(G.cardDisplayData, cardId)} — their hand was empty, so nothing was drawn.`);
        return;
    }
    // why: lazy-init at the park site (the heroEffectSmash precedent), never in Game.setup, so a
    // game that never plays one of these cards carries no new field. The park is silent; the
    // resolve move logs the discard-and-draw or the decline.
    if (!G.pendingSmashDiscards) {
        G.pendingSmashDiscards = [];
    }
    G.pendingSmashDiscards.push({
        playerID,
        magnitude: effect.magnitude ?? 1,
        reward: 'draw',
    });
}
/**
 * Compound park handler for the `put-hand-on-deck-top` hero keyword (WP-700 / D-24519).
 *
 * Gambit's Stack the Deck / Brainstorm's Time Loop Experiments / the dstr/wpnx/wtif siblings —
 * "Draw N cards. Then put a card from your hand on top of your deck." This is a two-part onPlay
 * effect: (1) draw `magnitude` cards for the acting player, then (2) park a MANDATORY
 * PendingPutHandOnDeckTop so the player places one card from the enlarged hand on top of their
 * own deck. resolvePutHandOnDeckTop performs the placement.
 *
 * // why: D-24519 — the draw happens HERE, before the park, so the drawn cards are in hand for
 * the player to choose from (faithful to "Draw N. THEN put a card…"). The draw reuses the same
 * deterministic drawFromPlayerDeck helper the `draw` keyword uses (reshuffle-on-empty via
 * ShuffleProvider); ctx is narrowed to ShuffleProvider structurally (the WP-005B/008B pattern).
 * effect.magnitude is the printed draw count, pre-gate-validated (put-hand-on-deck-top is NOT in
 * NO_MAGNITUDE_KEYWORDS); the ?? 1 is a defensive default for a direct unit dispatch.
 *
 * // why: D-24519 — an empty hand AFTER the draw (a degenerate that cannot arise from the seven
 * shipped cards, which always draw ≥1) parks nothing and logs the no-op, so the player sees why
 * the ability did nothing (mirrors heroEffectSmash's empty-hand branch). Lazy-init at the park
 * site, NEVER in Game.setup, so a game that never plays one of these cards carries no new field
 * and both hash oracles stay byte-unchanged. The park is SILENT; resolvePutHandOnDeckTop logs
 * the placement.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context, narrowed to ShuffleProvider for the deck reshuffle.
 * @param playerID - The player who played the card.
 * @param cardId - The played card (kept on the pending entry for log/UX provenance).
 * @param effect - The `{ type: 'put-hand-on-deck-top', magnitude: N }` descriptor.
 */
function heroEffectPutHandOnDeckTop(G, ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // Part 1: draw the printed number of cards into the acting player's hand.
    const requestedCount = effect.magnitude ?? 1;
    const drawnCount = drawFromPlayerDeck(G, playerID, requestedCount, ctx);
    // why: D-24732 — an effect draw by the current player is an "extra card drawn this turn"
    // (Berserker Rage's extra-cards-drawn-this-turn source, the cardsDrawnThisTurnAtLeast gate).
    G.turnEconomy.cardsDrawn += drawnCount;
    if (drawnCount < requestedCount) {
        // why: WP-434 — a short draw (deck + discard empty) is `partial`; name the realized amount.
        pushLog(G, `Player ${playerID} drew ${drawnCount} of ${requestedCount} card(s) from ${formatCardRef(G.cardDisplayData, cardId)} — their deck and discard pile were empty.`, 'partial', cardId);
    }
    else {
        pushLog(G, `Player ${playerID} drew ${drawnCount} card(s) from ${formatCardRef(G.cardDisplayData, cardId)}.`, 'applied', cardId);
    }
    // Part 2: park the mandatory put-on-top choice, unless the hand is empty (degenerate).
    if (playerZones.hand.length === 0) {
        pushLog(G, `Player ${playerID} could not put a card on top of their deck for ${formatCardRef(G.cardDisplayData, cardId)} — their hand was empty.`);
        return;
    }
    if (!G.pendingPutHandOnDeckTop) {
        G.pendingPutHandOnDeckTop = [];
    }
    G.pendingPutHandOnDeckTop.push({ playerID, sourceCardId: cardId });
}
/**
 * Reveals (SNAPSHOTS) one player's deck top for a reveal-top-dispose choice (WP-702 / D-24521),
 * reshuffling their discard into the deck first when the deck is empty (the Legendary
 * reveal-reshuffle rule). Returns the `{ ownerPlayerID, cardId }` snapshot, or null when the
 * deck (and discard) are genuinely exhausted — nothing to reveal.
 *
 * // why: the reveal SNAPSHOTS the deck top and does NOT remove it (the Melter / Ruthless
 * Dictator SNAPSHOT discipline): the block-all guard freezes the deck top while the choice is
 * pending, so this snapshot cannot drift; the resolve move then discards the exact top card or
 * keeps it. Shared by both handlers (own-deck + each-other) — one reveal-one-deck implementation.
 *
 * @param G - Game state (mutated only by a possible reshuffle-on-empty).
 * @param ownerPlayerID - The player whose deck top to reveal.
 * @param shuffleContext - ShuffleProvider for the reshuffle-on-empty (ctx.random).
 * @returns the snapshot entry, or null if the player has no revealable card.
 */
function revealDeckTopForDispose(G, ownerPlayerID, shuffleContext) {
    const zones = G.playerZones[ownerPlayerID];
    if (!zones) {
        return null;
    }
    // why: D-24285 — "reveal the top card" is a reveal, so an empty deck reshuffles the
    // discard into the deck first (the reveal-reshuffle rule; the Melter each-deck precedent).
    // reshuffleDiscardIntoDeck no-ops on an empty discard, so a genuinely exhausted deck +
    // discard contributes no revealed card (never a hollow — nothing was there to reveal).
    if (zones.deck.length === 0) {
        reshuffleDiscardIntoDeck(zones, shuffleContext);
    }
    if (zones.deck.length === 0) {
        return null;
    }
    return { ownerPlayerID, cardId: zones.deck[0] };
}
/**
 * Re-reveals stale KO-or-keep entries at the FRONT of G.pendingRevealTopDispose (WP-754 / D-24581).
 *
 * A KO-or-keep entry (`isDiscardAllowed === false`, parked by `reveal-top-may-ko`) snapshots the
 * owner's deck top, but a synchronous sibling can move that card before the player decides: a
 * later Excessive Violence draw in the same fight (Rending Claws after Gruesome Feast), a deferred
 * grant that draws, or a second reveal of the same top whose sibling was KO'd first. Without a
 * refresh the choice would clear as "moot" — the ability doing nothing again. Each stale front
 * entry is re-revealed (reshuffle-on-empty) or, with nothing left to reveal, dropped; an emptied
 * front is shifted and the next front checked, so the helper chains. It stops at the first front
 * that still holds a card, and it never touches an entry without `isDiscardAllowed === false`, so
 * shipped reveal-top-dispose entries keep their no-drift snapshot. Terminates: every pass either
 * stops or shifts one queue entry.
 *
 * A front that just moved up (`isFrontNew`, or any front after a shift here) was parked behind
 * another choice, so its park logged only that it was queued; a still-current entry there logs
 * its reveal now, so every KO-or-keep choice names the card it shows.
 *
 * @param G - Game state (mutated: entry cardIds, the queue, and a possible reshuffle).
 * @param shuffleProvider - ShuffleProvider (ctx.random) for a reshuffle-on-empty.
 * @param isFrontNew - True when the caller just shifted the queue, so the front moved up.
 */
export function refreshStaleKoOrKeepFront(G, shuffleProvider, isFrontNew = false) {
    const queue = G.pendingRevealTopDispose;
    if (queue === undefined) {
        return;
    }
    let isCurrentFrontNew = isFrontNew;
    while (queue.length > 0) {
        const front = queue[0];
        refreshStaleKoOrKeepEntries(G, front, shuffleProvider, isCurrentFrontNew);
        if (front.revealedTops.length > 0) {
            return;
        }
        queue.shift();
        isCurrentFrontNew = true;
    }
}
/**
 * Refreshes every stale KO-or-keep entry of ONE pending reveal-top choice, dropping an entry
 * whose owner has no card left to reveal.
 *
 * @param G - Game state (mutated).
 * @param choice - The pending reveal-top choice whose entries are checked.
 * @param shuffleProvider - ShuffleProvider for a reshuffle-on-empty.
 * @param isFrontNew - True when this choice just moved up to the front of the queue.
 */
function refreshStaleKoOrKeepEntries(G, choice, shuffleProvider, isFrontNew) {
    // why: iterate from the end so splicing a dropped entry never skips the next one.
    for (let index = choice.revealedTops.length - 1; index >= 0; index--) {
        const entry = choice.revealedTops[index];
        if (entry.isDiscardAllowed !== false) {
            continue;
        }
        const ownerDeck = G.playerZones[entry.ownerPlayerID]?.deck ?? [];
        if (ownerDeck.length > 0 && ownerDeck[0] === entry.cardId) {
            // why: a queued entry whose snapshot still matches the top (two copies share one ext id,
            // e.g. S.H.I.E.L.D. Agents) logged no reveal at park; name the card now it is shown.
            if (isFrontNew) {
                pushLog(G, `Player ${entry.ownerPlayerID} reveals the top card of their deck — ${formatCardRef(G.cardDisplayData, entry.cardId)} — KO it or keep it (reveal-top).`, 'neutral', entry.cardId);
            }
            continue;
        }
        const revealed = revealDeckTopForDispose(G, entry.ownerPlayerID, shuffleProvider);
        if (revealed === null) {
            choice.revealedTops.splice(index, 1);
            pushLog(G, `Player ${entry.ownerPlayerID} has no card left to reveal (reveal-top).`, 'blocked');
            continue;
        }
        entry.cardId = revealed.cardId;
        pushLog(G, `Player ${entry.ownerPlayerID} reveals the new top card of their deck — ${formatCardRef(G.cardDisplayData, revealed.cardId)} — KO it or keep it (reveal-top).`, 'neutral', revealed.cardId);
    }
}
/**
 * Parks ONE PendingRevealTopDispose for the active player carrying the revealed snapshot
 * (WP-702 / D-24521), lazily initializing the FIFO queue at the park site (NEVER in
 * Game.setup, so a game that never plays one of these cards carries no new field and both
 * hash oracles stay byte-unchanged). Self-narrates the park so the log records the choice
 * opened; resolveRevealTopDispose narrates each discard/keep. Callers guarantee a non-empty
 * `revealedTops`.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param activePlayerID - The active player who chooses discard-or-keep for every revealed card.
 * @param revealedTops - The non-empty snapshot of revealed `{ ownerPlayerID, cardId }` tops.
 */
function parkRevealTopDispose(G, activePlayerID, revealedTops) {
    if (!G.pendingRevealTopDispose) {
        G.pendingRevealTopDispose = [];
    }
    G.pendingRevealTopDispose.push({
        choiceType: 'reveal-top-dispose',
        playerID: activePlayerID,
        revealedTops,
    });
    // why: self-narrate the park so the log records the choice opened; the resolve move
    // narrates each discard/keep. Outcome `neutral` — nothing has landed yet.
    pushLog(G, `Player ${activePlayerID} revealed ${String(revealedTops.length)} deck top(s) — choose to discard or keep each (reveal-top).`, 'neutral');
}
/**
 * Park handler for the `reveal-top-dispose` hero keyword (WP-702 / D-24521).
 *
 * Gambit's Hypnotic Charm entry 1 + the standalone family ("Reveal the top card of your deck.
 * Discard it or put it back."). Snapshots the ACTIVE player's OWN deck top (reshuffle-on-empty)
 * and parks a block-all PendingRevealTopDispose for the active player; the shared
 * resolveRevealTopDispose then discards it ('discard') or keeps it ('top').
 *
 * // why: reveal-top-dispose carries NO magnitude — the outcome is a single per-card disposition
 * (discard or keep), not a count — so it is in NO_MAGNITUDE_KEYWORDS (a magnitude pre-gate would
 * wrongly safe-skip it). The reveal SNAPSHOTS the deck top (see revealDeckTopForDispose); an empty
 * deck + discard parks nothing and logs the no-op so the player sees why the ability did nothing.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context, narrowed to ShuffleProvider for the reshuffle-on-empty.
 * @param playerID - The active player who played the card.
 * @param _cardId - The played card (unused; the choice carries only the revealed tops).
 * @param _effect - The `{ type: 'reveal-top-dispose' }` descriptor (no magnitude).
 */
function heroEffectRevealTopDispose(G, ctx, playerID, _cardId, _effect) {
    // why: ctx narrows to ShuffleProvider structurally for the reveal-reshuffle (ctx.random),
    // exactly as heroEffectReveal / heroEffectPutHandOnDeckTop do (the WP-005B/008B pattern).
    const revealed = revealDeckTopForDispose(G, playerID, ctx);
    if (revealed === null) {
        // why: reachable no-op — the active player's deck and discard were both empty, so nothing
        // was revealed and no choice is parked. G.messages is hash-excluded (D-24081).
        pushLog(G, `Player ${playerID} had no card to reveal on top of their deck (reveal-top).`, 'blocked');
        return;
    }
    parkRevealTopDispose(G, playerID, [revealed]);
}
/**
 * Park handler for the `reveal-top-dispose-others` hero keyword (WP-702 / D-24521).
 *
 * Gambit's Hypnotic Charm entry 2, [hc:instinct]-gated ("Do the same thing to each other
 * player's deck."). Reveals EACH OTHER seat's deck top (sorted seat order) and parks ONE
 * shared PendingRevealTopDispose for the active player carrying every revealed
 * `{ ownerPlayerID, cardId }`; the shared resolveRevealTopDispose dispositions each by
 * owner+ext_id.
 *
 * // why: the each-other loop skips the ACTIVE player — the base clause (reveal-top-dispose)
 * already handled the active player's OWN deck, and this extension is "each OTHER player's
 * deck" (the Lizard/Melter each-other skip idiom, D-18902 sorted seat order for replay
 * determinism). NO magnitude → in NO_MAGNITUDE_KEYWORDS. No other seat / all exhausted parks
 * nothing and logs the no-op.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context, narrowed to ShuffleProvider for the reshuffle-on-empty.
 * @param playerID - The active player who played the card.
 * @param cardId - The played card (names the source on the D-24639 no-other-deck overlay).
 * @param _effect - The `{ type: 'reveal-top-dispose-others' }` descriptor (no magnitude).
 */
function heroEffectRevealTopDisposeOthers(G, ctx, playerID, cardId, _effect) {
    const revealedTops = [];
    for (const ownerPlayerID of Object.keys(G.playerZones).sort()) {
        // why: each OTHER player's deck — skip the active player (their own deck is the base clause).
        if (ownerPlayerID === playerID) {
            continue;
        }
        const revealed = revealDeckTopForDispose(G, ownerPlayerID, ctx);
        if (revealed !== null) {
            revealedTops.push(revealed);
        }
    }
    if (revealedTops.length === 0) {
        // why: reachable no-op — no OTHER seat had a revealable deck top (a solo game, or every
        // other deck + discard exhausted). Nothing parked. G.messages hash-excluded (D-24081).
        pushLog(G, `Player ${playerID} had no other player's deck top to reveal (reveal-top).`, 'blocked');
        // why: D-24639 — Jeff's ruling: in a 1-player game the printed "each other player's deck"
        // does nothing (like Covering Fire there), but the player must SEE that it resolved. Emit a
        // card-less heroEffectResolved "Hero Ability" overlay line. Guarded on G.notableEvents (the
        // minimal test builder omits it). Hashed, but the core sentinel plays no Gambit (inert there).
        if (Array.isArray(G.notableEvents)) {
            G.notableEvents.push({
                type: 'heroEffectResolved',
                playerId: playerID,
                narrative: composeNoOtherDeckNarrative(resolveTransformCardName(G, cardId)),
            });
        }
        return;
    }
    parkRevealTopDispose(G, playerID, revealedTops);
}
/**
 * Handler for the `reveal-top-dispose-ko` hero keyword (D-24558).
 *
 * co2e Gambit Hypnotic Charm's "[hc:covert]: You may KO the card you revealed from your own
 * deck." The card's first ability (`reveal-top-dispose` + `reveal-top-dispose-others`) has
 * already parked the reveal-top choice(s) — executeHeroEffects runs a card's abilities in
 * order — so this handler reveals nothing itself: it finds the MOST RECENT pending reveal-top
 * choice of the active player holding the active player's OWN revealed top and flags that
 * entry `isKoAllowed`, unlocking the optional 'ko' disposition on resolveRevealTopDispose. The
 * [hc:covert] gate rides this ability's own `abilities[]` entry (the free heroClassMatch
 * condition, D-24354), so an unmet gate never reaches here.
 *
 * // why: newest-first scan — the flag belongs to THIS play's reveal, not an older parked one.
 * "You may" stays optional: the flag only ADDS 'ko' to the existing discard / keep choice. Only
 * the own-deck entry is flagged — the printed KO is "from your own deck", never an opponent's.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Move context (unused; nothing is revealed or shuffled here).
 * @param playerID - The active player who played the card.
 * @param _cardId - The played card (unused).
 * @param _effect - The `{ type: 'reveal-top-dispose-ko' }` descriptor (no magnitude).
 */
function heroEffectRevealTopDisposeKo(G, _ctx, playerID, _cardId, _effect) {
    const queue = G.pendingRevealTopDispose ?? [];
    for (let queueIndex = queue.length - 1; queueIndex >= 0; queueIndex--) {
        const choice = queue[queueIndex];
        if (choice.playerID !== playerID) {
            continue;
        }
        for (const entry of choice.revealedTops) {
            if (entry.ownerPlayerID === playerID) {
                entry.isKoAllowed = true;
                pushLog(G, `Player ${playerID} may also KO the revealed ${formatCardRef(G.cardDisplayData, entry.cardId)} from their own deck (reveal-top).`, 'applied', entry.cardId);
                return;
            }
        }
    }
    // why: reachable no-op — the own-deck reveal parked nothing (deck + discard exhausted), so
    // there is no revealed card to KO. G.messages is hash-excluded (D-24081).
    pushLog(G, `Player ${playerID} had no revealed card of their own to KO (reveal-top).`, 'blocked');
}
/**
 * Park handler for the `reveal-top-may-ko` hero keyword (WP-754 / D-24581).
 *
 * "Reveal the top card of your deck. You may KO it." — vnom Gruesome Feast and mgtg Remove His
 * Spine (fired from Excessive Violence at fight time) and vill Electroshock Therapy (at play).
 * Snapshots the active player's deck top (reshuffle-on-empty) and parks ONE PendingRevealTopDispose
 * whose single entry allows KO and forbids discard, so resolveRevealTopDispose accepts only 'ko'
 * or 'top' (keep it on top).
 *
 * // why: D-24581 — reuse the reveal-top-dispose queue (already modelling a per-card disposition
 * of a revealed deck top) with the omit-when-absent `isDiscardAllowed: false`, instead of a
 * sibling pending type. The entry is pushed here rather than via parkRevealTopDispose because
 * that helper's log says "discard or keep"; this choice is KO-or-keep. No magnitude → in
 * NO_MAGNITUDE_KEYWORDS. An empty deck + discard parks nothing and logs the no-op.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context, narrowed to ShuffleProvider for the reshuffle-on-empty.
 * @param playerID - The active player who played the card.
 * @param _cardId - The played card (unused; the choice carries only the revealed top).
 * @param effect - The `{ type: 'reveal-top-may-ko' }` / `{ type: 'feast' }` descriptor (no magnitude;
 *   World-Spanning Hunger's Feasts carry `nonGreyHeroKoAttack`, WP-859).
 */
function heroEffectRevealTopMayKo(G, ctx, playerID, _cardId, effect) {
    const revealed = revealDeckTopForDispose(G, playerID, ctx);
    if (revealed === null) {
        // why: reachable no-op — deck and discard both empty, nothing revealed, nothing parked.
        // G.messages is hash-excluded (D-24081).
        pushLog(G, `Player ${playerID} had no card to reveal on top of their deck (reveal-top).`, 'blocked');
        return;
    }
    const revealedEntry = {
        ownerPlayerID: revealed.ownerPlayerID,
        cardId: revealed.cardId,
        isKoAllowed: true,
        isDiscardAllowed: false,
    };
    // why: WP-859 / D-24795 — World-Spanning Hunger's "+2 for each non-grey Hero that was KO'd
    // from your deck this turn" counts its own Feasts' KOs, which land in later moves, so each
    // Feast entry carries the reward resolveRevealTopDispose pays on a non-grey Hero KO (RS-2,
    // counted at resolution). Copied only when the descriptor has it, so every other KO-or-keep
    // entry keeps today's shape.
    if (effect.nonGreyHeroKoAttack !== undefined) {
        revealedEntry.nonGreyHeroKoAttack = effect.nonGreyHeroKoAttack;
    }
    if (!G.pendingRevealTopDispose) {
        G.pendingRevealTopDispose = [];
    }
    const isQueuedBehindAnotherChoice = G.pendingRevealTopDispose.length > 0;
    G.pendingRevealTopDispose.push({
        choiceType: 'reveal-top-dispose',
        playerID,
        revealedTops: [revealedEntry],
    });
    if (isQueuedBehindAnotherChoice) {
        // why: the snapshot is provisional while a choice ahead can still change the top (World-
        // Spanning Hunger's three Feasts all snapshot one card); naming it here logged the same card
        // three times. refreshStaleKoOrKeepFront logs the real reveal when this choice moves up.
        pushLog(G, `Player ${playerID} queues another reveal of the top card of their deck — it is shown once the choice before it resolves (reveal-top).`, 'neutral');
        return;
    }
    pushLog(G, `Player ${playerID} revealed ${formatCardRef(G.cardDisplayData, revealed.cardId)} from the top of their deck — KO it or keep it (reveal-top).`, 'neutral', revealed.cardId);
}
/**
 * Finds the LAST queued reveal-three assignment of `playerID`, optionally restricted to one
 * source card.
 *
 * @param G - Game state (read-only here).
 * @param playerID - The player whose entry to find.
 * @param sourceCardId - When given, only an entry opened by this card matches.
 * @returns the newest matching entry, or undefined when none is queued.
 */
function findLastRevealThreeAssignEntry(G, playerID, sourceCardId) {
    const queue = G.pendingRevealThreeAssign ?? [];
    for (let queueIndex = queue.length - 1; queueIndex >= 0; queueIndex--) {
        const entry = queue[queueIndex];
        if (entry.playerID !== playerID) {
            continue;
        }
        if (sourceCardId !== undefined && entry.sourceCardId !== sourceCardId) {
            continue;
        }
        return entry;
    }
    return undefined;
}
/**
 * Park handler for the `reveal-three-assign` hero keyword (WP-753 / D-24580).
 *
 * "Reveal the top three cards of your deck. Draw one of them, discard one, and KO one." —
 * Crystal of Kadavus (vnom) and Interplanetary Visitor (3dtc, dims). Tops the active player's
 * deck up from the discard when it holds fewer than three cards, snapshots the top three and
 * parks a block-all PendingRevealThreeAssign offering all three dispositions; the resolve move
 * assigns one card per call.
 *
 * // why: reveal-three-assign carries NO magnitude (a per-card assignment, not a count), so it
 * is in NO_MAGNITUDE_KEYWORDS — a magnitude pre-gate would wrongly safe-skip it. The ability says
 * "Reveal", so a short deck tops up from the discard first (D-24285), unlike Ruthless Dictator's
 * "Look at" which never reshuffles.
 *
 * // why: an entry already queued for the player means a reveal is still waiting — sibling
 * abilities run synchronously after a block-all park (D-24521 §6), e.g. Steal Abilities
 * re-firing two reveal-three cards in one run. A second snapshot now would capture the SAME
 * deck top, so the handler bumps that entry's repeat counter instead: the repeat reveals a
 * fresh top three once the queued one is assigned.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context, narrowed to ShuffleProvider for the top-up reshuffle.
 * @param playerID - The active player who played the card.
 * @param cardId - The played card (recorded as the entry's source for the repeat marker).
 * @param _effect - The `{ type: 'reveal-three-assign' }` descriptor (no magnitude).
 */
function heroEffectRevealThreeAssign(G, ctx, playerID, cardId, _effect) {
    const queuedEntry = findLastRevealThreeAssignEntry(G, playerID, undefined);
    if (queuedEntry !== undefined) {
        queuedEntry.remainingRepeats += 1;
        pushLog(G, `Player ${playerID} will reveal three more cards after the current reveal is assigned (${formatCardRef(G.cardDisplayData, cardId)}).`, 'neutral', cardId);
        return;
    }
    // why: ctx narrows to ShuffleProvider structurally for the top-up reshuffle (ctx.random),
    // exactly as heroEffectRevealTopDispose does (the WP-005B/008B pattern).
    const revealedCardIds = revealTopThreeForAssign(G, playerID, ctx);
    if (revealedCardIds.length === 0) {
        // why: reachable no-op — the deck and discard were both empty, so nothing was revealed and
        // no choice is parked. G.messages is hash-excluded (D-24081).
        pushLog(G, `Player ${playerID} had no cards to reveal on top of their deck (reveal three).`, 'blocked', cardId);
        return;
    }
    if (!G.pendingRevealThreeAssign) {
        G.pendingRevealThreeAssign = [];
    }
    G.pendingRevealThreeAssign.push({
        choiceType: 'reveal-three-assign',
        playerID,
        sourceCardId: cardId,
        revealedCardIds,
        availableDispositions: buildAllRevealThreeAssignDispositions(),
        remainingRepeats: 0,
    });
    // why: self-narrate the park; the resolve move narrates each draw / discard / KO.
    pushLog(G, `Player ${playerID} revealed the top ${String(revealedCardIds.length)} card(s) of their deck — assign draw / discard / KO (reveal three).`, 'neutral', cardId);
}
/**
 * Handler for the `reveal-three-assign-again` hero keyword (WP-753 / D-24580).
 *
 * Crystal of Kadavus's "[team:venomverse][team:venomverse]: Do this ability again." The Venomverse
 * gate is the ability entry's own requiresTeam condition, so an unmet gate never reaches here.
 *
 * // why: "again" is a COUNTER, not a second park. The card's first ability has already parked
 * its reveal (executeHeroEffects runs a card's abilities in order, synchronously after the park —
 * D-24521 §6); parking a second choice now would snapshot the SAME three cards. Instead this bumps
 * `remainingRepeats` on that entry (newest-first, matched by player AND source card), and the
 * resolve move reveals a fresh top three once the first set is assigned. When the first reveal
 * parked nothing (deck + discard empty) there is no entry, so it runs a fresh reveal.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Move context (forwarded for the fresh-reveal fallback's top-up reshuffle).
 * @param playerID - The active player who played the card.
 * @param cardId - The played card (matches the entry its first ability parked).
 * @param effect - The `{ type: 'reveal-three-assign-again' }` descriptor (no magnitude).
 */
function heroEffectRevealThreeAssignAgain(G, ctx, playerID, cardId, effect) {
    const sourceEntry = findLastRevealThreeAssignEntry(G, playerID, cardId);
    if (sourceEntry === undefined) {
        heroEffectRevealThreeAssign(G, ctx, playerID, cardId, effect);
        return;
    }
    sourceEntry.remainingRepeats += 1;
    pushLog(G, `Player ${playerID} will do ${formatCardRef(G.cardDisplayData, cardId)}'s reveal again on the next three cards.`, 'applied', cardId);
}
/**
 * Park handler for the `do-over` hero keyword (WP-681 / D-24498).
 *
 * Deadpool's "Hey, Can I Get a Do-Over?" — "If this is the first Hero you played this
 * turn, you may discard the rest of your hand and draw four cards." The
 * first-hero-played-this-turn condition already gated this hook (so it runs only when no
 * other Hero has been played this turn); discarding your whole hand is a real cost, so
 * this is a genuine OPTIONAL accept/decline choice, not an auto-resolve. This parks a
 * PendingDoOver onto the FIFO G.pendingDoOverChoices queue; resolveDoOver either discards
 * the entire current hand (through the discardFromHand chokepoint) and draws a FIXED 4, or
 * declines (nothing).
 *
 * // why: D-24498 — the "you may discard the rest of your hand" has a legal empty-hand
 * choice too (decline), but with only the Do-Over card played and nothing else in hand the
 * accept simply discards nothing and draws 4 — still a real choice, so we always park. The
 * park is SILENT (no G.messages line); the resolve move logs the accept/decline outcome.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the discard + draw happen at resolve time).
 * @param playerID - The player who played the Do-Over card.
 * @param _cardId - The played card (unused; the choice carries only the player).
 * @param _effect - The `{ type: 'do-over' }` descriptor (no magnitude).
 */
function heroEffectDoOver(G, _ctx, playerID, _cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: D-24498 — lazy-init at the park site (mirrors the Smash park) — NEVER in
    // Game.setup, so a game that never plays Do-Over carries no new field and both hash
    // oracles stay byte-unchanged. The park is SILENT; resolveDoOver logs the outcome.
    if (!G.pendingDoOverChoices) {
        G.pendingDoOverChoices = [];
    }
    G.pendingDoOverChoices.push({ playerID });
}
/**
 * Park handler for the `covering-fire` hero keyword (WP-719 / D-24541).
 *
 * Hawkeye's "Covering Fire", `[hc:tech]`-gated ("[hc:tech]: Choose one: each other player
 * draws a card or each other player discards a card."). The `[hc:tech]` prefix already parsed
 * as a heroClassMatch condition that gates this hook (so it runs only when another tech Hero
 * was played this turn); this handler parks ONE PendingCoveringFireChoice onto the FIFO
 * G.pendingCoveringFireChoices queue for the ACTIVE player. resolveCoveringFireChoice then
 * applies the chosen 'draw'/'discard' branch to EACH OTHER seat.
 *
 * // why: D-24541 — the choose-one is a genuine either/or (a table-wide draw vs. a table-wide
 * discard, neither dominant in the cooperative ruleset), so it parks an interactive choice for
 * the active player rather than auto-resolving. The park is SILENT (no G.messages line); the
 * resolve move logs the per-seat outcomes. Lazy-init at the park site — NEVER in Game.setup —
 * so a game that never plays Covering Fire carries no new field and both hash oracles stay
 * byte-unchanged.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the draw/discard happen at resolve time).
 * @param playerID - The active player who played the Covering Fire card.
 * @param cardId - The played card, recorded on the choice for log attribution.
 * @param _effect - The `{ type: 'covering-fire' }` descriptor (no magnitude).
 */
function heroEffectCoveringFire(G, _ctx, playerID, cardId, _effect) {
    // why: D-24541 — lazy-init at the park site (mirrors the Do-Over park) — never in Game.setup,
    // so a game that never plays Covering Fire carries no new field and both hash oracles stay
    // byte-unchanged. The park is SILENT; resolveCoveringFireChoice logs the per-seat outcomes.
    // why: D-24661 — both branches only affect "each OTHER player", so with no other seat
    // (a solo match) the choice is meaningless. Log the no-op and park nothing instead of
    // prompting (the Monarch's Decree D-24659 precedent).
    const hasOtherSeat = Object.keys(G.playerZones).some((seat) => seat !== playerID);
    if (!hasOtherSeat) {
        pushLog(G, `${formatCardRef(G.cardDisplayData, cardId)}: there is no other player to draw or discard (Covering Fire).`, 'neutral');
        return;
    }
    if (!G.pendingCoveringFireChoices) {
        G.pendingCoveringFireChoices = [];
    }
    G.pendingCoveringFireChoices.push({ playerID, sourceCardId: cardId });
}
/**
 * Park handler for the `spinning-cyclone` hero keyword (WP-795 / D-24664).
 *
 * core Storm's "Spinning Cyclone" ("You may move a Villain to a new city space. Rescue any
 * Bystanders captured by that Villain. (If you move a Villain to a city space that already
 * has Villain, swap them.)"). With at least one Villain in the City, this handler parks ONE
 * PendingMoveVillainChoice onto the FIFO G.pendingMoveVillainChoices queue for the ACTIVE
 * player. resolveMoveVillainChoice then moves (or swaps) the chosen Villain and rescues only
 * its Bystanders, or declines. With no City Villain there is nothing to move: the handler logs
 * a blocked line and parks nothing.
 *
 * // why: D-24664 — the move is optional and the pick (which Villain, which space) is a
 * genuine choice, so it parks an interactive choice for the active player (D-24284) rather
 * than auto-resolving. The park is SILENT; the resolve move logs the move, the rescue or the
 * decline.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the move happens at resolve time; no randomness).
 * @param playerID - The active player who played the Spinning Cyclone card.
 * @param cardId - The played card, recorded on the choice for log attribution.
 * @param _effect - The `{ type: 'spinning-cyclone' }` descriptor (no magnitude).
 */
function heroEffectSpinningCyclone(G, _ctx, playerID, cardId, _effect) {
    let hasCityVillain = false;
    for (const citySpace of G.city) {
        if (citySpace !== null) {
            hasCityVillain = true;
            break;
        }
    }
    if (!hasCityVillain) {
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} found no Villain in the City to move.`, 'blocked', cardId);
        return;
    }
    // why: D-24664 — lazy-init at the park site (mirrors the Covering Fire park) — never in
    // Game.setup, so a game that never plays Spinning Cyclone carries no new field and both hash
    // oracles stay byte-unchanged. The park is SILENT; resolveMoveVillainChoice logs the outcome.
    if (!G.pendingMoveVillainChoices) {
        G.pendingMoveVillainChoices = [];
    }
    G.pendingMoveVillainChoices.push({ playerID, sourceCardId: cardId });
}
/**
 * Handler for the `here-hold-this` hero keyword (WP-683 / D-24500).
 *
 * Deadpool's "Here, Hold This for a Second" — "A Villain of your choice captures a
 * Bystander." The active player picks a City Villain that captures the top-supply
 * Bystander (reusing `attachBystanderToVillain`). Resolves by cardinality:
 * empty Bystander supply → a logged no-op; 0 City Villains → the Mastermind captures
 * (universal-rules-v23 §capture); exactly 1 Villain → auto-attach; ≥2 → park an
 * ACTIVE-scoped single-seat PendingSeatChoice (kind 'here-hold-this') so the player
 * picks which Villain, resolved by resolveSeatChoice.
 *
 * // why: WP-683 / D-24500 — the empty-supply check is FIRST, so with no Bystander to
 * capture the ability is a clean no-op regardless of the City (never a Mastermind
 * capture of a non-existent Bystander, never a parked choice with nothing to award).
 *
 * // why: the ≥2 park uses parkSeatChoice WITHOUT the framework stage ride (events
 * undefined) — the sole addressed seat is the ACTIVE player, already the currentPlayer
 * in the (empty) playTurn stage, so they can submit the global resolveSeatChoice move
 * with no setActivePlayers admission (which is only needed to admit NON-ACTIVE seats).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the pick is active-scoped; no stage ride, no randomness).
 * @param playerID - The player who played the card (the sole addressed seat).
 * @param cardId - The played card (recorded for the neutral park log).
 * @param _effect - The `{ type: 'here-hold-this' }` descriptor (no magnitude).
 */
function heroEffectHereHoldThis(G, _ctx, playerID, cardId, _effect) {
    // why: empty Bystander supply → nothing to capture (a clean no-op). Checked first
    // so neither the Mastermind fallback nor a parked pick fires with no Bystander.
    if (G.piles.bystanders.length === 0) {
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} could not have a Villain capture a Bystander — the Bystander supply is empty.`, 'blocked', cardId);
        return;
    }
    const targets = buildHereHoldThisTargets(G);
    // why: WP-683 / D-24500 — universal-rules-v23 §capture: with no Villain in the City,
    // the MASTERMIND captures the Bystander instead.
    if (targets.length === 0) {
        captureBystanderToMastermind(G);
        return;
    }
    // why: exactly 1 City Villain → auto-attach with no prompt (mandatory-if-able).
    if (targets.length === 1) {
        attachBystanderToCityVillain(G, targets[0].cityIndex);
        return;
    }
    // why: ≥2 City Villains → park an ACTIVE-scoped pick; the current player chooses which
    // Villain captures. parkSeatChoice with events undefined (no stage ride) — the active
    // player is already the currentPlayer and needs no admission.
    parkSeatChoice(G, undefined, buildHereHoldThisChoice(G, playerID, targets));
    pushLog(G, `Player ${playerID} must choose which Villain captures a Bystander (${formatCardRef(G.cardDisplayData, cardId)}).`, 'neutral', cardId);
}
/**
 * Handler for the `random-acts` hero keyword (WP-683 / D-24500).
 *
 * Deadpool's "Random Acts of Unkindness" — "You may gain a Wound to your hand. Then
 * each player passes a card from their hand to the player on their left." Two steps:
 *
 *   Step 1 (this handler): if the Wound supply is non-empty, park an ACTIVE-scoped
 *   optional gain-Wound-to-HAND choice (kind 'random-acts-wound'); resolveSeatChoice
 *   gains the Wound (or declines) then CHAINS step 2.
 *   Step 2 (chained in resolveSeatChoice, or here when the Wound supply is empty): the
 *   simultaneous multi-seat pass-left (kind 'random-acts-pass-left').
 *
 * // why: WP-683 / D-24500 — the wound step is parked WITHOUT the stage ride (active
 * player only); the pass step (step 2) needs the stage ride to admit the NON-ACTIVE
 * seats, so it is parked WITH the move's events — done in resolveSeatChoice's chain
 * (which has the live events/ctx) or, when there is no wound step, here.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - The move-context wrapper (read for events + ctx.playOrder).
 * @param playerID - The player who played the card (the active player).
 * @param cardId - The played card (recorded for the neutral park log).
 * @param _effect - The `{ type: 'random-acts' }` descriptor (no magnitude).
 */
function heroEffectRandomActs(G, ctx, playerID, cardId, _effect) {
    // why: WP-683 / D-24500 — if the Wound supply can offer a Wound, the optional
    // gain-to-hand is a real choice, so park it (it chains the pass on resolve). The wound
    // choice is active-scoped, so no stage ride (events undefined).
    if (G.piles.wounds.length > 0) {
        // why: D-24678 — offer Diving Block's "draw a card instead" when the player can reveal
        // one (in hand or played this turn, D-24651).
        const canRevealDivingBlock = countRevealableDivingBlockCopies(G, playerID) > 0;
        parkSeatChoice(G, undefined, buildRandomActsWoundChoice(playerID, canRevealDivingBlock));
        pushLog(G, `Player ${playerID} may gain a Wound to their hand before the pass (${formatCardRef(G.cardDisplayData, cardId)}).`, 'neutral', cardId);
        return;
    }
    // why: no Wound to gain (empty supply) → skip step 1 and go straight to the pass. The
    // pass needs the stage ride to admit non-active seats, so it uses the move's events.
    const events = ctx.events;
    const bareCtx = ctx.ctx;
    const playOrder = bareCtx?.playOrder ?? Object.keys(G.playerZones).sort();
    const passChoice = buildPassLeftChoice(G, playOrder);
    if (passChoice === undefined) {
        // why: solo (playOrder < 2) or no seat holds a card, and no Wound to gain — the
        // ability does nothing; log the reason so it is not a silent no-op.
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} did nothing — the Wound supply is empty and there is no other player to pass to.`, 'blocked', cardId);
        return;
    }
    parkSeatChoice(G, events, passChoice);
    pushLog(G, `Each player must choose a card to pass to the player on their left (${formatCardRef(G.cardDisplayData, cardId)}).`, 'neutral', cardId);
}
/**
 * Park handler for the `optional-ko-shield-officer` hero keyword (WP-681 / D-24498).
 *
 * Nick Fury's "Battlefield Promotion" — "You may KO a [team:shield] Hero from your hand
 * or discard pile. If you do, you may gain a S.H.I.E.L.D. Officer to your hand." Reuses the
 * shipped optional-ko-reward pending queue: it parks a PendingOptionalKoReward with
 * koZones ['hand','discard'] (no in-play KO), koTeamFilter 'shield' (the KO target must be
 * a S.H.I.E.L.D. Hero — the Officer token itself counts), and rewardType 'gain-officer-hand'
 * (a new reward variant dispatched by resolveOptionalKoReward). The officer reward is pure
 * upside (a free Officer in hand), so it auto-resolves on KO (the reveal-from-hand
 * precedent) rather than parking a second choice; an empty Officer supply no-ops the reward.
 *
 * KO source = the hand ∪ discard S.H.I.E.L.D. Heroes ONLY. 0 eligible → a logged no-op that
 * parks nothing (never a throw), so the player sees why the ability did nothing.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the KO + reward happen at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the resolve-move log).
 * @param _effect - The `{ type: 'optional-ko-shield-officer' }` descriptor (no magnitude).
 */
function heroEffectOptionalKoShieldOfficer(G, _ctx, playerID, cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: D-24498 — eligible = hand ∪ discard S.H.I.E.L.D. Heroes ONLY (the printed
    // "[team:shield] Hero from your hand or discard pile"). 0 eligible → skipped no-op + a log
    // line so the player sees why the ability did nothing (mirrors the optional-ko-hand-discard
    // empty branch). The reward still requires a KO, so no eligible target means no reward.
    let eligibleCount = 0;
    for (const zone of [playerZones.hand, playerZones.discard]) {
        for (const zoneCardId of zone) {
            if (cardCountsAsShieldHero(G, zoneCardId)) {
                eligibleCount += 1;
            }
        }
    }
    if (eligibleCount === 0) {
        pushLog(G, `Player ${playerID} could not KO a S.H.I.E.L.D. Hero for ${formatCardRef(G.cardDisplayData, cardId)}'s ability — their hand and discard pile hold no S.H.I.E.L.D. Hero.`);
        return;
    }
    // why: D-24498 — park into the SHARED optional-ko-reward queue with the S.H.I.E.L.D.
    // team filter + the gain-officer-hand reward; the KO source is hand ∪ discard only.
    // Lazy-init the queue (mirrors the reward park); the park is SILENT.
    if (!G.pendingOptionalKoRewards) {
        G.pendingOptionalKoRewards = [];
    }
    G.pendingOptionalKoRewards.push({
        playerID,
        rewardType: 'gain-officer-hand',
        rewardMagnitude: 0,
        sourceCardId: cardId,
        koZones: ['hand', 'discard'],
        koTeamFilter: 'shield',
    });
}
/**
 * Park handler for the `ko-up-to-shield-hand-discard` hero keyword (WP-826 / D-24759).
 *
 * Agent X-13's Mobilize for War: "KO up to two [team:shield] Heroes from your hand and/or
 * discard pile." Reuses the shipped optional-ko-reward pending queue: it parks one no-reward
 * PendingOptionalKoReward per KO the card allows (koZones ['hand','discard'], koTeamFilter
 * 'shield'), capped at the number of eligible S.H.I.E.L.D. Heroes, so "up to" is a sequence
 * of independent optional choices (decline any of them). A later line of the same card can
 * attach a per-KO reward to these entries (see heroEffectAttackPerKoThisWay).
 *
 * 0 eligible → a logged no-op that parks nothing (never a throw).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the KOs happen at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the resolve-move log and the reward upgrade).
 * @param effect - The effect descriptor; its magnitude is the printed KO cap ("up to N").
 */
function heroEffectKoUpToShieldHandDiscard(G, _ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    let eligibleCount = 0;
    for (const zone of [playerZones.hand, playerZones.discard]) {
        for (const zoneCardId of zone) {
            if (cardCountsAsShieldHero(G, zoneCardId)) {
                eligibleCount += 1;
            }
        }
    }
    if (eligibleCount === 0) {
        pushLog(G, `Player ${playerID} could not KO a S.H.I.E.L.D. Hero for ${formatCardRef(G.cardDisplayData, cardId)}'s ability — their hand and discard pile hold no S.H.I.E.L.D. Hero.`);
        return;
    }
    // why: D-24759 — one entry per KO the card allows, but never more than there are eligible
    // S.H.I.E.L.D. Heroes right now: a KO never adds a S.H.I.E.L.D. Hero to hand or discard, so a
    // surplus entry could only ever be declined. The magnitude pre-gate already confirmed N >= 1.
    const koCap = effect.magnitude ?? 1;
    const entryCount = Math.min(koCap, eligibleCount);
    if (!G.pendingOptionalKoRewards) {
        G.pendingOptionalKoRewards = [];
    }
    for (let entryIndex = 0; entryIndex < entryCount; entryIndex++) {
        G.pendingOptionalKoRewards.push({
            playerID,
            rewardType: 'none',
            rewardMagnitude: 0,
            sourceCardId: cardId,
            koZones: ['hand', 'discard'],
            koTeamFilter: 'shield',
        });
    }
}
/**
 * Handler for the `attack-per-ko-this-way` hero keyword (WP-826 / D-24759).
 *
 * Mobilize for War's second line: "[keyword:Savior]: For each card KO'd this way, you get
 * +1[icon:attack]." The KOs belong to the card's first line, which parked them as no-reward
 * optional-ko entries a moment earlier in the same play. This handler gives each of those
 * still-pending entries an attack reward of `magnitude`, so every KO the player actually makes
 * grants +N at resolve time and a declined KO grants nothing. The line's own gate (Savior)
 * decides whether this runs at all: without it, the entries stay no-reward.
 *
 * Only entries parked by THIS card for THIS player with no reward are upgraded. The block-all
 * guard stops another card being played while the queue is non-empty, so every matching entry
 * belongs to this play.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused.
 * @param playerID - The player who played the card.
 * @param cardId - The played card whose earlier line parked the KO entries.
 * @param effect - The effect descriptor; its magnitude is the Attack per card KO'd.
 */
function heroEffectAttackPerKoThisWay(G, _ctx, playerID, cardId, effect) {
    const attackPerKo = effect.magnitude ?? 1;
    let upgradedCount = 0;
    // why: D-24759 — every matching entry is this play's: the block-all guard stops a card being
    // played while the queue is non-empty, and line 0 parked these a moment earlier in the same
    // executeHeroEffects call (hooks run in ability-line order). A declined KO grants nothing,
    // because the reward is dispatched only on an actual KO.
    for (const entry of G.pendingOptionalKoRewards ?? []) {
        if (entry.playerID === playerID && entry.sourceCardId === cardId && entry.rewardType === 'none') {
            entry.rewardType = 'attack';
            entry.rewardMagnitude = attackPerKo;
            upgradedCount += 1;
        }
    }
    // why: D-24759 — when the KO line parked nothing (no S.H.I.E.L.D. Hero to KO), there is no
    // card to reward; say so, so a met Savior gate does not read as a missing grant.
    if (upgradedCount === 0) {
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} gives +${attackPerKo} Attack for each card KO'd this way, but no card can be KO'd.`, 'neutral', cardId);
    }
}
/**
 * Park handler for the `optional-ko-shield-played-rescue` hero keyword (WP-826 / D-24759).
 *
 * Steve Rogers' Reassign to Civilian Duty: "[team:shield][team:shield][team:shield]: You may KO
 * a [team:shield] Hero that you played this turn. If you do, rescue a Bystander." The triple
 * S.H.I.E.L.D. gate is the hook's condition (D-24563). This parks one PendingOptionalKoReward on
 * the shared queue with koZones ['inPlay'] (cards played this turn only), koTeamFilter 'shield'
 * and the existing rescue reward, dispatched by resolveOptionalKoReward only on an actual KO.
 * The card itself is a S.H.I.E.L.D. Hero played this turn, so it is a legal target too; a KO'd
 * played card keeps the Attack it already produced (D-24442).
 *
 * 0 eligible → a logged no-op that parks nothing (never a throw).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the KO + rescue happen at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the resolve-move log).
 * @param effect - The effect descriptor; its magnitude is the number of Bystanders rescued.
 */
function heroEffectOptionalKoShieldPlayedRescue(G, _ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    let eligibleCount = 0;
    for (const inPlayCardId of playerZones.inPlay) {
        if (cardCountsAsShieldHero(G, inPlayCardId)) {
            eligibleCount += 1;
        }
    }
    if (eligibleCount === 0) {
        pushLog(G, `Player ${playerID} could not KO a S.H.I.E.L.D. Hero for ${formatCardRef(G.cardDisplayData, cardId)}'s ability — they played no S.H.I.E.L.D. Hero this turn.`);
        return;
    }
    if (!G.pendingOptionalKoRewards) {
        G.pendingOptionalKoRewards = [];
    }
    G.pendingOptionalKoRewards.push({
        playerID,
        rewardType: 'rescue',
        rewardMagnitude: effect.magnitude ?? 1,
        sourceCardId: cardId,
        koZones: ['inPlay'],
        koTeamFilter: 'shield',
    });
}
/**
 * Handler for the `optional-play-villain-top` hero keyword (WP-663 / D-24474).
 *
 * Shadowed Thoughts' "[hc:covert]: You may play the top card of the Villain Deck. If you
 * do, +2 Attack." Parks an interactive PendingPlayVillainTopChoice; the +Attack reward is
 * granted on resolve (resolvePlayVillainTopChoice), NOT at play time — playing the top
 * Villain-Deck card has a real downside (a Villain enters the city, or a Master Strike /
 * Scheme Twist fires), so the player genuinely chooses (contrast the auto-resolving
 * reveal-from-hand). The covert gate already gated the hook, so this runs only when the
 * Covert synergy is met.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the reward + villain-deck play happen at resolve time).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded for the resolve-move log).
 * @param effect - The effect descriptor carrying the Attack reward magnitude.
 */
function heroEffectOptionalPlayVillainTop(G, _ctx, playerID, cardId, effect) {
    // why: WP-663 / D-24474 — lazy-init at the park site (mirrors the optional-ko-reward
    // park) — NEVER in Game.setup, so a game that never parks one carries no new G field. The
    // park is SILENT (no G.messages line); the resolve move logs the accept/decline outcome.
    if (!G.pendingPlayVillainTopChoices) {
        G.pendingPlayVillainTopChoices = [];
    }
    G.pendingPlayVillainTopChoices.push({
        playerID,
        cardId,
        // why: the parser emits magnitude 2 from the [keyword:optional-play-villain-top:2]
        // marker (validated by the executeSingleEffect pre-gate — this keyword is NOT in
        // NO_MAGNITUDE_KEYWORDS); the ?? 2 is a defensive default for a direct unit dispatch.
        attackReward: effect.magnitude ?? 2,
    });
}
/**
 * Handler for the `ko-wound-reward` hero keyword (WP-382 / D-24183).
 *
 * The auto-resolving, Wound-restricted variant of `optional-ko-reward` (the
 * Healing Factor family, "you may KO a Wound from your hand or discard pile; if
 * you do, <reward>"). It immediately KOs one Wound to `G.ko` — preferring hand,
 * else discard — and grants the reward by REUSING `executeSingleEffect`; with no
 * Wound in either zone it logs a no-op (D-24017) and returns.
 *
 * The player choice / decline is intentionally NOT modeled (unlike
 * `optional-ko-reward`): a Wound is a fungible dead card and KO-plus-reward is
 * strictly beneficial, so an auto-resolve captures optimal play. Moves/effects
 * never throw.
 *
 * @param G - Game state (mutated: KOs a Wound, grants the reward).
 * @param ctx - Move context; carries `ctx.random` for the draw reward's reshuffle.
 * @param playerID - The player who played the card.
 * @param cardId - The source hero card's ext_id (passed to the reward executor).
 * @param effect - The descriptor carrying `rewardType` + `magnitude`.
 */
function heroEffectKoWoundReward(G, ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: D-24183 — KO a Wound, hand first (removes the currently-held Wound),
    // else discard. The KO target is filtered to WOUND_EXT_ID — a valuable Hero is
    // never KO'd (the reason this family could not reuse the KO-any-card keyword).
    let targetZone = null;
    if (playerZones.hand.includes(WOUND_EXT_ID)) {
        targetZone = 'hand';
    }
    else if (playerZones.discard.includes(WOUND_EXT_ID)) {
        targetZone = 'discard';
    }
    if (targetZone === null) {
        // why: D-24017 — no Wound to KO means the optional effect does nothing; log
        // the no-op so the player and the replay inspector can see why the ability
        // granted no reward.
        pushLog(G, `Player ${playerID} had no Wound in hand or discard pile to KO for a hero ability, so no reward was granted.`);
        return;
    }
    // why: defensive — the parser only emits a seeded rewardType, but an unseeded
    // reward here is a logged no-op (no reward executor exists for it).
    const rewardType = effect.rewardType;
    if (rewardType === undefined || !KO_WOUND_REWARD_SEEDED_REWARDS.has(rewardType)) {
        pushLog(G, `Player ${playerID} played a hero ability whose KO-a-Wound reward is not yet supported, so it was skipped.`);
        return;
    }
    // why: D-24183 — remove exactly one Wound from the chosen zone and KO it.
    // moveCardFromZone removes the first matching WOUND_EXT_ID; koCard appends it.
    const moveResult = moveCardFromZone(playerZones[targetZone], [], WOUND_EXT_ID);
    if (!moveResult.found) {
        return;
    }
    playerZones[targetZone] = moveResult.from;
    G.ko = koCard(G.ko, WOUND_EXT_ID);
    recordOwnedCardKoed(G, playerID, WOUND_EXT_ID, targetZone);
    pushLog(G, `Player ${playerID} KO'd a Wound from their ${targetZone} via a hero ability.`);
    // why: D-24183 — THEN grant the reward by REUSING the existing executor — no
    // re-implementation of draw / attack / recruit. `ctx` carries `ctx.random` for
    // the draw reward's deck-exhaustion reshuffle.
    executeSingleEffect(G, ctx, playerID, cardId, {
        type: rewardType,
        magnitude: effect.magnitude ?? 1,
    });
}
/**
 * Handler for the `ko-wound` hero keyword (WP-721 / D-24542).
 *
 * The REWARDLESS sibling of `ko-wound-reward` (the "You may KO a Wound from your
 * hand or discard pile." family with NO "if you do, <reward>" clause — X-23's
 * Healing Factor Genome, Peter Parker's Hot Bowl of Soup). It immediately KOs one
 * Wound to `G.ko` — preferring hand, else discard — and grants nothing further.
 * With no Wound in either zone it logs a no-op (D-24017) and returns.
 *
 * Auto-resolves — no `Pending*`, no resolve move, no `UIState` projection, no
 * client prompt (mirrors `ko-wound-reward`). KO'ing a Wound is a fungible dead
 * card and pure deck-thinning upside, so an auto-resolve captures optimal play.
 * Moves/effects never throw.
 *
 * @param G - Game state (mutated: KOs one Wound).
 * @param _ctx - Move context (unused — there is no reward that needs `ctx.random`).
 * @param playerID - The player who played the card.
 * @param _cardId - The source hero card's ext_id (unused — no reward to attribute).
 * @param _effect - The descriptor (carries no magnitude / reward for this keyword).
 */
function heroEffectKoWound(G, _ctx, playerID, _cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: D-24542 — KO a Wound, hand first (removes the currently-held Wound), else
    // discard. The KO target is filtered to WOUND_EXT_ID — a valuable Hero is never
    // KO'd (the same reason the reward variant could not reuse the KO-any-card keyword).
    let targetZone = null;
    if (playerZones.hand.includes(WOUND_EXT_ID)) {
        targetZone = 'hand';
    }
    else if (playerZones.discard.includes(WOUND_EXT_ID)) {
        targetZone = 'discard';
    }
    if (targetZone === null) {
        // why: D-24017 — no Wound to KO means the optional effect does nothing; log the
        // no-op so the player and the replay inspector can see the ability found no target.
        pushLog(G, `Player ${playerID} had no Wound in hand or discard pile to KO for a hero ability, so nothing was KO'd.`);
        return;
    }
    // why: D-24542 — remove exactly one Wound from the chosen zone and KO it.
    // moveCardFromZone removes the first matching WOUND_EXT_ID; koCard appends it.
    // There is NO reward dispatch (the rewardless variant), so unlike ko-wound-reward
    // this handler ends here.
    const moveResult = moveCardFromZone(playerZones[targetZone], [], WOUND_EXT_ID);
    if (!moveResult.found) {
        return;
    }
    playerZones[targetZone] = moveResult.from;
    G.ko = koCard(G.ko, WOUND_EXT_ID);
    recordOwnedCardKoed(G, playerID, WOUND_EXT_ID, targetZone);
    pushLog(G, `Player ${playerID} KO'd a Wound from their ${targetZone} via a hero ability.`);
}
/**
 * Park handler for the `optional-put-bottom-hq` hero keyword.
 *
 * Checks whether there are any cards in the HQ. If yes, parks a
 * `PendingOptionalPutBottomHQ` on `G.pendingOptionalPutBottomHQ[]` (lazy-init).
 * If the HQ is empty, logs a no-op message and returns without touching the queue.
 *
 * The card move itself happens at resolve time (resolveOptionalPutBottomHQ), NOT
 * here — the player must first choose which HQ card (if any) to move to the deck bottom.
 */
function heroEffectOptionalPutBottomHq(G, _ctx, playerID, cardId, _effect) {
    // why: check if there are any cards in the HQ to move. HQ is the shared board zone
    // (not per-player). If empty, the ability has no valid targets.
    const hqZone = G.hq;
    const eligibleCards = hqZone.filter(slot => slot !== null).length;
    if (eligibleCards === 0) {
        pushLog(G, `Player ${playerID} could not move a card from the HQ — the HQ is empty, so no card was moved.`);
        return;
    }
    // why: lazy-init at the park site (mirrors optional-ko-reward pattern) — NEVER in
    // Game.setup. The park itself is SILENT (no G.messages line); the move is silent too.
    if (!G.pendingOptionalPutBottomHQ) {
        G.pendingOptionalPutBottomHQ = [];
    }
    G.pendingOptionalPutBottomHQ.push({
        playerID,
        sourceCardId: cardId,
    });
}
/**
 * Park handler for the `put-bottom-hq-icon-reward` hero keyword (D-24133).
 *
 * The MANDATORY, icon-reward sibling of `heroEffectOptionalPutBottomHq`. The printed
 * "Put a card from the HQ on the bottom of the Hero Deck. If that card had a recruit
 * icon, you get +N recruit. If that card had an attack icon, you get +N attack."
 * (Wonder Man's Absorb Ambient Power). Parks onto the SAME single-card queue
 * (`G.pendingOptionalPutBottomHQ`) with `mandatory: true` (no Decline) and the reward
 * magnitude, so resolveOptionalPutBottomHQ applies the icon reward after the move.
 *
 * If the HQ is empty the mandatory choice cannot be made — logged no-op, parks nothing.
 */
function heroEffectPutBottomHqIconReward(G, _ctx, playerID, cardId, effect) {
    const hqZone = G.hq;
    const eligibleCards = hqZone.filter(slot => slot !== null).length;
    if (eligibleCards === 0) {
        pushLog(G, `Player ${playerID} could not move a card from the HQ — the HQ is empty, so no card was moved.`);
        return;
    }
    // why: the reward magnitude rides on the descriptor magnitude ([keyword:put-bottom-hq-icon-reward:N]);
    // default to 3 (Absorb Ambient Power's printed value) if a magnitude somehow did not parse.
    const iconRewardMagnitude = effect.magnitude !== undefined && effect.magnitude > 0 ? effect.magnitude : 3;
    if (!G.pendingOptionalPutBottomHQ) {
        G.pendingOptionalPutBottomHQ = [];
    }
    G.pendingOptionalPutBottomHQ.push({
        playerID,
        sourceCardId: cardId,
        mandatory: true,
        iconRewardMagnitude,
    });
}
/**
 * Park handler for the `put-any-number-bottom-hq` hero keyword (D-24132).
 *
 * The MULTI-select sibling of `heroEffectOptionalPutBottomHq`. Checks whether there are any
 * cards in the HQ. If yes, parks a `PendingPutAnyNumberBottomHQ` on
 * `G.pendingPutAnyNumberBottomHQ[]` (lazy-init), recording any trailing "Then you get
 * Empowered by [classes]" grant parsed onto the effect (applied AFTER the moves at resolve
 * time). If the HQ is empty, logs a no-op message and returns without touching the queue.
 *
 * The card moves and the Empowered grant both happen at resolve time
 * (resolvePutAnyNumberBottomHQ), NOT here — the player must first choose which HQ cards (if
 * any) to move to the deck bottom.
 */
function heroEffectPutAnyNumberBottomHq(G, _ctx, playerID, cardId, effect) {
    // why: check if there are any cards in the HQ to move. HQ is the shared board zone
    // (not per-player). If empty, the ability has no valid targets.
    const hqZone = G.hq;
    const eligibleCards = hqZone.filter(slot => slot !== null).length;
    if (eligibleCards === 0) {
        pushLog(G, `Player ${playerID} could not move cards from the HQ — the HQ is empty, so no cards were moved.`);
        return;
    }
    // why: lazy-init at the park site (mirrors optional-put-bottom-hq) — NEVER in Game.setup.
    // The park itself is SILENT (no G.messages line); the moves + Empowered grant are logged at
    // resolve time. empoweredClasses is recorded only when the parsed effect carries a non-empty
    // tail (omit-when-empty keeps Empyreal Force / Colliding-Dreams-line-1 entries minimal).
    if (!G.pendingPutAnyNumberBottomHQ) {
        G.pendingPutAnyNumberBottomHQ = [];
    }
    const empoweredClasses = effect.empoweredClasses;
    G.pendingPutAnyNumberBottomHQ.push({
        playerID,
        sourceCardId: cardId,
        ...(empoweredClasses !== undefined && empoweredClasses.length > 0 ? { empoweredClasses } : {}),
    });
}
/**
 * Park handler for the `return-zero-cost-discard` hero keyword (D-24139).
 *
 * The printed "Return a 0-cost card from your discard pile to your hand"
 * (Black Knight's Defend the Weak). Checks whether the chooser's discard pile
 * holds at least one 0-cost card (the shared isZeroCostCard predicate via
 * getEligibleZeroCostDiscardCards). If yes, parks a PendingReturnZeroCostDiscard
 * on `G.pendingReturnZeroCostDiscard[]` (lazy-init). If no eligible card exists,
 * logs a no-op message and returns without touching the queue.
 *
 * The card move itself happens at resolve time (resolveReturnZeroCostDiscard),
 * NOT here — the player must first choose which eligible card to take back.
 * The choice is mandatory (no decline): the printed text has no "you may".
 */
function heroEffectReturnZeroCostDiscard(G, _ctx, playerID, cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const eligibleCards = getEligibleZeroCostDiscardCards(G, playerID);
    if (eligibleCards.length === 0) {
        pushLog(G, `Player ${playerID} could not return a 0-cost card from their discard pile — it holds no 0-cost card, so nothing was returned.`);
        return;
    }
    // why: lazy-init at the park site (mirrors optional-put-bottom-hq) — NEVER in
    // Game.setup. The park itself is SILENT (no G.messages line); the return is
    // logged at resolve time.
    if (!G.pendingReturnZeroCostDiscard) {
        G.pendingReturnZeroCostDiscard = [];
    }
    G.pendingReturnZeroCostDiscard.push({
        playerID,
        sourceCardId: cardId,
    });
}
/**
 * Park handler for the `discard-to-play` hero card cost (WP-383 / D-24184).
 *
 * The printed "To play this card, you must discard a card from your hand"
 * (Cyclops Determination/Optic Blast + siblings). Parks a mandatory
 * PendingDiscardToPlay on `G.pendingDiscardToPlay[]` (lazy-init) with
 * `remaining` set to the cost magnitude, resolved by resolveDiscardToPlay.
 *
 * // why: payability (hand holds ≥ magnitude cards after the played card moved
 * to inPlay) is pre-guaranteed by the D-24185 pre-commit precondition in
 * playCard — an unpayable play never commits, so this handler never runs
 * without enough cards. The eligibility re-check below is defensive only (a
 * fail-closed against a future play path that skips the precondition); it must
 * NOT be relied on to prevent the base-power leak — that is the precondition's
 * job, before commit. The card move itself happens at resolve time.
 */
function heroEffectDiscardToPlay(G, _ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const remaining = effect.magnitude ?? 1;
    if (getEligibleDiscardToPlayCards(G, playerID).length < remaining) {
        // why: defensive fail-closed — the precondition should have blocked this,
        // so reaching here means an unexpected play path; log and skip the park
        // rather than create an unpayable pending choice that would freeze the turn.
        pushLog(G, `Player ${playerID} could not pay the discard-to-play cost — not enough cards in hand, so no discard was required.`);
        return;
    }
    // why: lazy-init at the park site (mirrors return-zero-cost-discard) — NEVER
    // in Game.setup. The park itself is SILENT; each discard is logged at resolve.
    if (!G.pendingDiscardToPlay) {
        G.pendingDiscardToPlay = [];
    }
    G.pendingDiscardToPlay.push({
        playerID,
        sourceCardId: cardId,
        remaining,
    });
}
/**
 * Park handler for the `victory-villain-attack` hero keyword (WP-285 / D-24067).
 *
 * Checks whether the player has at least one eligible villain in their victory pile
 * at play time. If yes, parks a `PendingVictoryPileCardPick` on
 * `G.pendingVictoryPileCardPick[]` (lazy-init). If no eligible villain exists, logs
 * a hollow-style no-op message (D-24067) and returns without touching the queue.
 *
 * The attack grant itself happens at resolve time (resolveVictoryPileCardPick), NOT
 * here — the player must first pick which villain to use.
 */
function heroEffectVictoryVillainAttack(G, _ctx, playerID, _cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const eligibleVillains = getEligibleVictoryVillains(G, playerID);
    if (eligibleVillains.length === 0) {
        // why: no eligible villains in victory pile at play time — no pending pick parked, logged as no-op (D-24067)
        pushLog(G, `Player ${playerID} played a victory-villain-attack hero ability but had no eligible villains in their victory pile — no pick was queued.`);
        return;
    }
    // why: lazy-init at the park site (never in Game.setup); absent field = no pending pick. Mirrors pendingOptionalKoRewards (D-24019).
    if (!G.pendingVictoryPileCardPick) {
        G.pendingVictoryPileCardPick = [];
    }
    G.pendingVictoryPileCardPick.push({ rewardType: 'attack', playerID });
}
/**
 * Park handler for the `draw-or-empowered` hero keyword (WP-286 / D-24069).
 *
 * Parks a `PendingDrawOrEmpowered` on `G.pendingDrawOrEmpowered[]` (lazy-init) carrying the
 * player and the empowered hero class the parser extracted from the "Empowered by [hc:X]" tail.
 * The draw or the empowered grant happens at resolve time (resolveDrawOrEmpowered), NOT here —
 * the player (or bot) must first pick which half of the printed "Choose one" to take.
 *
 * A descriptor with no empoweredClass (should never happen post-parse — the parser only emits a
 * draw-or-empowered effect with a parsed class) is a logged no-op that parks nothing.
 */
function heroEffectDrawOrEmpowered(G, _ctx, playerID, _cardId, effect) {
    const empoweredClass = effect.empoweredClass;
    if (empoweredClass === undefined || empoweredClass.length === 0) {
        // why: defensive — the parser always emits draw-or-empowered with a parsed empoweredClass; a
        // missing class here is a logged no-op that parks nothing (mirrors the optional-ko-reward
        // unseeded-reward guard). Unreachable post-parse, but never throw and never park a broken entry.
        pushLog(G, `Player ${playerID} played a draw-or-empowered hero ability with no empowered class, so the choice was skipped.`);
        return;
    }
    // why: parks an interactive draw-or-empowered choice resolved by resolveDrawOrEmpowered (D-24069)
    // why: lazy-init at the park site (never in Game.setup); absent field = no pending choice. Mirrors pendingVictoryPileCardPick (D-24067).
    if (!G.pendingDrawOrEmpowered) {
        G.pendingDrawOrEmpowered = [];
    }
    G.pendingDrawOrEmpowered.push({ playerID, empoweredClass });
}
/**
 * Park handler for the `count-scaled-choose` hero keyword (WP-675 / D-24490).
 *
 * Parks a `PendingCountScaledChoice` on `G.pendingCountScaledChoice[]` (lazy-init,
 * FIFO) carrying the two printed options. The grant happens at resolve time
 * (resolveCountScaledChoice), NOT here — the counts are resolved from `G` when the
 * player picks, so a card played after this one but before the choice resolves is
 * counted correctly (the draw-or-empowered pattern of carrying the descriptor).
 *
 * A missing/short options list (should never happen post-parse — the pre-pass emits
 * exactly two options) is a logged no-op that parks nothing.
 */
function heroEffectCountScaledChoose(G, _ctx, playerID, cardId, effect) {
    const options = effect.countScaledChoiceOptions;
    if (options === undefined || options.length < 2) {
        // why: defensive — the parser always emits count-scaled-choose with two options; a
        // missing/short list here parks nothing (mirrors the draw-or-empowered guard). Never throw.
        pushLog(G, `Player ${playerID} played a count-scaled-choose hero ability with no options, so the choice was skipped.`);
        return;
    }
    // why: parks an interactive count-scaled choice resolved by resolveCountScaledChoice (D-24490)
    // why: lazy-init at the park site (never in Game.setup); absent field = no pending choice.
    if (!G.pendingCountScaledChoice) {
        G.pendingCountScaledChoice = [];
    }
    // why: record the triggering card so the icon count sources exclude it at resolve time
    // (vnom's card shows both icons and would otherwise count itself). Fresh copy of the
    // options array — no aliasing of the parsed descriptor into G.
    G.pendingCountScaledChoice.push({ playerID, cardId, options: options.map((option) => ({ ...option })) });
}
/**
 * Executor for the `shuffle-discard-empty-reward` hero keyword (D-24148).
 *
 * The printed "If your discard pile is empty, you get +N[recruit|attack].
 * Otherwise, shuffle your discard pile into your deck." (Jocasta's Reprocess is
 * the recruit variant, Electromagnetic Eyebeams the attack variant). Mandatory
 * and immediate — the printed text offers no choice, so nothing is parked and
 * no move is blocked.
 *
 * Empty-discard branch: grants the descriptor's magnitude of the descriptor's
 * rewardType via addResources on G.turnEconomy. Played cards live in inPlay
 * until cleanup, so they never count toward the emptiness check (tabletop
 * rule).
 *
 * Non-empty branch: the ENTIRE discard pile is shuffled INTO the deck as one
 * combined deterministic shuffle — new deck = shuffle(deck + discard),
 * discard = []. Both branches append one G.messages line.
 *
 * A rewardType outside the seeded pair (should never happen post-parse — the
 * parser's seeded-set gate filters it) is a silent no-op. The upstream
 * magnitude pre-gate in executeSingleEffect already drops zero/missing
 * magnitudes.
 */
function heroEffectShuffleDiscardEmptyReward(G, ctx, playerID, cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const rewardType = effect.rewardType;
    if (rewardType !== 'attack' && rewardType !== 'recruit') {
        // why: defensive mirror of the parser's seeded-set gate (D-24148) — an
        // unseeded reward never grants or shuffles; unreachable post-parse.
        return;
    }
    const magnitude = effect.magnitude;
    if (magnitude < 1) {
        // why: D-24148 — the build gate ([1-9]\d*) and the parser's n >= 1 check
        // keep zero out of real data; this executor-level guard is the D-24019
        // downstream convention (isValidMagnitude deliberately admits 0 for
        // reveal-family semantics, so each executor owns its own n >= 1 floor).
        return;
    }
    if (playerZones.discard.length === 0) {
        if (rewardType === 'attack') {
            G.turnEconomy = addResources(G.turnEconomy, magnitude, 0);
        }
        else {
            G.turnEconomy = addResources(G.turnEconomy, 0, magnitude);
        }
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} found an empty discard pile and granted +${magnitude} ${rewardType}.`);
        return;
    }
    const shuffledInCount = playerZones.discard.length;
    const combined = moveAllCards(playerZones.discard, playerZones.deck);
    playerZones.discard = combined.from;
    // why: ctx is narrowed to ShuffleProvider because the combined discard-into-
    // deck shuffle needs ctx.random.Shuffle — the only permitted randomness
    // source, so the result replays identically from the seed (D-24148; the
    // established heroEffectDraw pattern).
    playerZones.deck = shuffleDeck(combined.to, ctx);
    pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} shuffled their ${shuffledInCount}-card discard pile into their deck (deck is now ${playerZones.deck.length} cards).`);
}
// why: D-24022 — the hero-effect ImplementationMap (mirrors WP-009B's pattern).
// Handlers are plain functions held OUTSIDE G; a new effect is a registry entry
// + a drift-test entry, not a `switch` edit. Keyed by HeroKeyword and `Partial`
// because 'wound'/'conditional' are intentionally unmapped (the deferred set);
// the union therefore stays typed + drift-detected. Exported so the registry
// drift test can assert its keys == HANDLED_KEYWORDS bidirectionally.
// why: WP-253 / D-24024 — the 7 legacy reveal-* entries are gone; ALL 8 reveal
// keywords now dispatch through the single parameterized `reveal` handler (their
// markers are translated to a `reveal` descriptor with revealRules at parse time).
/**
 * Logs the attribution line for a one-target automatic free defeat (D-24662).
 *
 * // why: the shared defeat core logs an ordinary "fought X at city space N" line, which
 * reads as a paid fight; without this line the free defeat was indistinguishable in the
 * log (live match s1jtBcEAOfw, Silent Sniper). Mirrors the D-24661 Cruel Ruler line.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerID - The player whose card granted the free defeat.
 * @param cardId - The played hero card granting the free defeat.
 * @param targetCardId - The auto-chosen target's display ext_id.
 */
function logAutoFreeDefeat(G, playerID, cardId, targetCardId) {
    pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} defeats ${formatCardRef(G.cardDisplayData, targetCardId)} for free.`, 'applied', cardId);
}
/**
 * Hero handler for the `defeat-with-bystander` keyword (WP-486 / D-24291).
 *
 * Silent Sniper's "Defeat a Villain or Mastermind that has a Bystander." Builds the
 * deterministic eligible-target set (City Villains holding a Bystander, ascending;
 * then the Mastermind when it holds one), then resolves by cardinality:
 * 0 → a self-narrated no-op (NEVER a hollow record — the keyword is in MVP_KEYWORDS,
 * so it reached its handler); exactly 1 → auto-defeat via the shared fight-defeat
 * core (no prompt); ≥2 → park a PendingDefeatChoice (block-all until resolved) so
 * the current player picks which target.
 *
 * why (D-24291): the defeat REUSES the shared fight-defeat cores (a documented
 * internal invocation), so the onFight/onDefeat hooks and Bystander/hero award run
 * in exactly one place. It spends NO attack and sets NO acted-this-turn flag —
 * Silent Sniper is a card play, not a fight — so `spendAttack` + `G.hasActedThisTurn`
 * stay in the fight moves and are excluded from the shared cores.
 *
 * why (ctx): executeHeroEffects is called with the move-context WRAPPER (playCard's
 * `...context` = `{ ctx, random, events, ... }`), so the bare bgio ctx (currentPlayer
 * + turn, read by the shared cores + the villain executor) is `ctx.ctx`, and the
 * ShuffleProvider for a villain Fight scry is the wrapper's `.random`.
 */
function heroEffectDefeatWithBystander(G, ctx, playerID, cardId, _effect) {
    const targets = buildDefeatWithBystanderTargets(G);
    // why: 0 eligible targets is a REACHABLE self-narrated no-op — the printed
    // ability had no legal target this play. It records NO hollow event (the handler
    // ran; the keyword is in MVP_KEYWORDS → classifyHeroEffectReason returns
    // `applied`), matching the D-24017 "surface the reason, don't fail silently" rule.
    if (targets.length === 0) {
        // why: WP-434 — nothing happened because the board offered no legal target, so
        // the outcome is `blocked` (the ability was suppressed, not partially applied).
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} found no Villain or Mastermind holding a Bystander to defeat.`, 'blocked', cardId);
        return;
    }
    // why: executeHeroEffects receives the move-context WRAPPER; the shared cores +
    // the villain executor read the BARE bgio ctx (currentPlayer + `.turn`), which is
    // the wrapper's nested `.ctx`. The Fight scry ShuffleProvider is the wrapper's
    // top-level `.random` (the same narrowing the draw handler uses).
    const bareCtx = ctx.ctx;
    const shuffleContext = { random: ctx.random };
    // why: exactly 1 eligible target → auto-defeat with no prompt (mandatory-if-able).
    // The dispatch routes through the shared core; a villain's onFight may park its
    // own nested pending, which the block-all guards then serialize.
    if (targets.length === 1) {
        logAutoFreeDefeat(G, playerID, cardId, targets[0].cardId);
        // why: D-24681 — forward the move's events, as resolveDefeatChoice does for the ≥2 path.
        // A free defeat of Loki's Vanishing Illusions / Dr. Doom's Monarch's Decree parks a
        // multi-seat seat choice that must admit the non-active seats via setActivePlayers;
        // without events the other seat could never submit (match CM_RIBLkJem: the bot ally
        // faulted on Vanishing Illusions after a one-target Silent Sniper defeat).
        const events = ctx.events;
        dispatchDefeatWithBystanderTarget(G, bareCtx, targets[0], shuffleContext, events);
        return;
    }
    // why: ≥2 eligible targets → park a PendingDefeatChoice; the current player must
    // pick which to defeat. Lazily initialize the FIFO queue (never in Game.setup);
    // snapshots stay counts-only, so it is never persisted. Shipped WITH its UIState
    // projection + client prompt + block-all guard (pending_choice_no_ux_freeze).
    if (!G.pendingDefeatChoices) {
        G.pendingDefeatChoices = [];
    }
    G.pendingDefeatChoices.push({
        choiceType: 'defeat-with-bystander',
        playerID,
        targets,
    });
    // why: WP-434 — parking a mandatory choice is `neutral` (the effect is mid-flight,
    // neither applied nor blocked); the applied/blocked outcome is logged at resolve.
    pushLog(G, `Player ${playerID} must choose which Villain or Mastermind to defeat with ${formatCardRef(G.cardDisplayData, cardId)}.`, 'neutral', cardId);
}
/**
 * Counts the S.H.I.E.L.D.-team Hero cards in the GLOBAL KO pile (WP-682 / D-24499).
 *
 * // why: Pure Fury's threshold is "the number of S.H.I.E.L.D. Heroes in the KO
 * pile" — the single global G.ko zone (all players' KO'd cards), NOT per-player.
 * cardCountsAsShieldHero is S.H.I.E.L.D.-team ONLY (it EXCLUDES HYDRA, unlike the
 * cardStats.isShieldOrHydra flag) plus the basic S.H.I.E.L.D. token carve-out — the
 * exact predicate the printed text wants. Pure read (no mutation, no new G field).
 *
 * @param G - The game state to inspect (not mutated).
 * @returns The number of S.H.I.E.L.D.-team Hero cards in G.ko.
 */
export function countShieldHeroesInKo(G) {
    let count = 0;
    for (const cardId of G.ko) {
        if (cardCountsAsShieldHero(G, cardId)) {
            count++;
        }
    }
    return count;
}
/**
 * Builds the deterministic list of eligible Pure Fury targets (WP-682 / D-24499).
 *
 * A target is eligible when its CURRENT Attack is STRICTLY LESS THAN the count of
 * S.H.I.E.L.D. Heroes in the global KO pile: a City Villain (any occupied space),
 * or the Mastermind when at least one tactic remains (defeating the Mastermind
 * defeats one tactic). Order is pinned — City spaces ascending, Mastermind appended
 * last — matching buildDefeatWithBystanderTargets, because this order feeds BOTH the
 * UIState projection and the ai.legalMoves bot/sim default.
 *
 * // why: D-24741 (Jeff ruling, amends D-24499) — "whose Attack is less than" reads the
 * target's current Attack, the value you would have to beat to fight it: Dark Portals,
 * Bank Robbery Bystanders, captured Heroes, Blood Frenzy and "-N attack" reductions all
 * count. D-24499 compared the printed value, so in tXTkG2Pardt Pure Fury defeated Loki
 * (printed 10, +1 Dark Portal) with 11 S.H.I.E.L.D. Heroes in the KO pile. The single
 * fight-cost authorities supply the value, so Pure Fury and a normal fight never disagree.
 *
 * @param G - The game state to inspect (not mutated).
 * @param fightingPlayerId - The player resolving Pure Fury (for Blood Frenzy's
 *   per-player count). When omitted, Blood Frenzy adds 0.
 * @returns The eligible targets in pinned order (City ascending, Mastermind last).
 */
export function buildPureFuryTargets(G, fightingPlayerId) {
    const koShieldHeroCount = countShieldHeroesInKo(G);
    const targets = [];
    for (let cityIndex = 0; cityIndex < G.city.length; cityIndex++) {
        const cardId = G.city[cityIndex];
        if (cardId === null || cardId === undefined) {
            continue;
        }
        // why: WP-682 / D-24499 — STRICTLY less than (attack < count), per the text.
        if (resolveFightCost(G, cardId, fightingPlayerId) < koShieldHeroCount) {
            targets.push({ kind: 'villain', cityIndex, cardId });
        }
    }
    // why: Masterminds are explicitly eligible (the text names them); a vanquished
    // Mastermind is not a defeatable target (mirrors buildDefeatWithBystanderTargets).
    // why: D-24739 — under Final Blow a pending final blow keeps the Mastermind eligible
    // after its last Tactic; WP-757 / D-24587 — a haunting Mastermind never is. Both live
    // in the shared isMastermindFreeDefeatTarget predicate.
    if (isMastermindFreeDefeatTarget(G)) {
        const mastermindCardId = G.mastermind.baseCardId;
        if (resolveMastermindFightCost(G, fightingPlayerId) < koShieldHeroCount) {
            targets.push({ kind: 'mastermind', cardId: mastermindCardId });
        }
    }
    return targets;
}
/**
 * Hero handler for the `pure-fury` keyword (WP-682 / D-24499).
 *
 * Nick Fury's Pure Fury — "Defeat any Villain or Mastermind whose Attack is less
 * than the number of S.H.I.E.L.D. Heroes in the KO pile." A FREE defeat (no attack
 * paid) that reuses the defeat-with-bystander shared free-defeat path
 * (dispatchDefeatWithBystanderTarget) incl. Masterminds. 0 eligible → self-narrated
 * no-op; exactly 1 → auto-defeat; ≥2 → park a PendingDefeatChoice { choiceType:
 * 'pure-fury' } the current player resolves via resolveDefeatChoice. Mirrors
 * heroEffectDefeatWithBystander exactly, differing only in the eligibility predicate.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - The move-context wrapper (its nested `.ctx` is the bare bgio ctx).
 * @param playerID - The player who played Pure Fury.
 * @param cardId - The Pure Fury card (for provenance logging).
 * @param _effect - The parsed hero effect descriptor (unused — no magnitude/target).
 */
function heroEffectPureFury(G, ctx, playerID, cardId, _effect) {
    const targets = buildPureFuryTargets(G, playerID);
    if (targets.length === 0) {
        // why: WP-434 — no legal target (no Villain/Mastermind weak enough) → `blocked`.
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} found no Villain or Mastermind weak enough for Pure Fury.`, 'blocked', cardId);
        return;
    }
    // why: the same context narrowing heroEffectDefeatWithBystander uses — the shared
    // cores read the bare bgio ctx (the wrapper's nested `.ctx`); the Fight scry
    // ShuffleProvider is the wrapper's top-level `.random`.
    const bareCtx = ctx.ctx;
    const shuffleContext = { random: ctx.random };
    // why: exactly 1 eligible target → auto-defeat with no prompt (mandatory-if-able),
    // via the SHARED free-defeat path (no attack spent, no acted-this-turn flag).
    if (targets.length === 1) {
        logAutoFreeDefeat(G, playerID, cardId, targets[0].cardId);
        // why: D-24681 — forward events so a free defeat of a multi-seat tactic admits the
        // other seats (the Silent Sniper twin above; same Vanishing Illusions / Monarch's Decree path).
        const events = ctx.events;
        dispatchDefeatWithBystanderTarget(G, bareCtx, targets[0], shuffleContext, events);
        return;
    }
    // why: ≥2 eligible targets → park a PendingDefeatChoice with the 'pure-fury'
    // discriminant; resolveDefeatChoice accepts it and dispatches through the same core.
    if (!G.pendingDefeatChoices) {
        G.pendingDefeatChoices = [];
    }
    G.pendingDefeatChoices.push({
        choiceType: 'pure-fury',
        playerID,
        targets,
    });
    pushLog(G, `Player ${playerID} must choose which Villain or Mastermind to defeat with ${formatCardRef(G.cardDisplayData, cardId)}.`, 'neutral', cardId);
}
// why: WP-535 / D-24345 — the ext_id of every Rogue Copy Powers copy. Excluding this
// ext_id (not just the one played instance) from the eligible-Hero set means a Copy
// Powers can never copy itself OR another Copy Powers, which neutralizes copy-of-copy
// recursion when two Copy Powers are played the same turn (Finding 5).
export const COPY_POWERS_EXT_ID = 'core/rogue/copy-powers';
// why: WP-592 / D-24401 — the base ext_id of Rogue's Steal Abilities card. The copy phase
// re-fires each discarded card's ability EXCEPT a discarded Steal Abilities or Copy Powers,
// which are economy-only (the recursion guard in heroEffectStealAbilities): both are
// reentrant-copy keywords that can re-target the in-play Steal Abilities card and recurse to
// a stack overflow. Zones store INSTANCE ids (`<base>#<copyIndex>`), so strip the `#N` suffix
// before comparing to this base — exactly as buildCopyPowersTargets does for COPY_POWERS_EXT_ID.
export const STEAL_ABILITIES_EXT_ID = 'core/rogue/steal-abilities';
/**
 * The Heroes the given player may copy for a Copy Powers play — the real Heroes in that
 * player's `inPlay` (deduplicated, in play order), excluding the Copy Powers ext_id.
 *
 * "Real Hero" = any played card that is NOT a Wound or Bystander. Copy Powers copies
 * "another Hero you played this turn," and the gray S.H.I.E.L.D. starters / Officers and
 * Sidekicks ARE Heroes — they merely carry `heroClass: null` (no class to copy and no
 * ability to re-fire, but a real printed economy that a full-duplicate copy doubles, and
 * a team that transfers). Requiring a non-null class silently dropped every gray Hero, so
 * Copy Powers reported "found no other Hero to copy" on a turn of only S.H.I.E.L.D. basics
 * (Jeff's bug report, 2026-09-11). The Wound/Bystander exclusion is the same "not a Hero"
 * discriminant heroConditions.evaluate.ts uses (countDistinctHeroCostsInHandOrPlay).
 *
 * // why: shared by the park-time handler, the UIState projection, the resolve
 * validation, and the bot default (the round-trip rule) so the client can only submit a
 * Hero the resolve move accepts. Deduplicated because two copies of the same Hero card
 * are the same ability + class — one choice, not two.
 *
 * @param G - The game state to inspect (not mutated).
 * @param playerID - The player whose in-play Heroes are the copy candidates.
 * @returns The copyable Hero ext_ids in play order, or [] when the player has none.
 */
export function buildCopyPowersTargets(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return [];
    }
    const targets = [];
    const seenBaseIds = new Set();
    for (const playedCardId of playerZones.inPlay) {
        const candidate = playedCardId;
        // why: zones store INSTANCE ids (`<base>#<copyIndex>`, e.g. core/rogue/copy-powers#0),
        // never the bare base — so strip the `#N` suffix to a base id for BOTH the copy-of-copy
        // exclusion and the dedup. Without the strip the exclusion (bare COPY_POWERS_EXT_ID)
        // never matched a real instance id, so Copy Powers counted ITSELF as an eligible target
        // → the 1-eligible auto path re-fired executeHeroEffects on Copy Powers → unbounded
        // recursion → stack overflow → the server crash Jeff saw as a ~30s "connection lost —
        // reconnecting" whenever Copy Powers was the only Hero in play. The trait lookup below
        // keeps the full instance id (cardTraits is instance-keyed); only exclusion + dedup use
        // the base, and two instances of one Hero collapse to a single choice.
        const hashIndex = candidate.indexOf('#');
        const baseCandidate = hashIndex === -1 ? candidate : candidate.slice(0, hashIndex);
        if (baseCandidate === COPY_POWERS_EXT_ID) {
            continue;
        }
        // why: Wounds and Bystanders are the only non-Heroes that can sit in inPlay, and they
        // are not copyable. Every other played card — including a gray S.H.I.E.L.D. basic /
        // Officer or a Sidekick (heroClass: null) — IS a Hero and IS a valid Copy Powers
        // target. This is the "not a Hero" discriminant, NOT "has a class" (the earlier
        // heroClass check wrongly excluded all gray Heroes).
        if (baseCandidate === WOUND_EXT_ID || baseCandidate === BYSTANDER_EXT_ID) {
            continue;
        }
        if (seenBaseIds.has(baseCandidate)) {
            continue;
        }
        seenBaseIds.add(baseCandidate);
        targets.push(candidate);
    }
    return targets;
}
/**
 * Applies a resolved Copy Powers copy: re-fires the chosen Hero's on-play ability and
 * grants Copy Powers the copied Hero's class.
 *
 * Used by BOTH re-fire paths — the 1-eligible in-handler auto path and the ≥2 resolve
 * move — so the copy behaves identically however it was selected.
 *
 * // why (ctx): the copied ability may draw / reshuffle, so this threads the FULL
 * move-context WRAPPER (`{ ctx, random, events, ... }`) straight into
 * executeHeroEffects — the only resolve-time re-fire in the engine that needs
 * `ctx.random`. Passing `{G, playerID}` alone (the shape other resolve moves use) would
 * crash a copied draw. executeHeroEffects is REENTRANT and visits only the chosen Hero's
 * hooks; it does NOT re-append inPlay or re-add base attack/recruit economy (Fork 2 —
 * ability only; base economy belongs to applyCardPlay, off the copy path).
 *
 * // why (dual-class): Copy Powers "is both covert and the color you copy." Covert is
 * already baked in card data (core.json `hc:covert`); this writes the copied class into
 * the turn-scoped `cardCopiedClasses` map (D-24730; cleared at end-of-turn cleanup), so
 * every downstream `[hc:<copied>]` gate counts Copy Powers as that class too. cardTraits
 * is never mutated. The class is granted BEFORE the re-fire so Copy Powers already counts
 * as the copied class if the copied ability itself reads classes.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - The move-context wrapper (its `.ctx` is the bare bgio ctx, `.random` the shuffle source).
 * @param playerID - The player copying (the Copy Powers owner).
 * @param sourceCardId - The Copy Powers card ext_id receiving the copied class.
 * @param chosenHeroId - The Hero whose ability is re-fired and whose class is copied.
 */
export function applyCopyPowers(G, ctx, playerID, sourceCardId, chosenHeroId) {
    pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, sourceCardId)} copied ${formatCardRef(G.cardDisplayData, chosenHeroId)}.`, 'neutral', sourceCardId);
    // why: D-24730 — the copied class goes into the turn-scoped cardCopiedClasses map, not
    // the setup Size-Changing map: end-of-turn cleanup deletes it, so this play's copy does
    // not follow the card into its next play.
    const copiedClass = G.cardTraits[chosenHeroId]?.heroClass;
    if (typeof copiedClass === 'string' && copiedClass.length > 0) {
        if (!G.cardCopiedClasses) {
            G.cardCopiedClasses = {};
        }
        const existingClasses = G.cardCopiedClasses[sourceCardId];
        if (existingClasses === undefined) {
            G.cardCopiedClasses[sourceCardId] = [copiedClass];
        }
        else if (!existingClasses.includes(copiedClass)) {
            existingClasses.push(copiedClass);
        }
    }
    // why: D-24391 — Copy Powers is a full duplicate of the copied Hero, so it adds the
    // copied card's PRINTED attack + recruit. The copied Hero was already played this turn
    // (Copy Powers copies "a Hero you played this turn"), so this is a SECOND instance — a
    // genuine duplicate legitimately DOUBLES the stat. A null-stat copied Hero adds 0/0.
    // G.cardStats is keyed by the same copy-suffixed instance ext_id as chosenHeroId.
    const copiedStats = G.cardStats[chosenHeroId];
    if (copiedStats) {
        G.turnEconomy = addResources(G.turnEconomy, copiedStats.attack, copiedStats.recruit);
        if (copiedStats.attack > 0 || copiedStats.recruit > 0) {
            pushLog(G, `Player ${playerID} gained +${copiedStats.attack} attack and +${copiedStats.recruit} recruit from copying ${formatCardRef(G.cardDisplayData, chosenHeroId)}.`, 'applied', sourceCardId);
        }
    }
    // why: D-24391 — Copy Powers counts as the copied Hero's TEAM, the runtime team-grant
    // sibling to the class grant above, via the lazy cardCopiedTeams map read by the
    // effectiveTeams.logic.ts helper. cardTraits.team is string|null — a teamless copied
    // Hero grants nothing. cardTraits is never mutated.
    const copiedTeam = G.cardTraits[chosenHeroId]?.team;
    if (typeof copiedTeam === 'string' && copiedTeam.length > 0) {
        if (!G.cardCopiedTeams) {
            G.cardCopiedTeams = {};
        }
        const existingTeams = G.cardCopiedTeams[sourceCardId];
        if (existingTeams === undefined) {
            G.cardCopiedTeams[sourceCardId] = [copiedTeam];
        }
        else if (!existingTeams.includes(copiedTeam)) {
            existingTeams.push(copiedTeam);
        }
    }
    // Re-fire the copied Hero's on-play ability. The count return is observability only.
    executeHeroEffects(G, ctx, playerID, chosenHeroId);
}
/**
 * Hero handler for the `copy-powers` keyword (WP-535 / D-24345).
 *
 * Rogue's "Copy Powers": "Play this card as a copy of another Hero you played this
 * turn." Builds the eligible-Hero set (the player's real in-play Heroes minus Copy
 * Powers), then resolves by cardinality:
 * 0 → a self-narrated no-op (NEVER a hollow record — the keyword is in MVP_KEYWORDS, so
 * it reached its handler); exactly 1 → auto-copy (no prompt); ≥2 → park a
 * PendingCopyPowersChoice (block-all until resolved) so the current player picks which
 * Hero to copy.
 *
 * // why (ctx): executeHeroEffects is called with the move-context WRAPPER, so this
 * handler received that same wrapper and threads it unchanged into applyCopyPowers →
 * executeHeroEffects for the auto path (the copied ability may draw via `ctx.random`).
 */
function heroEffectCopyPowers(G, ctx, playerID, cardId, _effect) {
    const targets = buildCopyPowersTargets(G, playerID);
    // why: 0 eligible Heroes is a REACHABLE self-narrated no-op — Copy Powers was the only
    // Hero played (or only S.H.I.E.L.D./Wounds alongside it). It records NO hollow event
    // (the handler ran; the keyword is in MVP_KEYWORDS → classifyHeroEffectReason returns
    // `applied`). WP-434 — nothing happened, so the outcome is `blocked`.
    if (targets.length === 0) {
        pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} found no other Hero played this turn to copy.`, 'blocked', cardId);
        return;
    }
    // why: exactly 1 eligible Hero → auto-copy with no prompt (mandatory-if-able). The
    // re-fire threads the same move-context wrapper this handler received.
    if (targets.length === 1) {
        applyCopyPowers(G, ctx, playerID, cardId, targets[0]);
        return;
    }
    // why: ≥2 eligible Heroes → park a PendingCopyPowersChoice; the current player must
    // pick which Hero to copy. Lazily initialize the FIFO queue (never in Game.setup); an
    // undefined field stays out of canonical JSON, keeping the hash oracles from re-pinning.
    // Shipped WITH its UIState projection + client prompt + block-all guard
    // (pending_choice_no_ux_freeze).
    if (!G.pendingCopyPowersChoices) {
        G.pendingCopyPowersChoices = [];
    }
    G.pendingCopyPowersChoices.push({
        choiceType: 'copy-powers',
        playerID,
        sourceCardId: cardId,
    });
    // why: WP-434 — parking a mandatory choice is `neutral` (the effect is mid-flight,
    // neither applied nor blocked); the applied/blocked outcome is logged at resolve.
    pushLog(G, `Player ${playerID} must choose which Hero to copy with ${formatCardRef(G.cardDisplayData, cardId)}.`, 'neutral', cardId);
}
/**
 * Hero handler for the `steal-abilities` keyword (WP-592 / D-24401).
 *
 * Rogue's "Steal Abilities": "Each player discards the top card of their deck. Play a
 * copy of each of those cards." Two deterministic, synchronous phases:
 *
 * 1. Discard phase — for each player in `Object.keys(G.playerZones).sort()` seat order,
 *    discard the top card of their deck to their own discard pile (reshuffling their
 *    discard into their deck first when the deck is empty, D-24285); a player with no
 *    cards anywhere discards nothing.
 * 2. Copy phase — the Steal Abilities player plays a copy of each discarded card in that
 *    order: add the card's printed `G.cardStats` attack/recruit to `G.turnEconomy`, then
 *    re-fire its on-play ability via the reentrant `executeHeroEffects`.
 *
 * A copy is EPHEMERAL — it feeds economy + re-fires the ability only. It enters no zone,
 * never calls `applyCardPlay`, never mutates `cardTraits`, and grants Steal Abilities NO
 * class/team (the deliberate D-24401 bounding — unlike Copy Powers' full-duplicate merge,
 * "a copy of each card" is not a merge into the source). The real discarded cards stay in
 * their owners' discard piles.
 *
 * Recursion guard — a discarded card whose base ext_id is `STEAL_ABILITIES_EXT_ID` OR
 * `COPY_POWERS_EXT_ID` is economy-only (NOT re-fired). Both are reentrant-copy keywords: a
 * re-fired Copy Powers auto-copies the in-play Steal Abilities card (the sole eligible Rogue
 * Hero) and re-fires it, recursing to the `COPY_POWERS_EXT_ID` stack-overflow class.
 *
 * // why (ctx): `executeHeroEffects` is called with the move-context WRAPPER, so this handler
 * received that same wrapper and threads it unchanged into every re-fire AND the reshuffle —
 * a copied draw / an empty-deck reshuffle needs `ctx.random`. Passing `{ G, playerID }` alone
 * would crash a copied draw / reshuffle.
 */
function heroEffectStealAbilities(G, ctx, playerID, cardId, _effect) {
    // why: WP-592 / D-24401 — iterate a SORTED key order (matching gain-wound-each /
    // scoring.logic.ts) so the "each player discards" distribution replays identically, and so
    // ALL players are targeted, not just the active one. Deterministic — the only randomness is
    // an empty-deck reshuffle via the threaded ctx.random.
    const seatOrder = Object.keys(G.playerZones).sort();
    // Phase 1 — each player discards the top card of their deck.
    const discardedCardIds = [];
    for (const ownerPlayerId of seatOrder) {
        const ownerZones = G.playerZones[ownerPlayerId];
        if (!ownerZones) {
            continue;
        }
        if (ownerZones.deck.length === 0) {
            // why: D-24285 — the standard empty-deck reshuffle: when a player must discard the top
            // of an empty deck, shuffle their discard into a fresh deck first, via the threaded
            // ctx.random (ctx as ShuffleProvider, exactly as heroEffectReveal does) — never
            // Math.random(). A player whose discard is also empty stays empty (no-op).
            reshuffleDiscardIntoDeck(ownerZones, ctx);
        }
        const topCardId = ownerZones.deck[0];
        if (!topCardId) {
            // why: D-24017 — no cards anywhere for this player is a legitimate no-op, but a silent
            // skip reads as "the card did nothing"; log it so the reason is observable.
            pushLog(G, `Player ${ownerPlayerId} had no card to discard for ${formatCardRef(G.cardDisplayData, cardId)}.`);
            continue;
        }
        const moveResult = moveCardFromZone(ownerZones.deck, ownerZones.discard, topCardId);
        ownerZones.deck = moveResult.from;
        ownerZones.discard = moveResult.to;
        discardedCardIds.push(topCardId);
        pushLog(G, `Player ${ownerPlayerId} discarded ${formatCardRef(G.cardDisplayData, topCardId)} for ${formatCardRef(G.cardDisplayData, cardId)}.`);
    }
    // Phase 2 — the Steal Abilities player plays a copy of each discarded card.
    for (const discardedCardId of discardedCardIds) {
        // why: D-24391 economy pattern reused WITHOUT the class/team grant — a Steal Abilities
        // copy feeds economy + re-fires the ability only (the D-24401 bounding). A null-stat
        // copied card (a Wound) adds 0/0. Written inline rather than extracted (duplicate-first,
        // code-style Rule 1 — this is only the second copy of the economy block).
        const copiedStats = G.cardStats[discardedCardId];
        if (copiedStats) {
            G.turnEconomy = addResources(G.turnEconomy, copiedStats.attack, copiedStats.recruit);
            if (copiedStats.attack > 0 || copiedStats.recruit > 0) {
                pushLog(G, `Player ${playerID} gained +${copiedStats.attack} attack and +${copiedStats.recruit} recruit from copying ${formatCardRef(G.cardDisplayData, discardedCardId)}.`, 'applied', cardId);
            }
        }
        // why: WP-592 / D-24401 — the recursion guard. Zones store INSTANCE ids
        // (`<base>#<copyIndex>`), so strip the `#N` suffix before comparing to the base ext_ids
        // (mirrors buildCopyPowersTargets). A discarded Steal Abilities OR Copy Powers is
        // economy-only, NOT re-fired: both are reentrant-copy keywords that can re-target the
        // just-played, in-play Steal Abilities card, and re-firing them recurses to the
        // COPY_POWERS_EXT_ID stack-overflow class (the ~30s "connection lost" crash). Nothing
        // decreases monotonically (reshuffle-on-empty recycles the discards back to deck-top),
        // so excluding BOTH — not just steal-abilities — is what bounds the mutual re-fire.
        const hashIndex = discardedCardId.indexOf('#');
        const baseDiscardedId = hashIndex === -1 ? discardedCardId : discardedCardId.slice(0, hashIndex);
        if (baseDiscardedId === STEAL_ABILITIES_EXT_ID || baseDiscardedId === COPY_POWERS_EXT_ID) {
            pushLog(G, `Player ${playerID}'s ${formatCardRef(G.cardDisplayData, cardId)} copied the economy of ${formatCardRef(G.cardDisplayData, discardedCardId)} but did not re-fire its ability (copy-of-copy guard).`, 'neutral', cardId);
            continue;
        }
        // Re-fire the discarded card's on-play ability for the Steal Abilities player. The count
        // return is observability only; the wrapper is threaded so a copied draw/reshuffle replays.
        executeHeroEffects(G, ctx, playerID, discardedCardId);
    }
}
/**
 * Hero handler for the `recruit-as-attack` keyword (WP-580 / D-24389).
 *
 * God of Thunder's "You can use Recruit as Attack this turn." Sets the
 * turn-scoped conversion flag on `G.turnEconomy`; the fight moves, the bot
 * affordability projection (`ai.legalMoves.ts`), and the UIState economy
 * projection then fund fight costs from unspent recruit (attack first). No
 * magnitude, no pending choice, no resource total moved at play time.
 */
function heroEffectRecruitAsAttack(G, _ctx, playerID, cardId, _effect) {
    G.turnEconomy = enableRecruitSpendableAsAttack(G.turnEconomy);
    // why: WP-434 — the conversion is `applied` (green): it changed turn state
    // (recruit may now fund fights) even though no resource total moved yet.
    pushLog(G, `Player ${playerID} can spend Recruit as Attack this turn (${formatCardRef(G.cardDisplayData, cardId)}).`, 'applied', cardId);
}
/**
 * Hero handler for the `no-more-draws` keyword (WP-731 / D-24552).
 *
 * Venompool's "Shenanigans" — "But you can't draw any more cards until the end
 * of this turn." Sets the turn-scoped `drawsLocked` flag on `G.turnEconomy`;
 * `heroEffectDraw` then draws 0 and logs `blocked` for the rest of this player's
 * turn. The end-of-turn hand refill, setup deal, and other-seat draws use
 * `drawCardsIntoHand` directly (not this handler's `draw` path), so the lock
 * lifts at turn end exactly as printed. No magnitude, no pending choice, no
 * resource total moved.
 */
function heroEffectNoMoreDraws(G, _ctx, playerID, cardId, _effect) {
    G.turnEconomy = enableDrawLock(G.turnEconomy);
    // why: WP-434 — the restriction is `applied` (green): it changed turn state
    // (further draws are now barred) even though no card moved.
    pushLog(G, `Player ${playerID} can't draw any more cards this turn (${formatCardRef(G.cardDisplayData, cardId)}).`, 'applied', cardId);
}
/**
 * Hero handler for the `play-both-sides` keyword (WP-780 / D-24619).
 *
 * cvwr Cloak & Dagger Penumbra — "Whenever you play a Divided Card card this turn, play both
 * sides as if they were two different cards." Sets the turn-scoped `isPlayBothSidesActive` flag
 * on `G.turnEconomy` (idempotent — a second Penumbra rebuilds the same flag); playCard then
 * routes every LATER split card this turn through playBothSplitFaces. No magnitude, no pending
 * choice, no resource total moved.
 */
function heroEffectPlayBothSides(G, _ctx, playerID, cardId, _effect) {
    G.turnEconomy = enablePlayBothSides(G.turnEconomy);
    // why: WP-434 — `applied` (green): it changed turn state (later Divided Cards play both
    // sides) even though no card moved.
    pushLog(G, `Player ${playerID} will play both sides of each Divided Card played this turn (${formatCardRef(G.cardDisplayData, cardId)}).`, 'applied', cardId);
}
/**
 * Hero handler for the `out-of-time` keyword (WP-803 / D-24692).
 *
 * Man / Woman Out of Time — "After you use this card's abilities, set it aside. At the
 * beginning of your next turn, play this card a second time and then discard it." (rules v23
 * L1667-1694). The Out of Time line is the LAST line on every in-scope card, so the card's other
 * abilities have already run. Three guards, in order; each failure logs one `blocked` sentence
 * and returns:
 *   1. the card is not already in `G.turnEconomy.outOfTimePlayedCardIds` (a replayed card, or one
 *      already set aside this turn, never goes Out of Time again);
 *   2. the card is in the player's inPlay (Steal Abilities re-fires a card from the discard pile);
 *   3. the card is the LAST inPlay entry, i.e. the card being played now (Copy Powers re-fires an
 *      earlier-played card).
 * On apply: the card leaves inPlay for the end of `G.outOfTimeCards[playerID]` (map and key
 * created lazily), and is recorded in the turn's Out of Time ledger so `playedCardIdsThisTurn`
 * still counts it as played this turn.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Context (unused).
 * @param playerID - The player who played the card.
 * @param cardId - The played hero card.
 * @param _effect - The `{ type: 'out-of-time' }` descriptor (no magnitude).
 */
function heroEffectOutOfTime(G, _ctx, playerID, cardId, _effect) {
    const cardRef = formatCardRef(G.cardDisplayData, cardId);
    // why: guard 1 — rules v23 L1674 ("You can't use Man Out of Time again to play that card for a
    // third turn") and L1692 (an enemy-sent card played back "can't use Man Out of Time again").
    // The turn-2 replay pre-records the card here before it plays, so its own Out of Time line
    // lands in this branch.
    if (G.turnEconomy?.outOfTimePlayedCardIds?.includes(cardId) === true) {
        pushLog(G, `${cardRef} is not set aside again: a card goes Out of Time only once.`, 'blocked', cardId);
        return;
    }
    const playerZones = G.playerZones[playerID];
    // why: guard 2 — rules v23 L1689 ("If a special ability lets you copy … a Hero card, you can't
    // use Man Out of Time on the copy"). Steal Abilities re-fires a card sitting in the discard pile.
    if (playerZones === undefined || !playerZones.inPlay.includes(cardId)) {
        pushLog(G, `${cardRef} is not set aside: a copy can't use Out of Time.`, 'blocked', cardId);
        return;
    }
    // why: guard 3 — rules v23 L1689 again. Copy Powers re-fires an EARLIER-played card while the
    // copying card is the newest inPlay entry; only the card being played now is last. This holds
    // because Out of Time is the last line on every in-scope card and no earlier line appends to
    // inPlay mid-pass (D-24692 records the fragility).
    if (playerZones.inPlay[playerZones.inPlay.length - 1] !== cardId) {
        pushLog(G, `${cardRef} is not set aside: a copy can't use Out of Time.`, 'blocked', cardId);
        return;
    }
    const moved = moveCardFromZone(playerZones.inPlay, G.outOfTimeCards?.[playerID] ?? [], cardId);
    playerZones.inPlay = moved.from;
    // why: lazily created here, never in Game.setup, so a match with no Out of Time card never
    // writes the field and both hash oracles stay byte-stable (the D-24526 precedent).
    if (G.outOfTimeCards === undefined) {
        G.outOfTimeCards = {};
    }
    G.outOfTimeCards[playerID] = moved.to;
    // why: rules v23 L1682 + L3441-3453 — the card still counts as played this turn for Superpower
    // gates and played-card counts, through playedCardIdsThisTurn's ledger read.
    G.turnEconomy = recordOutOfTimePlayed(G.turnEconomy, cardId);
    // why: WP-434 — `applied` (green): the card moved out of play.
    pushLog(G, `Player ${playerID} set ${cardRef} aside Out of Time; it plays again at the start of their next turn.`, 'applied', cardId);
}
/**
 * Handler for the `grant-out-of-time` hero keyword (WP-828 / D-24761).
 *
 * Winter Soldier's 2>4: "A Hero in your hand gains [keyword:Man Out of Time] this turn." (and
 * the [hc:tech]-gated "Another Hero …" line). Queues one owed grant; the choice of Hero is made
 * after the play by openOutOfTimeGrantSeatChoiceIfNeeded (see moves/outOfTimeGrant.logic.ts).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused (the grant resolves after the play).
 * @param playerID - The player who played the card.
 * @param cardId - The played card (recorded as the grant's source).
 * @param _effect - The `{ type: 'grant-out-of-time' }` descriptor (no magnitude).
 */
function heroEffectGrantOutOfTime(G, _ctx, playerID, cardId, _effect) {
    queueOutOfTimeGrant(G, playerID, cardId);
}
/**
 * Hero handler for the `fight-cost-reduction` keyword (WP-794 / D-24663).
 *
 * core Storm Lightning Bolt / Tidal Wave, cvwr Storm & Black Panther Lightning Strike and
 * dkcy Forge Dirty Work — "Any Villain you fight on the <space> this turn gets -N attack" /
 * "The Mastermind gets -N attack this turn". Appends `{ target, amount, sourceCardId }` to
 * the lazy `G.turnEconomy.fightCostReductions` list; `resolveFightCost` /
 * `resolveMastermindFightCost` then subtract it from every eligible fight this turn. A
 * missing target is a silent no-op (the magnitude pre-gate already guarantees the amount).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Context (unused).
 * @param playerID - The player who played the card.
 * @param cardId - The played hero card.
 * @param effect - The fight-cost-reduction descriptor (magnitude + target).
 */
function heroEffectFightCostReduction(G, _ctx, playerID, cardId, effect) {
    const target = effect.fightCostReductionTarget;
    if (target === undefined) {
        return;
    }
    const amount = effect.magnitude;
    G.turnEconomy = addFightCostReduction(G.turnEconomy, target, amount, cardId);
    const cardRef = formatCardRef(G.cardDisplayData, cardId);
    // why: WP-434 — `applied` (green): it changed turn state (eligible fights now cost less)
    // even though no resource total moved.
    if (target === 'mastermind') {
        pushLog(G, `Player ${playerID}'s ${cardRef}: the Mastermind gets -${amount} attack this turn.`, 'applied', cardId);
        return;
    }
    // why: the printed cards say "in the Sewers" but "on the Rooftops" / "on the Bridge".
    const preposition = target === 'sewers' ? 'in' : 'on';
    pushLog(G, `Player ${playerID}'s ${cardRef}: Villains you fight ${preposition} the ${formatAttackTargets([target])} this turn get -${amount} attack.`, 'applied', cardId);
}
// ---------------------------------------------------------------------------
// Investigate handler (static-criterion + draw subset; WP-564 / D-24373)
// ---------------------------------------------------------------------------
/**
 * Investigate handler — looks at the top `investigateLookCount` (default 2) cards of the
 * acting player's own deck, draws the FIRST card in look order that matches the static
 * `investigateCriteria`, and puts the non-selected looked-at cards on the BOTTOM of the
 * deck in look order. A deck shorter than the look count reshuffles the discard via the
 * shared `reshuffleDiscardIntoDeck` helper first (D-24285). Deterministic — first-match,
 * no `ctx.random` in selection, no pending choice. All three outcomes are narrated.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Context (narrowed to ShuffleProvider for the short-deck reshuffle).
 * @param playerID - Active player ID.
 * @param _cardId - The played hero card's CardExtId (unused; investigate reads the deck).
 * @param effect - The investigate effect descriptor (criteria + look count).
 */
function heroEffectInvestigate(G, ctx, playerID, _cardId, effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const criteria = effect.investigateCriteria ?? [];
    // why: WP-564 / D-24373 — default 2 (the printed look count); the descriptor field lets
    // the deferred look-at-three modifier set it without reshaping the effect.
    const lookCount = effect.investigateLookCount ?? INVESTIGATE_HANDLER_DEFAULT_LOOK_COUNT;
    // why: defensive — the parser only emits an investigate descriptor with a resolved
    // criterion, so an empty criteria list is unreachable in practice; no-op if it happens.
    if (criteria.length === 0) {
        return;
    }
    const criterionText = describeInvestigateCriteria(criteria);
    // why: D-24285 — looking at the top of a deck shorter than the look count reshuffles the
    // discard into the deck first (the standard Legendary rule that a look/reveal, like a
    // draw, reshuffles an exhausted deck mid-effect), via the SHARED helper — never a second
    // reshuffle path. ctx narrows to ShuffleProvider exactly as the draw / reveal handlers do.
    if (playerZones.deck.length < lookCount) {
        reshuffleDiscardIntoDeck(playerZones, ctx);
    }
    const windowSize = Math.min(lookCount, playerZones.deck.length);
    if (windowSize === 0) {
        // why: an empty deck (and discard) is a legal, narrated outcome — nothing to look at.
        pushLog(G, `Player ${playerID} Investigated for ${criterionText} but had no cards to look at.`, 'blocked');
        return;
    }
    // why: look order is top-first; the FIRST matching card (deterministic) is drawn.
    const lookWindow = playerZones.deck.slice(0, windowSize);
    let drawnCardId;
    for (const candidateId of lookWindow) {
        if (investigateCardMatchesCriteria(G, candidateId, criteria)) {
            drawnCardId = candidateId;
            break;
        }
    }
    if (drawnCardId !== undefined) {
        const moveResult = moveCardFromZone(playerZones.deck, playerZones.hand, drawnCardId);
        playerZones.deck = moveResult.from;
        playerZones.hand = moveResult.to;
        // why: D-24732 — Investigate draws the matching card, an extra card drawn this turn.
        G.turnEconomy.cardsDrawn += 1;
    }
    // why: WP-564 / D-24373 — put the non-selected looked-at cards on the BOTTOM in look
    // order (look order preserved so replay is reproducible). After removing the drawn card
    // (if any) from the deck, the top `nonDrawnCount` cards are exactly the non-drawn
    // looked-at cards in look order; move that block to the bottom in one splice.
    const nonDrawnCount = windowSize - (drawnCardId !== undefined ? 1 : 0);
    const toBottom = playerZones.deck.slice(0, nonDrawnCount);
    const rest = playerZones.deck.slice(nonDrawnCount);
    playerZones.deck = [...rest, ...toBottom];
    if (drawnCardId !== undefined) {
        pushLog(G, `Player ${playerID} Investigated for ${criterionText} and drew ${formatCardRef(G.cardDisplayData, drawnCardId)}.`, 'applied', drawnCardId);
    }
    else {
        // why: naming the criterion (not a bare "nothing happened") is the WP-550 fidelity
        // precedent — a zero-match draw with no context reads as a broken card.
        pushLog(G, `Player ${playerID} Investigated for ${criterionText} but found no matching card in the top ${windowSize}; put ${windowSize === 1 ? 'it' : 'them'} on the bottom.`, 'blocked');
    }
}
// why: WP-564 / D-24373 — the printed default look count, mirrored here from the setup
// parser's INVESTIGATE_DEFAULT_LOOK_COUNT (duplicate-first — two files, one value; a third
// appearance would justify extracting a shared const).
const INVESTIGATE_HANDLER_DEFAULT_LOOK_COUNT = 2;
/**
 * Reveal-from-hand → draw (WP-659 / D-24470). Psychic Link's "Each player may reveal
 * another [team]/[hc] Hero. Each player who does draws a card."
 *
 * For every player in deterministic seat order (`Object.keys(G.playerZones).sort()` — the
 * gain-wound-each / steal-abilities precedent), if that player can reveal a card matching
 * the reveal criterion, they draw one card. The printed "may" is auto-taken: revealing costs
 * nothing and yields a draw, so declining is strictly dominated — no pending-choice park
 * (D-24470). Reveal is state-neutral (the matched card STAYS where it is — the only mutation
 * is the draw). The playing player may also reveal a card they played this turn (D-24731);
 * every other player reveals from hand only (see playerCanRevealMatch).
 *
 * Deterministic: fixed seat order; the draw reshuffles the discard through the single
 * `ctx.random` shuffle envelope on an empty deck (drawFromPlayerDeck). Never throws — an
 * empty hand or a no-match player is simply skipped.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Context narrowed to ShuffleProvider for the draw reshuffle.
 * @param playerID - The playing player (the only one who can reveal a played card); only their own draw counts toward turnEconomy.cardsDrawn (D-24732).
 * @param cardId - The played card (for the log reference; never revealed by itself).
 * @param effect - The effect descriptor carrying the single revealCriterion.
 */
function heroEffectRevealFromHand(G, ctx, playerID, cardId, effect) {
    const criterion = effect.revealCriterion;
    // why: defensive — the parser only emits a reveal-from-hand effect once its criterion
    // resolves (the Honest-Partial branch); a criterion-less effect is unreachable, no-op here.
    if (criterion === undefined) {
        return;
    }
    let anyPlayerRevealed = false;
    for (const eachPlayerId of Object.keys(G.playerZones).sort()) {
        const playerZones = G.playerZones[eachPlayerId];
        if (!playerZones) {
            continue;
        }
        const isPlayingPlayer = eachPlayerId === playerID;
        if (!playerCanRevealMatch(G, playerZones, isPlayingPlayer, cardId, criterion)) {
            continue;
        }
        anyPlayerRevealed = true;
        const drawnCount = drawFromPlayerDeck(G, eachPlayerId, 1, ctx);
        // why: D-24732 — only the playing player's draw is one of "your" extra cards this turn;
        // turnEconomy belongs to the current player, so another seat's draw never counts.
        if (eachPlayerId === playerID) {
            G.turnEconomy.cardsDrawn += drawnCount;
        }
        if (drawnCount > 0) {
            pushLog(G, `Player ${eachPlayerId} revealed a matching Hero for ${formatCardRef(G.cardDisplayData, cardId)} and drew a card.`, 'applied', cardId);
        }
        else {
            // why: WP-434 — a match with an empty deck AND discard is `partial` (the ability fired,
            // the source ran dry) — distinct from a no-match player, who is silently skipped.
            pushLog(G, `Player ${eachPlayerId} revealed a matching Hero for ${formatCardRef(G.cardDisplayData, cardId)} but their deck and discard pile were empty.`, 'partial', cardId);
        }
    }
    // why: D-24668 — when no player held a matching Hero the ability resolved with no log
    // line at all (Psychic Link, match zE7zTyM-ziJ 13.2.6). Say so, the WP-434 `blocked`
    // outcome for an ability whose condition found nothing.
    if (!anyPlayerRevealed) {
        pushLog(G, `No player had a matching Hero to reveal for ${formatCardRef(G.cardDisplayData, cardId)}, so no one drew.`, 'blocked', cardId);
    }
}
/**
 * Returns whether a player can reveal a card matching a reveal-from-hand criterion
 * (Psychic Link's "reveal another [team:x-men] Hero").
 *
 * Every player can reveal from hand. The playing player can also reveal a card they played
 * this turn that is still in play, other than the resolving card itself ("another").
 *
 * @param G - Game state (read-only).
 * @param playerZones - The revealing player's zones.
 * @param isPlayingPlayer - Whether this player is the one resolving the card.
 * @param resolvingCardId - The card whose effect is resolving (never revealed by itself).
 * @param criterion - The single reveal criterion.
 * @returns Whether the player holds or has played a matching card.
 */
function playerCanRevealMatch(G, playerZones, isPlayingPlayer, resolvingCardId, criterion) {
    for (const handCardId of playerZones.hand) {
        // why: reuse investigateCardMatchesCriteria (zone-agnostic — projects the card's
        // stats/traits and matches) with the single criterion wrapped in a 1-element list, the
        // OR-combined form the shared matcher takes.
        if (investigateCardMatchesCriteria(G, handCardId, [criterion])) {
            return true;
        }
    }
    // why: D-24731 — rules v23 "Revealing a Card" (L3186-3190): "You can reveal a card from
    // your hand or you can reveal a card in front of you that you have already played this
    // turn." Only the playing player has played cards on this turn, so the other players stay
    // hand-only. The D-24651 Diving Block precedent; the Array.isArray guard covers minimal
    // test states that omit the play area.
    if (!isPlayingPlayer || !Array.isArray(playerZones.inPlay)) {
        return false;
    }
    // why: D-24692 — a played card that went Out of Time is not "in front of you" any more.
    for (const playedCardId of playedCardIdsStillInPlay(G, playerZones.inPlay)) {
        // why: "another" — the resolving card is in play but cannot reveal itself.
        if (playedCardId === resolvingCardId) {
            continue;
        }
        if (playedCardMatchesRevealCriterion(G, playedCardId, criterion)) {
            return true;
        }
    }
    return false;
}
/**
 * Returns whether a played card matches a reveal criterion, reading its effective
 * when-played team and class so a Copy Powers copy counts.
 *
 * @param G - Game state (read-only).
 * @param playedCardId - A card the playing player has in play.
 * @param criterion - The single reveal criterion.
 * @returns Whether the played card matches.
 */
function playedCardMatchesRevealCriterion(G, playedCardId, criterion) {
    // why: D-24391 / D-24726 — the single effective-team source for a card in play.
    if (criterion.kind === 'team') {
        return cardHasTeamWhenPlayed(G, playedCardId, criterion.team);
    }
    // why: D-24074 / D-24730 — the single effective-class source for a card in play.
    if (criterion.kind === 'hero-class') {
        return cardHasClassWhenPlayed(G, playedCardId, criterion.heroClass);
    }
    return investigateCardMatchesCriteria(G, playedCardId, [criterion]);
}
/**
 * Reveal-or-gain-a-Wound for each player (WP-843 / D-24777). co2e Crazed Rampage's "Each
 * player reveals another [hc:strength] Hero or gains a Wound."
 *
 * For every player, starting with the playing player and going clockwise (rules v23 L2247,
 * D-24780), a player who cannot reveal another Hero
 * matching the criterion gains one Wound at once; the players who can are queued on
 * `G.pendingRevealOrWoundChoices`, and each chooses after the move whether to reveal or gain
 * the Wound (WP-845 / D-24779). An empty Wound supply is a logged no-op for that player.
 * Deterministic (no RNG — the Wound is top-of-pile) and never throws.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Unused; no shuffle or randomness is involved.
 * @param playerID - The playing player (the only one who can reveal a played card); only their own Wound counts toward turnEconomy.woundsDrawn.
 * @param cardId - The played card (for the log reference; never revealed by itself).
 * @param effect - The effect descriptor carrying the single revealCriterion.
 */
function heroEffectRevealOrGainWoundEach(G, _ctx, playerID, cardId, effect) {
    const criterion = effect.revealCriterion;
    // why: defensive — the parser only emits this effect once its criterion resolves (the
    // Honest-Partial branch); a criterion-less effect is unreachable, no-op here.
    if (criterion === undefined) {
        return;
    }
    const cardRef = formatCardRef(G.cardDisplayData, cardId);
    const requirement = describeHeroRevealRequirement(criterion);
    let anyPlayerLackedMatch = false;
    const gainedPlayerIds = [];
    const divingBlockPendingPlayerIds = [];
    const revealingPlayerIds = [];
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780). The
    // queued revealers are re-sorted ascending by enqueueRevealOrWoundChoice (D-24501).
    for (const eachPlayerId of seatsInTurnOrderFrom(Object.keys(G.playerZones), playerID)) {
        const playerZones = G.playerZones[eachPlayerId];
        if (!playerZones) {
            continue;
        }
        const isPlayingPlayer = eachPlayerId === playerID;
        // why: WP-845 / D-24779 (amends D-24777 §5) — rules v23 "Revealing a Card" (L3196-3200) lets
        // a player who can reveal decline and take the Wound instead, so that player is queued for a
        // choice after the move rather than auto-revealing. The predicate is fixed now, at effect
        // time. playerCanRevealMatch is the D-24731 "another" scope: hand for every player, plus
        // played-this-turn for the playing player, never the resolving card itself.
        if (playerCanRevealMatch(G, playerZones, isPlayingPlayer, cardId, criterion)) {
            revealingPlayerIds.push(eachPlayerId);
            continue;
        }
        anyPlayerLackedMatch = true;
        // why: gainWoundWithOutcome is the gainWoundForPlayer chokepoint, so Diving Block / Flying
        // Shield Block still intercept this Wound (WP-682 / WP-827 parity with heroEffectGainWound).
        const woundOutcome = gainWoundWithOutcome(G, eachPlayerId);
        if (woundOutcome === 'no-wound') {
            pushLog(G, `Player ${eachPlayerId} could not gain a Wound — the Wound supply is empty.`, 'blocked', cardId);
            continue;
        }
        if (woundOutcome === 'diving-block-pending') {
            divingBlockPendingPlayerIds.push(eachPlayerId);
        }
        else {
            gainedPlayerIds.push(eachPlayerId);
        }
        // why: woundsDrawn projects the playing player's wounds only (UI economy), matching
        // heroEffectGainWound; another seat's Wound never moves it.
        if (isPlayingPlayer) {
            G.turnEconomy.woundsDrawn += 1;
        }
    }
    // why: WP-845 / D-24779 — with nobody queued this is today's call, unchanged. With players
    // queued, the summary still names the cannot-reveal players first, but its "every player
    // revealed" line would be false (the queued players have not chosen yet), so it is skipped.
    if (revealingPlayerIds.length === 0 || anyPlayerLackedMatch) {
        logRevealOrGainWoundOutcome(G, cardId, cardRef, requirement, anyPlayerLackedMatch, gainedPlayerIds, divingBlockPendingPlayerIds);
    }
    if (revealingPlayerIds.length > 0) {
        queueRevealOrGainWoundEachChoice(G, playerID, cardId, cardRef, requirement, revealingPlayerIds);
    }
}
/**
 * Queues the players who can reveal for a reveal-or-gain-a-Wound choice after the move, and
 * logs who will choose (WP-845 / D-24779).
 *
 * @param G - Game state (queue appended, log appended).
 * @param playerID - The playing (current) player.
 * @param cardId - The played card.
 * @param cardRef - The formatted card reference.
 * @param requirement - The title-cased criterion label (e.g. `Strength`).
 * @param revealingPlayerIds - The players who can reveal, in seat order.
 */
function queueRevealOrGainWoundEachChoice(G, playerID, cardId, cardRef, requirement, revealingPlayerIds) {
    enqueueRevealOrWoundChoice(G, {
        sourceCardId: cardId,
        currentPlayerID: playerID,
        revealOptionLabel: `Reveal another ${requirement} Hero`,
        revealingSeats: [...revealingPlayerIds],
    });
    pushLog(G, `${formatRevealOrWoundPlayerNames(revealingPlayerIds)} can reveal another ${requirement} Hero and will choose whether to reveal it or gain a Wound (${cardRef}).`, 'neutral', cardId);
}
/**
 * Writes the summary log line for a reveal-or-gain-wound-each resolution (WP-843 / D-24777).
 *
 * @param G - Game state (the log is appended).
 * @param cardId - The played card, for WP-438 attribution.
 * @param cardRef - The formatted card reference.
 * @param requirement - The title-cased criterion label (e.g. `Strength`).
 * @param anyPlayerLackedMatch - Whether any player could not reveal a match.
 * @param gainedPlayerIds - Players whose Wound landed.
 * @param divingBlockPendingPlayerIds - Players whose Wound a Diving Block reveal may still prevent.
 */
function logRevealOrGainWoundOutcome(G, cardId, cardRef, requirement, anyPlayerLackedMatch, gainedPlayerIds, divingBlockPendingPlayerIds) {
    if (divingBlockPendingPlayerIds.length > 0) {
        // why: D-24673 — while a Diving Block reveal is pending the Wound is not gained yet; the
        // reveal / decline line that follows gives the outcome.
        pushLog(G, `Each player without another ${requirement} Hero gains a Wound (${cardRef}): ${composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds)}.`, 'applied', cardId);
        return;
    }
    if (gainedPlayerIds.length > 0) {
        const playerNames = gainedPlayerIds.map((gainedPlayerId) => `Player ${gainedPlayerId}`).join(', ');
        pushLog(G, `Each player without another ${requirement} Hero gained a Wound (${cardRef}): ${playerNames}.`, 'applied', cardId);
        return;
    }
    // why: D-24686 — a player who lacked a match but met an empty supply already got the
    // empty-supply line; saying "every player revealed" would be false.
    if (!anyPlayerLackedMatch) {
        pushLog(G, `Every player revealed another ${requirement} Hero for ${cardRef}, so no one gained a Wound.`, 'blocked', cardId);
    }
}
/**
 * Returns the printed label for a hero reveal criterion, title-casing its slug
 * (`strength` → `Strength`, `x-men` → `X-Men`).
 *
 * // why: duplicate-first — the villain file's describeRevealRequirement is private to the
 * villain layer and takes a different shape; a third copy would justify a shared helper.
 *
 * @param criterion - The single reveal criterion.
 * @returns The criterion label for a log line.
 */
function describeHeroRevealRequirement(criterion) {
    let slug = 'matching';
    if (criterion.kind === 'hero-class') {
        slug = criterion.heroClass;
    }
    else if (criterion.kind === 'team') {
        slug = criterion.team;
    }
    const words = [];
    for (const word of slug.split('-')) {
        words.push(word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word);
    }
    return words.join('-');
}
/**
 * Returns whether a deck card matches an investigate criterion list, projecting the card's
 * runtime facts from `G.cardStats` (icon / cost) and `G.cardTraits` (hero-class / team).
 * A card with no stat / trait entry yields `undefined` for the missing facts, so the
 * criteria needing them do not match (no draw on a fabricated 0) — mirroring the reveal
 * handler's skip-a-no-stats-card posture.
 *
 * @param G - Game state (read-only).
 * @param cardId - The deck card to evaluate.
 * @param criteria - The effect's static criteria (INCLUSIVE OR).
 * @returns Whether the card satisfies at least one criterion.
 */
function investigateCardMatchesCriteria(G, cardId, criteria) {
    // why: WP-772 / D-24604 — investigated cards come from the deck window or a hand reveal, both
    // off play, so a split card is both halves' classes with its printed numbers totalled (rules
    // v23 p.49), resolved through the split-face map. Cost is face a's (equal on both halves).
    const stats = offPlayCardStats(G, cardId);
    const traits = offPlayCardTraits(G, cardId);
    const candidate = {
        attack: stats?.attack,
        recruit: stats?.recruit,
        cost: stats?.cost,
        heroClass: traits?.heroClass,
        // why: WP-703 / D-24523 — carry the second printed class so the Investigate hero-class criterion matches a dual-class card on either class.
        heroClass2: traits?.heroClass2,
        team: traits?.team,
    };
    return investigateCandidateMatches(criteria, candidate);
}
/**
 * Builds the player-facing criterion phrase for an investigate log line (e.g. "a card with
 * an attack icon", "a card that costs 3 or less", "a strength card or an x-factor-
 * investigations card"). Multiple criteria are joined with " or " (the printed inclusive OR).
 *
 * @param criteria - The effect's static criteria.
 * @returns The criterion phrase for the log line.
 */
function describeInvestigateCriteria(criteria) {
    const parts = [];
    for (const criterion of criteria) {
        parts.push(describeInvestigateCriterion(criterion));
    }
    return parts.join(' or ');
}
/**
 * Describes a single investigate criterion in player-facing English.
 *
 * @param criterion - The criterion to describe.
 * @returns The criterion phrase.
 */
function describeInvestigateCriterion(criterion) {
    if (criterion.kind === 'icon') {
        return criterion.icon === 'attack' ? 'a card with an attack icon' : 'a card with a recruit icon';
    }
    if (criterion.kind === 'cost') {
        if (criterion.comparison === 'eq') {
            return `a card that costs ${criterion.value}`;
        }
        if (criterion.comparison === 'lte') {
            return `a card that costs ${criterion.value} or less`;
        }
        return `a card that costs ${criterion.value} or more`;
    }
    if (criterion.kind === 'hero-class') {
        return `a ${criterion.heroClass} card`;
    }
    return `a ${criterion.team} card`;
}
/**
 * Hero handler for the `transform` keyword (WP-658 / D-24469).
 *
 * She-Hulk's "[keyword:Transform] this into Hurl Trucks" (the wwhk mechanic): swaps
 * the played base card for its stronger second-form, pulled out of the G.transformDeck
 * side deck (D-24468). The condition gate ("made ≥6 Recruit this turn") is the shipped
 * recruit-threshold condition (D-24354), evaluated by evaluateAllConditions BEFORE this
 * handler runs — so this handler only executes when the gate passed.
 *
 * Placement (Jeff-confirmed permanent-upgrade rule): the second-form leaves the side
 * deck and enters play in the base card's slot; the base card goes BACK to the side
 * deck (set aside). The second-form then follows the normal played-card cleanup to the
 * discard pile, so it cycles through the player's deck henceforth — a permanent deck
 * upgrade, with no net card gained (the base leaves the deck as the second-form joins it).
 *
 * Applies the second-form's printed attack/recruit (playing a card applies its printed
 * icons imperatively — applyCardPlay — and the transform makes the second-form the card
 * now in play). The base's already-applied recruit is NOT refunded — you keep the recruit
 * you made to meet the gate.
 *
 * why (deferred, Honest-Partial): the second-form's OWN ability hooks are not re-fired
 * here. Hurl Trucks' only ability is `[keyword:Smash]`, a keyword unsupported engine-wide,
 * so firing it would add nothing but a hollow record; when a future transform target
 * carries a supported onPlay ability, a follow-up can re-fire via the copy-powers
 * reentrant executeHeroEffects pattern.
 *
 * Soft no-op (AC-5): if the side deck holds no matching second-form copy (multi-player
 * contention exhausted it), the transform does not happen — logged, base stays in play,
 * never a throw (moves never throw).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Move context (unused — the swap needs no randomness/reshuffle).
 * @param playerID - Active player ID.
 * @param cardId - The played base card's CardExtId (e.g. `wwhk/she-hulk/hurl-legal-objections#3`).
 * @param _effect - The `{ type: 'transform' }` descriptor (no parameters).
 */
function heroEffectTransform(G, _ctx, playerID, cardId, _effect) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    // why: guard against G states that predate WP-658 (older test mocks with no
    // transformTargets / transformDeck). A real match always seeds both at setup;
    // an absent map/side deck is a silent no-op, never a throw (mirrors the
    // executeHeroEffects heroAbilityHooks guard).
    if (!G.transformTargets || !G.transformDeck) {
        return;
    }
    // why: strip the `#copy` suffix so the played base instance ext_id
    // (`wwhk/she-hulk/hurl-legal-objections#3`) resolves to the copy-agnostic card
    // key (`wwhk/she-hulk/hurl-legal-objections`) buildTransformTargets stored.
    const hashIndex = cardId.indexOf('#');
    const baseKey = (hashIndex === -1 ? cardId : cardId.slice(0, hashIndex));
    const targetKey = G.transformTargets[baseKey];
    if (targetKey === undefined) {
        // why: defensive — the setup parser only emits an executable transform for a
        // SUPPORTED_TRANSFORM_BASES card, every one of which is in transformTargets, so a
        // missing key is unreachable in practice. A silent no-op, never a throw.
        return;
    }
    // why: find the FIRST matching second-form copy still in the side deck. Instance
    // ids are `{targetKey}#{copy}`; the appended `#` stops a target key prefix-matching
    // a longer sibling slug. Front-of-zone, deterministic — no ctx.random.
    const targetPrefix = `${targetKey}#`;
    let targetInstanceId;
    for (const sideDeckId of G.transformDeck) {
        if (sideDeckId.startsWith(targetPrefix)) {
            targetInstanceId = sideDeckId;
            break;
        }
    }
    if (targetInstanceId === undefined) {
        // why: AC-5 — the side deck ran out of this second-form copy (multi-player
        // contention). The transform simply does not happen: log and continue. WP-434 —
        // nothing happened, so the outcome is `blocked`.
        pushLog(G, `Player ${playerID} could not Transform ${formatCardRef(G.cardDisplayData, cardId)} — no matching second-form copy remained in the transform side deck.`, 'blocked', cardId);
        return;
    }
    // Route the base card back to the side deck (set aside): a permanent deck upgrade —
    // the player's deck loses the base as it gains the second-form, no net card change.
    const baseMove = moveCardFromZone(playerZones.inPlay, G.transformDeck, cardId);
    if (!baseMove.found) {
        // why: WP-859 / D-24795 — a deferred (wait-and-see) Transform can fire after its base card
        // left play (KO'd from play, say). Swapping then would put the second form into play while
        // the base stays wherever it went: a free card. Refuse before any zone is assigned, so the
        // side deck and inPlay are untouched. Covers Hurl Legal Objections and Gamma-Draining
        // Nanites too, which have had this window since WP-658 / WP-665.
        pushLog(G, `Player ${playerID} could not Transform ${formatCardRef(G.cardDisplayData, cardId)} — it is no longer in play.`, 'blocked', cardId);
        return;
    }
    playerZones.inPlay = baseMove.from;
    G.transformDeck = baseMove.to;
    // Bring the second-form into play in the base card's slot.
    const targetMove = moveCardFromZone(G.transformDeck, playerZones.inPlay, targetInstanceId);
    G.transformDeck = targetMove.from;
    playerZones.inPlay = targetMove.to;
    // why: apply the second-form's printed attack/recruit — the transform makes it the
    // card now in play, and printed icons are applied imperatively at play time
    // (applyCardPlay). The base's already-applied recruit is intentionally NOT refunded.
    const targetStats = G.cardStats[targetInstanceId];
    const targetAttack = targetStats ? targetStats.attack : 0;
    const targetRecruit = targetStats ? targetStats.recruit : 0;
    G.turnEconomy = addResources(G.turnEconomy, targetAttack, targetRecruit);
    // why: WP-434 — a completed transform that applied the second-form is `applied` (green).
    pushLog(G, `Player ${playerID} Transformed ${formatCardRef(G.cardDisplayData, cardId)} into ${formatCardRef(G.cardDisplayData, targetInstanceId)}${formatTransformEconomyClause(targetAttack, targetRecruit)}.`, 'applied', targetInstanceId);
    // why: WP-672 / D-24487 — emit the transformResolved notable event LAST (after the
    // swap settled and the applied log pushed), observing final state — the healWounds /
    // bystanderRevealed emission precedent. G.messages is not projected to clients, so this
    // event is what drives the arena-client "Transformed!" overlay + the transform VFX beat.
    // Names resolve HERE via G.cardDisplayData (the composer stays pure), with a defensive
    // raw-ext_id fallback when display data is absent (legacy test states may omit it).
    // Guarded push: the minimal heroEffects test builder omits G.notableEvents; a real match
    // always seeds it at setup ([] in buildInitialGameState). Handlers never throw, so an
    // absent array is a silent skip, mirroring the transformTargets / transformDeck guards.
    if (Array.isArray(G.notableEvents)) {
        const baseName = resolveTransformCardName(G, cardId);
        const secondFormName = resolveTransformCardName(G, targetInstanceId);
        G.notableEvents.push({
            type: 'transformResolved',
            playerId: playerID,
            narrative: composeTransformNarrative(baseName, secondFormName),
        });
    }
}
/**
 * Resolves a card's display name from `G.cardDisplayData` for the transform
 * narrative, falling back to the raw ext_id when no display entry exists (WP-672).
 * Mirrors the `villainDeck.reveal.ts` bystanderRevealed name-resolution so the
 * `composeTransformNarrative` composer keeps its no-`G` purity.
 *
 * @param G - Game state (read-only here).
 * @param cardId - The card ext_id to resolve to a display name.
 * @returns The card's display name, or the raw ext_id when display data is absent.
 */
function resolveTransformCardName(G, cardId) {
    const display = G.cardDisplayData?.[cardId];
    if (display && typeof display.name === 'string' && display.name.length > 0) {
        return display.name;
    }
    return cardId;
}
/**
 * Builds the trailing " (+N attack, +M recruit)" clause for a transform log line,
 * omitting either half that is zero and the whole clause when the second-form has no
 * printed economy (WP-658 / D-24469). Mirrors the intent of formatBaseEconomyClause but
 * is local to keep heroEffects.execute.ts free of a moves-layer import (avoids a cycle).
 *
 * @param attack - The second-form's printed attack (>= 0).
 * @param recruit - The second-form's printed recruit (>= 0).
 * @returns The clause including a leading space, or an empty string when both are zero.
 */
function formatTransformEconomyClause(attack, recruit) {
    const parts = [];
    if (attack > 0) {
        parts.push(`+${attack} attack`);
    }
    if (recruit > 0) {
        parts.push(`+${recruit} recruit`);
    }
    if (parts.length === 0) {
        return '';
    }
    return ` (${parts.join(', ')})`;
}
/**
 * Dispatches each effect of a Digest / Indigestion branch through the reentrant
 * executeSingleEffect (WP-735 / D-24555), in printed order.
 *
 * A `for...of` loop (no `.reduce()` in effect application). Each branch effect is an
 * already-parsed inline effect (attack / recruit / draw / rescue), so this reuses the
 * shipped executors — the copy-powers / steal-abilities reentrant precedent.
 *
 * @param G - Game state (mutated by each branch effect).
 * @param ctx - Context, forwarded to each effect's handler.
 * @param playerID - Active player ID.
 * @param cardId - The played hero card's CardExtId.
 * @param branchEffects - The branch's already-parsed inline effects.
 * @returns void.
 */
function runDigestIndigestionBranch(G, ctx, playerID, cardId, branchEffects) {
    for (const branchEffect of branchEffects) {
        executeSingleEffect(G, ctx, playerID, cardId, branchEffect);
    }
}
/**
 * Decides whether a fused effect's "Instead … both" upgrade holds (WP-740 / D-24562, D-24563).
 *
 * No bothCondition → false. Otherwise the condition goes through evaluateAllConditions, which is
 * count-aware: a doubled "[team:X][team:X]" line arrives as one bothCondition carrying
 * requiredMatches, so it needs that many OTHER played-this-turn cards matching it (the rulebook's
 * "Critical Hit" rule), while a single-icon line (the Core-4) needs one.
 *
 * why: D-24563 — the count now rides bothCondition.requiredMatches and the general evaluator
 * enforces it, so the old WP-740 descriptor count field and its digest-only branch are retired.
 *
 * @param G - Game state (read-only here).
 * @param playerID - Active player ID.
 * @param cardId - The played card; excluded from the count by exact instance id.
 * @param effect - The 'digest-indigestion' or 'day-night-both' descriptor.
 * @returns Whether both branches should run.
 */
function isDigestBothConditionMet(G, playerID, cardId, effect) {
    return effect.bothCondition !== undefined && evaluateAllConditions(G, playerID, [effect.bothCondition], cardId);
}
/**
 * Executes a 'digest-indigestion' effect (WP-735 / D-24555): the Venomverse
 * "Digest N / Indigestion" Victory-Pile-count branch for an allowlisted card.
 *
 * Reads the player's Victory-Pile card count (READ-ONLY — the pile is never mutated
 * here) and runs the printed branch: if the "Instead, you get both" upgrade condition
 * holds, runs BOTH branches; else the Digest branch when the count meets the threshold,
 * else the mutually-exclusive Indigestion branch (keywords-full ids 54/55). Each branch's
 * effects dispatch through the reentrant executeSingleEffect.
 *
 * @param G - Game state (mutated by the dispatched branch effects).
 * @param ctx - Context, forwarded to each branch effect's handler.
 * @param playerID - Active player ID.
 * @param cardId - The played hero card's CardExtId.
 * @param effect - The 'digest-indigestion' descriptor { digestThreshold, digestEffects, indigestionEffects?, bothCondition? }.
 * @returns void.
 */
function heroEffectDigestIndigestion(G, ctx, playerID, cardId, effect) {
    // why: WP-735 / D-24555 — safe-skip a malformed effect. digestThreshold/digestEffects are
    // optional on the shared descriptor type; a 'digest-indigestion' effect that lacks either is
    // malformed. Return via explicit narrowing BEFORE reading the count so we never evaluate
    // `count >= undefined` (→ NaN → a silent wrong branch).
    const { digestThreshold, digestEffects } = effect;
    if (digestThreshold === undefined || digestEffects === undefined) {
        return;
    }
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return;
    }
    const indigestionEffects = effect.indigestionEffects ?? [];
    // why: WP-735 / D-24555 — Digest N is a READ-ONLY Victory-Pile-size threshold (glossary id 54):
    // count the pile, never mutate it. All card types in the Victory Pile count.
    const victoryPileCount = playerZones.victory.length;
    // why: WP-735 / D-24555 — the printed "[hc:X]: Instead, you get both." upgrade OVERRIDES the
    // Digest threshold gate: when its class/team condition holds, BOTH branches run regardless of
    // count (the standard Legendary "Instead" override; the printed line is the primary source).
    const runsBoth = isDigestBothConditionMet(G, playerID, cardId, effect);
    if (runsBoth) {
        runDigestIndigestionBranch(G, ctx, playerID, cardId, digestEffects);
        runDigestIndigestionBranch(G, ctx, playerID, cardId, indigestionEffects);
        return;
    }
    // why: WP-735 / D-24555 — glossary id 55: Indigestion is the MUTUALLY-EXCLUSIVE fallback used
    // ONLY when the pile holds fewer than N ("you cannot choose Indigestion when Digest is
    // available"). A single-branch card (empty indigestionEffects) runs nothing below threshold.
    if (victoryPileCount >= digestThreshold) {
        runDigestIndigestionBranch(G, ctx, playerID, cardId, digestEffects);
    }
    else if (indigestionEffects.length === 0) {
        // why: WP-754 / D-24581 — a single-branch Digest card (Hungry for Action, Cauldron of the
        // Cosmos) below its threshold runs nothing; without a line the ability looks silently
        // dropped. One neutral line names the threshold and the count. G.messages is hash-excluded
        // (D-24081), so this adds no determinism impact.
        pushLog(G, `Player ${playerID}'s ${resolveCardName(G.cardDisplayData, cardId)} — Digest ${digestThreshold} not met (${victoryPileCount} in Victory Pile); no effect.`, 'neutral', cardId);
    }
    else {
        runDigestIndigestionBranch(G, ctx, playerID, cardId, indigestionEffects);
    }
}
/**
 * Hero handler for the `blood-frenzy` keyword (WP-765 / D-24598).
 *
 * "You get +1 Attack for each different Victory Point value among the cards in your Victory
 * Pile." The count comes from the shared countDistinctVictoryPointValues (the helper WP-760's
 * villain fight-cost Blood Frenzy also consumes), read at resolve time.
 *
 * @param G - Game state (turnEconomy mutated).
 * @param _ctx - Unused.
 * @param playerID - Active player ID.
 * @param cardId - The played hero card's CardExtId.
 * @param _effect - The 'blood-frenzy' descriptor (no magnitude).
 * @returns void.
 */
function heroEffectBloodFrenzy(G, _ctx, playerID, cardId, _effect) {
    const distinctValues = countDistinctVictoryPointValues(G, playerID);
    G.turnEconomy = addResources(G.turnEconomy, distinctValues, 0);
    pushLog(G, `Player ${playerID} gained +${distinctValues} attack from ${formatCardRef(G.cardDisplayData, cardId)} (Blood Frenzy: ${distinctValues} different Victory Point value(s)).`, 'applied', cardId);
}
/**
 * Hero handler for the `blood-frenzy-recruit` keyword (WP-765 / D-24598) — Morbius Mesmerize's
 * "Blood Frenzy, gaining Recruit instead of Attack". Same shared distinct-VP count, granted as
 * recruit.
 *
 * @param G - Game state (turnEconomy mutated).
 * @param _ctx - Unused.
 * @param playerID - Active player ID.
 * @param cardId - The played hero card's CardExtId.
 * @param _effect - The 'blood-frenzy-recruit' descriptor (no magnitude).
 * @returns void.
 */
function heroEffectBloodFrenzyRecruit(G, _ctx, playerID, cardId, _effect) {
    const distinctValues = countDistinctVictoryPointValues(G, playerID);
    G.turnEconomy = addResources(G.turnEconomy, 0, distinctValues);
    pushLog(G, `Player ${playerID} gained +${distinctValues} recruit from ${formatCardRef(G.cardDisplayData, cardId)} (Blood Frenzy: ${distinctValues} different Victory Point value(s)).`, 'applied', cardId);
}
/**
 * Hero handler for the fused `day-night-both` keyword (WP-765 / D-24598).
 *
 * If the printed "[X]: Instead, you get both." upgrade holds, runs the Sunlight branch then the
 * Moonlight branch; otherwise runs the branch computeDayNight selects; on a tie ('neither') runs
 * nothing and logs why. Each branch effect dispatches through the reentrant executeSingleEffect.
 *
 * // why: the digest-indigestion composite precedent (D-24555). HeroCondition has no OR, so the
 * three printed lines cannot be separate hooks without double-firing a branch under the upgrade;
 * the upgrade check reuses isDigestBothConditionMet. Nanite's four [team:x-men] arrive as one
 * bothCondition carrying requiredMatches 4, which the evaluator enforces (D-24563). Day/night is
 * read here, as the hook resolves.
 *
 * @param G - Game state (mutated by the dispatched branch effects).
 * @param ctx - Context, forwarded to each branch effect's handler.
 * @param playerID - Active player ID.
 * @param cardId - The played hero card's CardExtId.
 * @param effect - The 'day-night-both' descriptor { sunlightEffects, moonlightEffects, bothCondition? }.
 * @returns void.
 */
function heroEffectDayNightBoth(G, ctx, playerID, cardId, effect) {
    const sunlightEffects = effect.sunlightEffects ?? [];
    const moonlightEffects = effect.moonlightEffects ?? [];
    if (isDigestBothConditionMet(G, playerID, cardId, effect)) {
        runDigestIndigestionBranch(G, ctx, playerID, cardId, sunlightEffects);
        runDigestIndigestionBranch(G, ctx, playerID, cardId, moonlightEffects);
        return;
    }
    const dayNight = computeDayNight(G);
    if (dayNight === 'sunlight') {
        runDigestIndigestionBranch(G, ctx, playerID, cardId, sunlightEffects);
    }
    else if (dayNight === 'moonlight') {
        runDigestIndigestionBranch(G, ctx, playerID, cardId, moonlightEffects);
    }
    else {
        // why: a tie leaves neither Sunlight nor Moonlight in effect, so the card does nothing; one
        // neutral line keeps that observable (G.messages is hash-excluded, D-24081).
        pushLog(G, `Player ${playerID}'s ${resolveCardName(G.cardDisplayData, cardId)} — neither Sunlight nor Moonlight is in effect; no effect.`, 'neutral', cardId);
    }
}
/**
 * Hero handler for the `excessive-violence` keyword (WP-736 / D-24556).
 *
 * Venomverse's "Excessive Violence" (keywords-full id 30). `executeHeroEffects` fires this at
 * PLAY time (it does not filter by hook timing), and its ONLY job at play is to ENROL the played
 * card into the turn-scoped EV ledger (`G.turnEconomy.excessiveViolencePlayedCards`) — it applies
 * NONE of the inner draw / recruit / rescue / optional-ko effects. Those fire later, and only if
 * the player fights "using Excessive Violence", from the fight move's `fireExcessiveViolencePlays`.
 *
 * Safe-skips (no enrolment, no throw) when the fused wrapper has no inner effects — a malformed or
 * non-allowlisted descriptor whose `excessiveViolenceEffects` is undefined.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param _ctx - Context (unused — enrolment reads/writes only G.turnEconomy).
 * @param playerID - Active player ID (unused for enrolment; the ledger is the active player's).
 * @param cardId - The played EV card's CardExtId to enrol.
 * @param effect - The 'excessive-violence' wrapper descriptor { excessiveViolenceEffects }.
 */
function heroEffectExcessiveViolence(G, _ctx, playerID, cardId, effect) {
    // why: WP-736 / D-24556 — safe-skip a wrapper with no inner effects (a malformed or
    // non-allowlisted descriptor). Enrolling an empty EV card would make it fire nothing at fight
    // time anyway; skipping keeps the ledger free of no-op cards.
    if (effect.excessiveViolenceEffects === undefined) {
        return;
    }
    // why: WP-736 / D-24556 — ENROL only. The inner effects fire from the fight-time driver, never
    // here. enrollExcessiveViolenceCard appends the card to the turn-scoped ledger (materializing it
    // on the first EV play), carried across same-turn rebuilds by carryConversionFlag.
    G.turnEconomy = enrollExcessiveViolenceCard(G.turnEconomy, cardId);
    // why: WP-434 — enrolment is `applied` (green): it changed turn state (the card is now armed to
    // fire on an Excessive Violence fight) even though no resource total moved and no card was drawn.
    pushLog(G, `Player ${playerID} readied ${formatCardRef(G.cardDisplayData, cardId)} for Excessive Violence — its ability fires if you fight using Excessive Violence this turn.`, 'applied', cardId);
}
/**
 * Fires every enrolled "Excessive Violence" ability, in play (enrolment) order (WP-736 / D-24556).
 *
 * Called from the MOVE BODY of fightVillain / fightMastermind — NEVER from the shared
 * defeatCityVillainCore / defeatMastermindTacticCore (which non-fight defeat paths reach without
 * the `useExcessiveViolence` arg) — AFTER the defeat is settled and the extra `[attack]` is debited.
 * For each `cardId` in the turn-scoped ledger, it finds that card's `excessive-violence` hook in
 * `G.heroAbilityHooks` and dispatches each descriptor in its `excessiveViolenceEffects` through the
 * reentrant `executeSingleEffect` (the copy-powers / steal-abilities / digest-indigestion precedent).
 *
 * Deterministic: fixed enrolment order, a `for...of` loop (no `.reduce()`), no `ctx.random` beyond
 * what an inner executor already uses. Never throws (moves never throw); a missing hook or empty
 * ledger is a silent no-op.
 *
 * After the drain, when ≥1 enrolled EV card actually fired an inner effect, it emits ONE
 * `excessiveViolenceFired` notable event + ONE distinct frame log beat (WP-746 / D-24569) — the
 * overspend MOMENT was otherwise silent (each inner effect logs on its own, but the +1-attack fire
 * raised no distinct signal, so the payoff read as "nothing happened"). Additive: no game outcome,
 * zone, `G.counters`, or inner-effect log changes.
 *
 * @param G - Game state (mutated by each fired inner effect; the fire beat appends to G.notableEvents / G.messages).
 * @param ctx - Context, forwarded to each inner effect's handler.
 * @param playerID - The fighting player's ID (whose EV ledger is fired).
 * @returns void.
 */
export function fireExcessiveViolencePlays(G, ctx, playerID) {
    const playedCards = G.turnEconomy.excessiveViolencePlayedCards ?? [];
    // why: WP-736 / D-24556 — defensive guard mirroring executeHeroEffects: a G state that predates
    // the hooks (older test fixtures) has no heroAbilityHooks. `?? []` keeps this a silent no-op
    // (moves never throw) rather than iterating undefined.
    const allHooks = G.heroAbilityHooks ?? [];
    // why: WP-746 / D-24569 — count of enrolled EV CARDS that fired ≥1 inner effect (NOT the
    // inner-effect count: a card with several inner effects is one "Excessive Violence ability").
    // A LOCAL variable used only to build the frame narrative — never written to G.counters.
    let firedCardCount = 0;
    for (const cardId of playedCards) {
        // why: WP-736 / D-24556 — find this enrolled card's fused excessive-violence hook. A copy with
        // no such hook (defensive: only allowlisted cards enrol) contributes nothing.
        const hooks = getHooksForCard(allHooks, cardId);
        let cardFiredAny = false;
        for (const hook of hooks) {
            for (const effect of hook.effects ?? []) {
                if (effect.type !== 'excessive-violence') {
                    continue;
                }
                for (const innerEffect of effect.excessiveViolenceEffects ?? []) {
                    executeSingleEffect(G, ctx, playerID, cardId, innerEffect);
                    cardFiredAny = true;
                }
            }
        }
        if (cardFiredAny) {
            firedCardCount += 1;
        }
    }
    // why: WP-746 / D-24569 — emit the excessiveViolenceFired beat LAST, once per fight, only when
    // ≥1 enrolled EV card actually dispatched an inner effect (firedCardCount > 0). A declined EV
    // fight or an empty ledger fires nothing. The frame log reuses the existing `applied` LogOutcome
    // (the overspend action did its thing — no new LOG_OUTCOMES member) and is ONE distinct beat over
    // the inner effects' own lines. G.messages is excluded from finalStateHash (D-24081), so the log
    // is replay-safe. The notable-event push is guarded on Array.isArray(G.notableEvents) because the
    // minimal test builder omits it (a real match seeds [] at setup) and moves never throw — the
    // heroEffectResolved emission precedent (~line 2447). Byte-inert on both pinned hashes: EV is
    // vnom-only and neither the core-only sentinel nor the empty PRE_WP080 replay plays an EV card,
    // so no EV fire reaches either pinned trajectory (the WP-697 heroEffectResolved outcome; VERIFIED
    // at execution). The same composed narrative drives both the frame log and the event so the log
    // and the overlay read identically.
    if (firedCardCount > 0) {
        const narrative = composeExcessiveViolenceFiredNarrative(`Player ${playerID}`, firedCardCount);
        pushLog(G, narrative, 'applied');
        if (Array.isArray(G.notableEvents)) {
            G.notableEvents.push({
                type: 'excessiveViolenceFired',
                playerId: playerID,
                narrative,
            });
        }
    }
    // why: WP-754 / D-24581 — the EV abilities fire synchronously in play order, so a KO-or-keep
    // reveal (Gruesome Feast) can be followed by an EV draw (Rending Claws) that takes the
    // snapshotted card. Re-reveal a stale KO-or-keep front so the choice shows the current top
    // instead of clearing as moot. A no-op when no stale KO-or-keep entry is queued.
    refreshStaleKoOrKeepFront(G, ctx);
}
export const HERO_EFFECT_HANDLERS = {
    draw: heroEffectDraw,
    attack: heroEffectAttack,
    recruit: heroEffectRecruit,
    ko: heroEffectKo,
    rescue: heroEffectRescue,
    reveal: heroEffectReveal,
    'attack-per-count': heroEffectAttackPerCount,
    'recruit-per-count': heroEffectRecruitPerCount,
    // why: WP-714 / D-24537 — Ultron's Genetic Experimentation ("[hc:tech]: Kidnap a
    // Bystander for each other [hc:tech] Ally you played this turn"): captures
    // N = magnitude × ⌊count / perEach⌋ Bystanders (tech-heroes-played-this-turn, self-
    // exclusive) to the first City villain (Mastermind fallback), non-interactive + supply-bounded.
    'kidnap-per-count': heroEffectKidnapPerCount,
    'optional-ko-reward': heroEffectOptionalKoReward,
    'optional-ko-hand-discard': heroEffectOptionalKoHandDiscard,
    'ko-wound-reward': heroEffectKoWoundReward,
    // why: WP-721 / D-24542 — the rewardless sibling of ko-wound-reward (X-23's Healing
    // Factor Genome, Peter Parker's Hot Bowl of Soup): KOs one Wound (hand-first-else-discard)
    // with NO reward dispatch; auto-resolves (pure deck-thinning upside).
    'ko-wound': heroEffectKoWound,
    'optional-put-bottom-hq': heroEffectOptionalPutBottomHq,
    'put-any-number-bottom-hq': heroEffectPutAnyNumberBottomHq,
    'put-bottom-hq-icon-reward': heroEffectPutBottomHqIconReward,
    'victory-villain-attack': heroEffectVictoryVillainAttack,
    'draw-or-empowered': heroEffectDrawOrEmpowered,
    'count-scaled-choose': heroEffectCountScaledChoose,
    'return-zero-cost-discard': heroEffectReturnZeroCostDiscard,
    'discard-to-play': heroEffectDiscardToPlay,
    // why: D-24156 — one shared handler under both keys; it branches on effect.type
    // (self = active player, each = every player).
    'gain-wound-self': heroEffectGainWound,
    'gain-wound-each': heroEffectGainWound,
    'shuffle-discard-empty-reward': heroEffectShuffleDiscardEmptyReward,
    // why: WP-486 / D-24291 — Silent Sniper's "Defeat a Villain or Mastermind that
    // has a Bystander." (defeats one eligible target via the shared fight-defeat core,
    // or parks a PendingDefeatChoice when ≥2 qualify).
    'defeat-with-bystander': heroEffectDefeatWithBystander,
    // why: WP-535 / D-24345 — Rogue's Copy Powers "Play this card as a copy of another
    // Hero you played this turn." (re-fires the chosen Hero's ability + grants the copied
    // class, or parks a PendingCopyPowersChoice when ≥2 Heroes qualify).
    'copy-powers': heroEffectCopyPowers,
    // why: WP-580 / D-24389 — God of Thunder's "You can use Recruit as Attack this turn."
    // (sets the turn-scoped conversion flag; fights then draw on unspent recruit).
    'recruit-as-attack': heroEffectRecruitAsAttack,
    // why: WP-731 / D-24552 — Venompool's Shenanigans "you can't draw any more cards this
    // turn." (sets the turn-scoped drawsLocked flag; heroEffectDraw then draws 0 while set).
    'no-more-draws': heroEffectNoMoreDraws,
    // why: WP-592 / D-24401 — Rogue's Steal Abilities "Each player discards the top card of
    // their deck. Play a copy of each of those cards." (each player discards their deck top,
    // then the Steal Abilities player copies each = economy + reentrant executeHeroEffects
    // re-fire; recursion guard excludes a discarded steal-abilities OR copy-powers).
    'steal-abilities': heroEffectStealAbilities,
    // why: WP-564 / D-24373 — "Investigate for <criterion>" (static-criterion + draw): looks
    // at the top N, draws the first matching card in look order, bottoms the rest.
    investigate: heroEffectInvestigate,
    // why: WP-658 / D-24469 — "[keyword:Transform] this into <second-form>" (wwhk): pulls the
    // matching second-form out of G.transformDeck into play, routes the base card back to the
    // side deck (permanent upgrade), and applies the second-form's printed attack/recruit.
    transform: heroEffectTransform,
    // why: WP-659 / D-24470 — "Each player may reveal another [team]/[hc] Hero. Each player who
    // does draws a card." (Psychic Link): each player holding a criterion-matching Hero in hand
    // draws 1 (auto-reveal, seat order). The co-located token is captured as the reveal
    // criterion at setup, not read as a play-gate.
    'reveal-from-hand': heroEffectRevealFromHand,
    // why: WP-663 / D-24474 — Shadowed Thoughts' "You may play the top Villain-Deck card →
    // +2 Attack": parks a PendingPlayVillainTopChoice resolved by resolvePlayVillainTopChoice.
    'optional-play-villain-top': heroEffectOptionalPlayVillainTop,
    // why: WP-668 / D-24481 — Jade Giantess' "For every 2 Recruit you made this turn,
    // Reveal the top card of the Hero Deck, put it on the bottom of that deck, and you
    // get that card's printed Attack": a synchronous count-scaled reveal — floor(recruit
    // / divisor) reveals of G.heroDeck[0], each granting its printed attack and rotating
    // to the bottom. No pending choice; magnitude carries the divisor.
    'reveal-herodeck-attack': heroEffectRevealHeroDeckAttack,
    // why: WP-676 / D-24492 — the Smash keyword ("you may discard another card from your
    // hand; if you do, +N attack"): parks a PendingSmashDiscard resolved by
    // resolveSmashDiscard (discard a hand card for +magnitude Attack, or decline).
    smash: heroEffectSmash,
    // why: WP-678 / D-24494 (supersedes D-24060) — the two Undercover source-shape effects
    // send a card to the Victory Pile (worth 1 VP). 'undercover-hand-shield-hero' auto-sends /
    // parks a PendingUndercoverChoice (≥2 eligible); 'undercover-officer-stack' is deterministic.
    'undercover-hand-shield-hero': heroEffectUndercoverHandShieldHero,
    'undercover-officer-stack': heroEffectUndercoverOfficerStack,
    // why: WP-683 / D-24500 — Deadpool's Here, Hold This for a Second ("A Villain of your
    // choice captures a Bystander."): attaches via attachBystanderToVillain (Mastermind
    // fallback at 0 Villains, empty-supply no-op, 1 → auto, ≥2 → active-scoped PendingSeatChoice
    // kind 'here-hold-this' resolved by resolveSeatChoice).
    'here-hold-this': heroEffectHereHoldThis,
    // why: WP-683 / D-24500 — Deadpool's Random Acts of Unkindness ("You may gain a Wound to
    // your hand. Then each player passes a card from their hand to the player on their left."):
    // parks an active-scoped wound choice (kind 'random-acts-wound') that chains a simultaneous
    // multi-seat pass-left (kind 'random-acts-pass-left'), both resolved by resolveSeatChoice.
    'random-acts': heroEffectRandomActs,
    // why: WP-681 / D-24498 — Deadpool's Do-Over ("discard the rest of your hand and draw
    // four cards", first-Hero-gated): parks a PendingDoOver resolved by resolveDoOver
    // (accept = discard hand + draw 4, or decline).
    'do-over': heroEffectDoOver,
    // why: WP-681 / D-24498 — Nick Fury's Battlefield Promotion ("KO a [team:shield] Hero
    // from hand/discard; if you do, gain a S.H.I.E.L.D. Officer to your hand"): parks a
    // PendingOptionalKoReward (koTeamFilter 'shield', rewardType 'gain-officer-hand')
    // resolved by resolveOptionalKoReward.
    'optional-ko-shield-officer': heroEffectOptionalKoShieldOfficer,
    // why: WP-682 / D-24499 — Nick Fury's Pure Fury ("Defeat any Villain or Mastermind whose
    // Attack is less than the number of S.H.I.E.L.D. Heroes in the KO pile"): a FREE defeat
    // that reuses the defeat-with-bystander shared free-defeat path (0/1/≥2 → no-op / auto /
    // park PendingDefeatChoice choiceType 'pure-fury', resolved by resolveDefeatChoice).
    'pure-fury': heroEffectPureFury,
    // why: WP-700 / D-24519 — "Draw N cards. Then put a card from your hand on top of your deck."
    // (Gambit's Stack the Deck + siblings): draws `magnitude` cards via drawFromPlayerDeck then
    // parks a MANDATORY PendingPutHandOnDeckTop resolved by resolvePutHandOnDeckTop (place one
    // hand card on the deck top; no decline). Carries a magnitude (the draw count) → NOT in
    // NO_MAGNITUDE_KEYWORDS.
    'put-hand-on-deck-top': heroEffectPutHandOnDeckTop,
    'reveal-top-dispose': heroEffectRevealTopDispose,
    'reveal-top-dispose-others': heroEffectRevealTopDisposeOthers,
    // why: D-24558 — co2e Hypnotic Charm's "[hc:covert]: You may KO the card you revealed from your
    // own deck.": flags the active player's own revealed top (isKoAllowed) in the most recent pending
    // reveal-top choice so resolveRevealTopDispose accepts the optional 'ko'. NO magnitude.
    'reveal-top-dispose-ko': heroEffectRevealTopDisposeKo,
    // why: WP-719 / D-24541 — Hawkeye's Covering Fire ("[hc:tech]: Choose one: each other player
    // draws a card or each other player discards a card"): parks a PendingCoveringFireChoice for
    // the active player, resolved by resolveCoveringFireChoice (draw/discard applied to each other
    // seat). Carries NO magnitude → in NO_MAGNITUDE_KEYWORDS.
    'covering-fire': heroEffectCoveringFire,
    // why: WP-795 / D-24664 — core Storm's Spinning Cyclone ("You may move a Villain to a new city
    // space. Rescue any Bystanders captured by that Villain."; swap if occupied): parks a
    // PendingMoveVillainChoice for the active player when a Villain is in the City, resolved by
    // resolveMoveVillainChoice (move / swap + moved-Villain-only rescue, or decline). Carries NO
    // magnitude → in NO_MAGNITUDE_KEYWORDS.
    'spinning-cyclone': heroEffectSpinningCyclone,
    // why: WP-803 / D-24692 — Man / Woman Out of Time (rules v23 L1667-1694): after the card's
    // abilities, set it aside (out of inPlay into G.outOfTimeCards[playerID]) and record it in the
    // turn's Out of Time ledger so it still counts as played; replayOutOfTimeCards plays it again on
    // the owner's next start → main advance. Copies never go Out of Time. Carries NO magnitude → in
    // NO_MAGNITUDE_KEYWORDS.
    'out-of-time': heroEffectOutOfTime,
    // why: WP-735 / D-24555 — the Venomverse "Digest N / Indigestion" Victory-Pile-count branch:
    // heroEffectDigestIndigestion reads G.playerZones[pid].victory.length (READ-ONLY) and dispatches
    // the Digest branch (count ≥ threshold), the Indigestion fallback (below threshold), or BOTH
    // (when the printed "Instead … both" upgrade condition holds) via the reentrant executeSingleEffect.
    // Carries NO top-level magnitude → in NO_MAGNITUDE_KEYWORDS.
    'digest-indigestion': heroEffectDigestIndigestion,
    // why: WP-736 / D-24556 — the Venomverse "Excessive Violence" fight-overspend keyword:
    // heroEffectExcessiveViolence ENROLS the played card into the turn-scoped ledger at play time;
    // the fight move's fireExcessiveViolencePlays fires each enrolled card's excessiveViolenceEffects
    // (draw / +recruit / rescue / optional-ko) at fight resolution when the player spends the extra
    // [attack]. Carries NO top-level magnitude → in NO_MAGNITUDE_KEYWORDS.
    'excessive-violence': heroEffectExcessiveViolence,
    // why: WP-753 / D-24580 — "Reveal the top three cards of your deck. Draw one of them, discard
    // one, and KO one.": tops up (D-24285), snapshots the top three and parks a block-all
    // PendingRevealThreeAssign resolved by resolveRevealThreeAssign. NO magnitude.
    'reveal-three-assign': heroEffectRevealThreeAssign,
    // why: WP-753 / D-24580 — Crystal of Kadavus's "Do this ability again.": bumps the just-parked
    // entry's remainingRepeats (the resolve move re-reveals). NO magnitude.
    'reveal-three-assign-again': heroEffectRevealThreeAssignAgain,
    // why: WP-754 / D-24581 — "You may discard a card. If you do, draw a card.": parks a
    // draw-reward entry on the Smash queue, resolved by resolveSmashDiscard (discard then draw
    // `magnitude`, or decline).
    'optional-discard-draw': heroEffectOptionalDiscardDraw,
    // why: WP-754 / D-24581 — "Reveal the top card of your deck. You may KO it.": parks a KO-or-keep
    // entry on the reveal-top-dispose queue, resolved by resolveRevealTopDispose ('ko' or 'top').
    'reveal-top-may-ko': heroEffectRevealTopMayKo,
    // why: WP-765 / D-24598 — hero Blood Frenzy: +N attack (or recruit), N = distinct VP values in
    // the active player's Victory Pile (shared economy/bloodFrenzy.logic.ts). NO magnitude.
    'blood-frenzy': heroEffectBloodFrenzy,
    'blood-frenzy-recruit': heroEffectBloodFrenzyRecruit,
    // why: WP-765 / D-24598 — the fused Sunlight / Moonlight / "Instead, you get both" composite:
    // both branches under the upgrade, else the computeDayNight branch, else nothing. NO magnitude.
    'day-night-both': heroEffectDayNightBoth,
    // why: WP-767 / D-24600 — Snarling Fangs' "you may KO one of your Heroes": parks a no-reward
    // optional-ko-reward entry scoped to hand + played this turn, Heroes only. NO magnitude.
    'optional-ko-your-hero': heroEffectOptionalKoYourHero,
    // why: WP-780 / D-24619 — cvwr Penumbra's "play both sides": sets the turn-scoped
    // isPlayBothSidesActive flag; playCard then plays a later split card's faces a and b via
    // playBothSplitFaces instead of parking the picker. NO magnitude.
    'play-both-sides': heroEffectPlayBothSides,
    // why: WP-794 / D-24663 — "Any Villain you fight on the <space> this turn gets -N" / "The
    // Mastermind gets -N this turn": appends a turn-scoped reduction that resolveFightCost /
    // resolveMastermindFightCost subtract. Carries a magnitude (the amount).
    'fight-cost-reduction': heroEffectFightCostReduction,
    // why: WP-826 / D-24759 — Mobilize for War's "KO up to two [team:shield] Heroes from your
    // hand and/or discard pile": parks up to N no-reward shield-only hand/discard entries on the
    // optional-ko-reward queue (koZones hand / discard, koTeamFilter 'shield'), each resolved by
    // resolveOptionalKoReward. Carries a magnitude (the KO cap) → NOT in NO_MAGNITUDE_KEYWORDS.
    'ko-up-to-shield-hand-discard': heroEffectKoUpToShieldHandDiscard,
    // why: WP-826 / D-24759 — Mobilize for War's Savior line "For each card KO'd this way, you
    // get +1 Attack": upgrades this card's just-parked no-reward entries (rewardType /
    // rewardMagnitude) to an attack:N reward. Carries a magnitude → NOT in NO_MAGNITUDE_KEYWORDS.
    'attack-per-ko-this-way': heroEffectAttackPerKoThisWay,
    // why: WP-826 / D-24759 — Reassign to Civilian Duty's "You may KO a [team:shield] Hero that
    // you played this turn. If you do, rescue a Bystander.": parks one inPlay shield-only entry
    // (koZones ['inPlay'], koTeamFilter 'shield') with a rescue reward on the optional-ko-reward
    // queue. Carries a magnitude (Bystanders rescued) → NOT in NO_MAGNITUDE_KEYWORDS.
    'optional-ko-shield-played-rescue': heroEffectOptionalKoShieldPlayedRescue,
    // why: WP-828 / D-24761 — Winter Soldier's 2>4: queues one owed Man Out of Time grant for a hand
    // Hero, resolved after the play by the onMove opener. NO magnitude.
    'grant-out-of-time': heroEffectGrantOutOfTime,
    // why: WP-843 / D-24777 — co2e Crazed Rampage's "Each player reveals another [hc:strength]
    // Hero or gains a Wound": each player who cannot reveal another matching Hero gains a Wound
    // through gainWoundWithOutcome (auto-reveal, sorted seat order). NO magnitude.
    'reveal-or-gain-wound-each': heroEffectRevealOrGainWoundEach,
    // why: WP-844 / D-24778 — "Gain a [rule:Sidekick]": up to N Sidekicks from the top of the
    // Sidekick Stack into the playing player's discard, outside the once-per-turn recruit. Carries
    // a magnitude → NOT in NO_MAGNITUDE_KEYWORDS.
    'gain-sidekick': heroEffectGainSidekick,
    // why: WP-850 / D-24786 — Bishop's Firepower from the Future: discard the top N, gain their printed
    // Attack, record them; then (X-Men gate) KO any number of those cards via the KO-from-discard choice.
    'discard-top-printed-attack': heroEffectDiscardTopPrintedAttack,
    'ko-discarded-this-way': heroEffectKoDiscardedThisWay,
    // why: WP-860 / D-24796 — Commune with the Spirit World: parks the cost seat choice; the discard
    // picks and the KO pick chain from resolveSeatChoice.
    'discard-artifact-or-three-then-ko': heroEffectDiscardArtifactOrThreeThenKo,
    // why: WP-857 / D-24793 — hero Feast = "KO the top card of your deck". It reuses the shipped
    // KO-or-keep reveal unchanged: the deck top is shown in the owner-only prompt, then the player
    // KOs or keeps it (RS-1 reveal-before-choose), resolved by resolveRevealTopDispose.
    feast: heroEffectRevealTopMayKo,
};
// ---------------------------------------------------------------------------
// Single effect dispatch
// ---------------------------------------------------------------------------
/**
 * Executes a single hero effect descriptor.
 *
 * Validates magnitude, checks keyword support, then dispatches to the
 * registered handler in HERO_EFFECT_HANDLERS. Returns without mutation for
 * unsupported keywords or invalid magnitudes.
 *
 * // why: WP-409 / D-24221 — returns whether the effect FIRED (reached its
 * // HERO_EFFECT_HANDLERS dispatch). `false` on any safe skip (unsupported
 * // keyword, invalid magnitude, or the unreachable undefined-handler guard);
 * // `true` after the handler runs. executeHeroEffects tallies these into the
 * // observability-only lastPlayEffectsFired count. Existing callers that ignore
 * // the return are unaffected (void -> boolean is backward-compatible).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - Context (narrowed to ShuffleProvider for draw).
 * @param playerID - Active player ID.
 * @param cardId - The played hero card's CardExtId.
 * @param effect - The effect descriptor to execute.
 * @returns `true` if the effect reached its handler and fired; `false` if safe-skipped.
 */
// why: D-24019 — exported so resolveOptionalKoReward can dispatch the reward to
// the existing executor (rescue / draw / attack / recruit) instead of
// re-implementing it. The KO-then-reward path passes a synthesized
// { type: rewardType, magnitude: rewardMagnitude } descriptor.
export function executeSingleEffect(G, ctx, playerID, cardId, effect) {
    const keyword = effect.type;
    // why: unsupported keywords are safely ignored in MVP. Only the keywords in
    // MVP_KEYWORDS execute; 'wound' and 'conditional' are deferred.
    if (!MVP_KEYWORDS.has(keyword)) {
        return false;
    }
    // why: 'ko' and NO_MAGNITUDE_KEYWORDS members ('rescue', 'reveal') bypass the
    // pre-check magnitude gate — 'ko' targets the played card (no magnitude), 'rescue'
    // defaults its magnitude to 1, and 'reveal' moves ALL its magnitude gating into the
    // translation (revealRulesForLegacyKeyword) + the per-rule predicates, so the
    // no-magnitude and M=0-valid reveals still fire (D-24024 / pre-flight PS-1). Every
    // other MVP keyword requires a valid magnitude here.
    if (failsMagnitudePreGate(effect)) {
        return false;
    }
    // why: data-driven dispatch (WP-251 / D-24022). An undefined handler reproduces
    // the former `default` arm exactly — a silent skip with no throw. Because the
    // pre-gate above already filters to MVP_KEYWORDS and the drift test pins the
    // registry keys == MVP_KEYWORDS, this branch is unreachable in practice.
    const handler = HERO_EFFECT_HANDLERS[keyword];
    if (handler === undefined) {
        return false;
    }
    handler(G, ctx, playerID, cardId, effect);
    return true;
}
/**
 * Selects the card the deterministic bot/sim KOs when an optional-KO-reward
 * choice is pending.
 *
 * Tie-break ORDER (D-24019, extended by D-24442 — its OWN policy, NOT a reuse of
 * WP-242's selectDefaultKoTarget; it does not exclude wounds and does not prefer
 * S.H.I.E.L.D. cards): (1) lowest cost; then (2) discard-zone before hand-zone;
 * then (3) lowest array index within the chosen zone. ANY card is eligible
 * (the printed text says "a card", not "a Hero").
 *
 * why: D-24442 — `inPlay` (cards played this turn) is a valid KO source, but the
 * bot scans it ONLY as a last-resort fallback when hand AND discard are both
 * empty. This is the determinism keystone: every choice that parked under the
 * pre-D-24442 code did so because hand ∪ discard was non-empty, so the discard→
 * hand scan below returns the identical card for every recorded choice — pinned
 * hashes stay byte-stable. The inPlay branch only fires in the new empty-hand+
 * discard park (e.g. after a within-turn reshuffle), which no pre-D-24442 game
 * reached.
 *
 * The bot ALWAYS returns a target and NEVER declines — decline is a human-only
 * option. Returns null only when all three zones are empty (an engine-invariant
 * violation while a choice is pending, since the park requires ≥1 eligible card
 * and the block-all guard freezes the board).
 *
 * @param zones - The player's card zones (discard + hand first; inPlay only as
 *   the empty-hand+discard fallback).
 * @param cardStats - Card stat lookup for the cost tie-break (?.cost ?? 0).
 * @param isEligible - Target filter (default accept-all).
 * @param allowInPlay - Whether the inPlay fallback may be returned (default true).
 * @param allowDiscard - Whether the discard scan runs (default true; WP-767 / D-24600).
 * @param allowHand - Whether the hand scan runs (default true; WP-826 / D-24759).
 * @returns The default KO target, or null when all three zones are empty.
 */
export function selectDefaultOptionalKoTarget(zones, cardStats, isEligible = () => true, allowInPlay = true, allowDiscard = true, allowHand = true) {
    // why: iterate discard fully (index ascending) then hand (index ascending),
    // replacing the candidate ONLY on a STRICTLY lower cost. Because the scan
    // order is discard-before-hand and lowest-index-first, the retained candidate
    // for the minimum cost is automatically the discard-before-hand, lowest-index
    // one — exactly the locked tie-break, without an explicit rank comparison.
    // This block is byte-identical to the pre-D-24442 scan (pick preservation) —
    // isEligible defaults to accept-all, so every existing caller is unchanged.
    // why: WP-681 / D-24498 — isEligible restricts the default target to the entry's
    // koTeamFilter (Battlefield Promotion's S.H.I.E.L.D.-only KO); passing a non-shield
    // target would be rejected by the resolve and hang the sim, so the bot must pick a
    // valid one. allowInPlay respects koZones so the inPlay fallback is not returned for a
    // hand∪discard-only entry.
    let bestZone = null;
    let bestCardId = null;
    let bestCost = Number.POSITIVE_INFINITY;
    const orderedZones = ['discard', 'hand'];
    for (const zoneName of orderedZones) {
        // why: WP-767 / D-24600 — mirrors the allowInPlay gate: a koZones that omits discard
        // (Snarling Fangs' hand + played-this-turn entry) must never yield a discard target the
        // resolve rejects (a sim hang). Skipping only the discard scan leaves the hand scan order
        // and tie-break unchanged, and the default true keeps every existing caller's pick.
        if (zoneName === 'discard' && !allowDiscard) {
            continue;
        }
        // why: WP-826 / D-24759 — the same for hand: Reassign to Civilian Duty's entry permits only
        // inPlay, so a hand target would be rejected (a sim hang). Skipping both scans falls through
        // to the inPlay scan below; the default true keeps every existing caller's pick.
        if (zoneName === 'hand' && !allowHand) {
            continue;
        }
        const zoneArray = zones[zoneName];
        for (let cardIndex = 0; cardIndex < zoneArray.length; cardIndex++) {
            const cardId = zoneArray[cardIndex];
            if (!isEligible(cardId)) {
                continue;
            }
            const cost = cardStats[cardId]?.cost ?? 0;
            if (cost < bestCost) {
                bestCost = cost;
                bestZone = zoneName;
                bestCardId = cardId;
            }
        }
    }
    if (bestZone !== null && bestCardId !== null) {
        return { zone: bestZone, cardId: bestCardId };
    }
    // why: D-24442 fallback — hand AND discard are both empty, so scan inPlay
    // (lowest cost, then lowest index). Reached only in the new empty-hand+discard
    // park, so it never perturbs a pre-D-24442 recorded pick. Skipped when the entry
    // disallows an in-play KO (allowInPlay false — WP-681 koZones respect).
    if (!allowInPlay) {
        return null;
    }
    let fallbackCardId = null;
    let fallbackCost = Number.POSITIVE_INFINITY;
    for (let cardIndex = 0; cardIndex < zones.inPlay.length; cardIndex++) {
        const cardId = zones.inPlay[cardIndex];
        if (!isEligible(cardId)) {
            continue;
        }
        const cost = cardStats[cardId]?.cost ?? 0;
        if (cost < fallbackCost) {
            fallbackCost = cost;
            fallbackCardId = cardId;
        }
    }
    if (fallbackCardId !== null) {
        return { zone: 'inPlay', cardId: fallbackCardId };
    }
    return null;
}
/**
 * Selects the hand card the deterministic bot/sim discards when a Smash choice is
 * pending (WP-676 / D-24492).
 *
 * Tie-break ORDER: (1) lowest `cost`; then (2) lowest `CardExtId` (ascending
 * string compare). Only the HAND is eligible ("discard another card from your
 * hand"), so — unlike selectDefaultOptionalKoTarget — there is no discard/inPlay
 * scan. The bot ALWAYS discards when the hand is non-empty and NEVER declines
 * (decline is a human-only option; +N Attack is strictly beneficial to a bot with
 * cards to spare), returning null ONLY when the hand is empty — in which case the
 * caller declines. This is deterministic and replay-faithful (no RNG).
 *
 * @param G - The game state to read (not mutated).
 * @param playerID - The choosing player whose hand is scanned.
 * @returns The CardExtId to discard, or null when the hand is empty (→ decline).
 */
export function selectDefaultSmashDiscardTarget(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return null;
    }
    // why: scan the hand once, replacing the candidate on a strictly lower cost, or on
    // an equal cost with a strictly lower CardExtId (the ascending-string tie-break). An
    // explicit for loop, never .reduce() (effect/selection code, code-style §Patterns).
    let bestCardId = null;
    let bestCost = Number.POSITIVE_INFINITY;
    for (const cardId of playerZones.hand) {
        const cost = G.cardStats[cardId]?.cost ?? 0;
        if (cost < bestCost ||
            (cost === bestCost && bestCardId !== null && cardId < bestCardId)) {
            bestCost = cost;
            bestCardId = cardId;
        }
    }
    return bestCardId;
}
/**
 * Bot/sim default target for a pending put-a-hand-card-on-deck-top choice (WP-700 / D-24519):
 * the lowest-`cost` hand card, ties broken by ascending CardExtId. Deterministic, not strategic
 * — the bot must make some legal placement; optimal deck-stacking is out of scope. Mirrors
 * selectDefaultSmashDiscardTarget exactly (a distinct export so the two defaults can diverge if
 * strategy is ever added).
 *
 * @param G - The game state to inspect (not mutated).
 * @param playerID - The player whose hand supplies the default.
 * @returns The chosen CardExtId, or null when the hand is empty (no legal placement).
 */
export function selectDefaultPutHandOnDeckTopTarget(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return null;
    }
    // why: scan the hand once, replacing the candidate on a strictly lower cost, or on an equal
    // cost with a strictly lower CardExtId (the ascending-string tie-break). An explicit for loop,
    // never .reduce() (effect/selection code, code-style §Patterns).
    let bestCardId = null;
    let bestCost = Number.POSITIVE_INFINITY;
    for (const cardId of playerZones.hand) {
        const cost = G.cardStats[cardId]?.cost ?? 0;
        if (cost < bestCost ||
            (cost === bestCost && bestCardId !== null && cardId < bestCardId)) {
            bestCost = cost;
            bestCardId = cardId;
        }
    }
    return bestCardId;
}
/**
 * Re-checks every deferred conditional grant and fires those now satisfied.
 *
 * why: WP-568 / D-24377 — invoked from the play-phase `turn.onMove` hook, the same
 * per-move cadence `latchFinalTurnIfDeckExhausted` and
 * `applyPileDepletionResourceLoss` already use: it observes the state each move
 * leaves behind, which is exactly when a recruit total can cross a threshold.
 *
 * An entry fires through `runHookEffects` — the same dispatch path the immediate
 * play uses — and is removed as it fires, so a threshold crossed, dropped and
 * re-crossed within one turn grants EXACTLY ONCE.
 *
 * @param G - The game state, mutated in place.
 * @param ctx - Move context (carries `random` for draws/reveals).
 */
export function resolveDeferredHeroGrants(G, ctx) {
    // why: derive the turn through readTurnNumber, the same narrowing
    // executeHeroEffects uses - it returns 0 rather than throwing when a
    // hand-built context omits the nested ctx, keeping the never-throw contract.
    const turn = readTurnNumber(ctx);
    if (!G.heroAbilityHooks) {
        return;
    }
    // why: WP-855 / D-24791 — the deck-KO draw HOLD. This resolve runs after every move,
    // including one that leaves a reveal choice open, and several deck-KO sites resolve one card
    // of a multi-card deck-top snapshot (Ruthless Dictator's top three, Crystal of Kadavus, a
    // queued second scry). A draw now would take a card the open choice still holds; Ruthless
    // Dictator and scry-KO keep their queue on a missing card, so the turn would freeze. So a
    // deck-KO grant does not fire while any choice is parked, and its count is KEPT (not
    // deleted below) so the draws land after the move that closes the last choice.
    // `pendingHeroChoice` is added by hand: hasAnyPendingChoice does not cover the singular
    // hero choice, and Gambit's Kinetic Card parks it on deck[0] without blocking play.
    // Computed once, before any grant fires.
    const isDeckKoDrawHeld = hasAnyPendingChoice(G) || G.pendingHeroChoice !== undefined;
    resolveDeferredConditionalGrants(G, (entry) => {
        const hook = G.heroAbilityHooks[entry.hookIndex];
        if (hook === undefined) {
            return false;
        }
        if (isDeckKoDrawHeld && hookHasConditionType(hook, DECK_CARD_KOED_CONDITION_TYPE)) {
            return false;
        }
        // why: re-evaluate ALL of the hook's conditions, not just the deferred one —
        // a hook can carry an out-of-scope gate too, and it must still hold at the
        // moment the effect actually applies.
        return evaluateAllConditions(G, entry.playerId, hook.conditions, entry.cardId);
    }, (entry) => {
        const hook = G.heroAbilityHooks[entry.hookIndex];
        if (hook === undefined) {
            return;
        }
        // why: WP-656 / D-24467 log-polish — runHookEffects already emits the concrete
        // grant line ("… gained +N recruit from <card>"), so a separate "ability applied —
        // its condition was met later this turn" confirmation was pure duplication (two lines
        // per grant; a multi-copy Diamond Form turn logged a wall of them). The grant line is
        // self-sufficient — the on-play "… is waiting …" line already told the player the
        // ability was deferred — so the confirmation is dropped.
        // why: WP-853 / D-24789 — an owned-KO grant fires once PER counted KO: one move can
        // KO several of the player's cards (heal three Wounds, a Villain KOing every Hero
        // with a trait), and each is its own "whenever". Every other hook fires once.
        // Explicit for loop, no .reduce() (.claude/rules/code-style.md).
        // why: WP-855 / D-24791 — a deck-KO grant fires once per card KO'd from the owner's deck
        // the same way (a Villain or a reveal can KO several deck cards in one move).
        let repeatCount = 1;
        if (hookHasConditionType(hook, OWNED_CARD_KOED_CONDITION_TYPE)) {
            repeatCount = Math.max(1, G.ownedCardsKoedSinceResolve?.[entry.playerId] ?? 0);
        }
        else if (hookHasConditionType(hook, DECK_CARD_KOED_CONDITION_TYPE)) {
            repeatCount = Math.max(1, G.deckCardsKoedSinceResolve?.[entry.playerId] ?? 0);
        }
        for (let i = 0; i < repeatCount; i++) {
            runHookEffects(G, ctx, entry.playerId, entry.cardId, hook, turn);
        }
        // why: WP-656 / D-24467 — a defeat-gated grant (Diamond Form) is EDGE-TRIGGERED
        // per defeat, not one-shot. resolveDeferredConditionalGrants removed this entry
        // as it fired (the numeric-threshold one-shot contract, unchanged); RE-ARM it by
        // re-recording so it survives to credit the NEXT Villain/Mastermind defeat this
        // turn. This uses the documented "a fire callback that defers a new grant appends
        // to the surviving list" support. Over-fire on non-defeat moves is prevented not
        // by removal but by the edge flag being consumed below — a re-armed grant reads a
        // cleared flag and stays put. The turn boundary drops it (clearDeferredConditionalGrants).
        // why: WP-853 / D-24789 — an owned-KO grant re-arms the same way, so it survives to
        // credit the next KO; its count is consumed below like the defeat flag.
        // why: WP-856 / D-24792 — a Hero-KO grant (OWNED_HERO_KOED_CONDITION_TYPE) is left
        // out on purpose: "the first time … this turn" fires once (repeatCount 1 above), so
        // it is not re-recorded and the first counted Hero KO is the only one it credits.
        // why: WP-855 / D-24791 — a deck-KO grant re-arms like the owned-KO grant ("whenever"),
        // so it survives to credit the next deck KO this turn.
        if (hookHasConditionType(hook, REPEATABLE_DEFEAT_CONDITION_TYPE) ||
            hookHasConditionType(hook, OWNED_CARD_KOED_CONDITION_TYPE) ||
            hookHasConditionType(hook, DECK_CARD_KOED_CONDITION_TYPE)) {
            recordDeferredConditionalGrant(G, entry.playerId, entry.cardId, entry.hookIndex);
        }
    });
    // why: WP-656 / D-24467 — consume the per-move defeat EDGE. The fight sites set
    // G.villainOrMastermindDefeatedSinceResolve (gated on a pending grant) and the
    // `defeatedVillainOrMastermindThisTurn` condition read it above; deleting it after
    // this move's resolution makes the grant fire exactly once per defeat and never on a
    // subsequent non-defeat move (the resolution runs after EVERY play-phase move). The
    // guarded delete is a no-op when unset, so a game with no such grant is byte-unchanged.
    if (G.villainOrMastermindDefeatedSinceResolve !== undefined) {
        delete G.villainOrMastermindDefeatedSinceResolve;
    }
    // why: WP-853 / D-24789 — consume the per-owner owned-KO count the same way (the
    // D-24467 edge, now a count). The grants above already fired once per counted KO, so
    // deleting it makes each KO credit exactly once and a later non-KO move see nothing.
    // Guarded, so a game that never counted a KO is byte-unchanged.
    if (G.ownedCardsKoedSinceResolve !== undefined) {
        delete G.ownedCardsKoedSinceResolve;
    }
    // why: WP-856 / D-24792 — consume the per-owner Hero-KO count too. A Hero-KO grant is not
    // re-armed above, so it fired once (the first time) and is gone; deleting the count keeps
    // a later move from reading a stale KO. Guarded, so a game that never counted one is
    // byte-unchanged.
    if (G.ownedHeroesKoedSinceResolve !== undefined) {
        delete G.ownedHeroesKoedSinceResolve;
    }
    // why: WP-855 / D-24791 — consume the per-owner deck-KO count like the owned count, but
    // only when the draw was not held: a held grant did not fire, so its KOs are still owed and
    // the count waits for the move that closes the last parked choice. Guarded, so a game that
    // never counted a deck KO is byte-unchanged.
    if (G.deckCardsKoedSinceResolve !== undefined && !isDeckKoDrawHeld) {
        delete G.deckCardsKoedSinceResolve;
    }
    // why: WP-754 / D-24581 — a wait-and-see grant fired above (e.g. crossed by an Excessive
    // Violence recruit or draw) can draw the card a pending KO-or-keep reveal snapshotted. Re-reveal
    // a stale KO-or-keep front. A no-op when no stale KO-or-keep entry is queued, so a game that
    // never plays a KO-or-keep card is byte-unchanged.
    refreshStaleKoOrKeepFront(G, ctx);
}
/**
 * Reports whether a hook carries a condition of the given type — used to re-arm the
 * repeatable per-event grants (Diamond Form's defeat, WP-656 / D-24467; Absorb Energies'
 * owned KO, WP-853 / D-24789) and to repeat the owned-KO grant per counted KO.
 *
 * @param hook - The deferred grant's hook.
 * @param conditionType - The condition type to look for.
 * @returns True when a condition of that type is present.
 */
function hookHasConditionType(hook, conditionType) {
    return findConditionOfType(hook, conditionType) !== undefined;
}
/**
 * Returns the hook's first condition of the given type, or undefined when it has none.
 *
 * @param hook - The hero ability hook.
 * @param conditionType - The condition type to look for.
 * @returns The matching condition, or undefined.
 */
function findConditionOfType(hook, conditionType) {
    if (hook.conditions === undefined) {
        return undefined;
    }
    for (const condition of hook.conditions) {
        if (condition.type === conditionType) {
            return condition;
        }
    }
    return undefined;
}
/**
 * Returns the hook's conditions minus one, so the owned-KO always-defer branch can
 * check the hook's OTHER gates (WP-853 / D-24789).
 *
 * @param hook - The hero ability hook.
 * @param excluded - The condition to leave out.
 * @returns A new array of the remaining conditions.
 */
function conditionsWithout(hook, excluded) {
    const remaining = [];
    for (const condition of hook.conditions ?? []) {
        if (condition !== excluded) {
            remaining.push(condition);
        }
    }
    return remaining;
}
//# sourceMappingURL=heroEffects.execute.js.map